import {
  Document,
  Packer,
  Paragraph,
  HeadingLevel,
  TextRun,
  ExternalHyperlink,
  PageBreak,
  BorderStyle,
  ShadingType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  Header,
  Footer,
  PageNumber,
  TableOfContents,
  AlignmentType,
  VerticalAlign,
  LevelFormat,
  convertInchesToTwip,
  ImageRun,
  HorizontalPositionAlign,
  HorizontalPositionRelativeFrom,
  VerticalPositionAlign,
  VerticalPositionRelativeFrom,
  TextWrappingType,
} from "docx";

// =========================================================
// THEME — single place to tweak the "consulting firm" look
// =========================================================
console.log("🟣🟣🟣 WORD GENERATOR TITLE FIX 08-10-2026 🟣🟣🟣");
console.log("🔥 LIVE WORD GENERATOR CONFIRMED 🔥");
const THEME = {
  primary: "1F3864", // deep navy — headings / accents
  secondary: "2E5395", // secondary navy — H2
  tertiary: "4472A8", // H3
  accent: "8496B0", // muted steel blue — rules, boxes
  gold: "B08A32", // sparingly used, callouts / dividers
  tableHeaderBg: "1F3864",
  tableHeaderText: "FFFFFF",
  tableAltRowBg: "F2F4F8",
  tableCaptionBg: "E7EDF7",
  boxBg: "EEF2F8",
  boxBorder: "2E5395",
  watermark: "F1F3F7",
  figureBg: "F8FAFD",
  figureGrid: "D9E2F3",
  kpiBg: "1F3864",
  riskLowBg: "E6EFE6",
  riskMedBg: "FBF3E3",
  riskHighBg: "FBEAEA",
  swotStrengthBg: "E6EFE6",
  swotWeaknessBg: "FBEAEA",
  swotOpportunityBg: "E7EEFB",
  swotThreatBg: "FBF3E3",
  fontHeading: "Calibri",
  fontBody: "Calibri",
  fontArabic: "Tahoma",
};

// DXA page geometry (A4, 1 inch margins) — used to size table/column widths
const PAGE_WIDTH_DXA = 11906;
const MARGIN_DXA = convertInchesToTwip(1);
const LANDSCAPE_PAGE_WIDTH_DXA = 16838;
const LANDSCAPE_MARGIN_DXA = convertInchesToTwip(0.6);

const LANDSCAPE_CONTENT_WIDTH_DXA =
  LANDSCAPE_PAGE_WIDTH_DXA - LANDSCAPE_MARGIN_DXA * 2;

const RISK_LANDSCAPE_START = Symbol("RISK_LANDSCAPE_START");
const RISK_LANDSCAPE_END = Symbol("RISK_LANDSCAPE_END");
const CONTENT_WIDTH_DXA = PAGE_WIDTH_DXA - MARGIN_DXA * 2; // ~9026

// =========================================================
// LANGUAGE / HEADING DICTIONARY
// =========================================================

const ARABIC_REGEX = /[\u0600-\u06FF]/;
const containsArabic = (text) => ARABIC_REGEX.test(String(text ?? ""));
const LATIN_REGEX = /[A-Za-z]/;
const STRATEGIC_BUSINESS_MODEL_RISK_EN =
  "Strategic Risks Affecting Business Model";
const STRATEGIC_BUSINESS_MODEL_RISK_AR =
  "المخاطر الاستراتيجية المؤثرة على نموذج العمل";
const isStrategicBusinessModelRiskHeading = (value) => {
  const normalized = String(value ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return /^strategic (risks?|المخاطر|مخاطر) affecting (the )?business model$/i.test(
    normalized,
  );
};
const normalizeStrategicBusinessModelRiskText = (value, lang) => {
  let text = String(value ?? "");
  const shouldArabicize = lang === "ar" || containsArabic(text);
  const replacement = shouldArabicize
    ? STRATEGIC_BUSINESS_MODEL_RISK_AR
    : STRATEGIC_BUSINESS_MODEL_RISK_EN;

  text = text.replace(
    /\bStrategic\s+(?:Risks?|المخاطر|مخاطر)\s+Affecting\s+(?:the\s+)?Business\s+Model\b/gi,
    replacement,
  );

  return text;
};

// Detects overall report language from the study content so every
// engine-generated label (cover, TOC, section names, box titles)
// follows one language — never mixed.
const detectReportLanguage = (study) => {
  const explicit =
    study?.report_language ||
    study?.language ||
    study?.lang ||
    study?.meta?.report_language ||
    study?.meta?.language ||
    study?.metadata?.report_language ||
    study?.metadata?.language;

  const explicitLang = String(explicit || "")
    .toLowerCase()
    .trim();

  // اللغة المختارة من الاستبيان لها الأولوية المطلقة
  if (explicitLang.startsWith("ar")) return "ar";
  if (explicitLang.startsWith("en")) return "en";

  // fallback فقط إذا لم تصل لغة صريحة
  const sample = JSON.stringify(study || {});
  const arabicMatches = sample.match(ARABIC_REGEX) || [];

  return arabicMatches.length >= 10 ? "ar" : "en";
};
const isTechnicalOperationalStudy = (study) => {
  const studyType = String(study?.study_type || "")
    .trim()
    .toLowerCase();

  if (studyType === "technical_operational") {
    return true;
  }

  return Boolean(
    study?.stage1?.technical_overview ||
    study?.stage1?.capacity_analysis ||
    study?.stage1?.site_infrastructure_analysis ||
    study?.stage2?.equipment_technology_analysis ||
    study?.stage2?.operations_process_analysis ||
    study?.stage2?.quality_storage_analysis ||
    study?.stage3?.workforce_analysis ||
    study?.stage3?.safety_environment_analysis ||
    study?.stage3?.automation_analysis ||
    study?.stage4?.technical_risk_analysis ||
    study?.stage4?.implementation_requirements,
  );
};
// Internal JSON-key -> professional heading, per language.
// This is what keeps raw keys like "financial_forecast" from ever
// reaching the page. Extend freely — anything missing here falls
// back to a prettified version of the key, so nothing silently
// disappears.
const HEADING_DICTIONARY = {
  // Stage 1
  business_overview: { en: "Business Overview", ar: "نظرة عامة على المشروع" },
  business_background: { en: "Business Background", ar: "خلفية المشروع" },
  industry_context: { en: "Industry Context", ar: "السياق القطاعي" },
  problem_statement: { en: "Problem Statement", ar: "بيان المشكلة" },
  value_proposition: { en: "Value Proposition", ar: "القيمة المقترحة" },
  business_model: { en: "Business Model", ar: "نموذج العمل" },
  strategic_objectives: {
    en: "Strategic Objectives",
    ar: "الأهداف الإستراتيجية",
  },
  country_analysis: { en: "Country Analysis", ar: "تحليل الدولة" },
  country_economic_overview: {
    en: "Economic Overview",
    ar: "نظرة عامة اقتصادية",
  },
  macroeconomic_indicators: {
    en: "Macroeconomic Indicators",
    ar: "المؤشرات الاقتصادية الكلية",
  },
  market_growth_trend: {
    en: "Market Growth Trend",
    ar: "اتجاه نمو السوق",
  },

  market_supply_comparison: {
    en: "Market Supply Comparison",
    ar: "مقارنة هيكل العرض السوقي",
  },

  market_sizing_chart: {
    en: "Market Size: TAM / SAM / SOM",
    ar: "حجم السوق: TAM إجمالي السوق القابل للاستهداف / SAM السوق المتاح للخدمة / SOM السوق الممكن الاستحواذ عليه",
  },

  porters_five_forces_chart: {
    en: "Porter's Five Forces Assessment — Scale 1 to 5",
    ar: "التقييم الرقمي لقوى بورتر الخمس — مقياس من 1 إلى 5",
  },

  risk_level_distribution_chart: {
    en: "Risk Level Distribution",
    ar: "توزيع مستويات المخاطر",
  },
  political_environment: { en: "Political Environment", ar: "البيئة السياسية" },
  investment_climate: { en: "Investment Climate", ar: "مناخ الاستثمار" },
  infrastructure: { en: "Infrastructure", ar: "البنية التحتية" },
  digital_readiness: { en: "Digital Readiness", ar: "الجاهزية الرقمية" },
  demographic_profile: { en: "Demographic Profile", ar: "الملف الديموغرافي" },
  industry_analysis: { en: "Industry Analysis", ar: "تحليل القطاع" },
  industry_overview: { en: "Industry Overview", ar: "نظرة عامة على القطاع" },
  industry_structure: { en: "Industry Structure", ar: "هيكل القطاع" },
  current_trends: { en: "Current Trends", ar: "الاتجاهات الحالية" },
  growth_drivers: { en: "Growth Drivers", ar: "محركات النمو" },
  industry_challenges: { en: "Industry Challenges", ar: "تحديات القطاع" },
  market_maturity: { en: "Market Maturity", ar: "نضج السوق" },
  regulatory_environment: {
    en: "Regulatory Environment",
    ar: "البيئة التنظيمية",
  },

  // Stage 2
  competitor_analysis: { en: "Competitor Analysis", ar: "تحليل المنافسين" },
  competitive_landscape: { en: "Competitive Landscape", ar: "المشهد التنافسي" },
  direct_competitors: { en: "Direct Competitors", ar: "المنافسون المباشرون" },
  competitive_advantages_and_moats: {
    en: "Competitive Advantages & Moats",
    ar: "المزايا التنافسية والحواجز الدفاعية",
  },
  competitive_vulnerabilities_and_white_space: {
    en: "Vulnerabilities & White Space",
    ar: "الثغرات التنافسية والفرص غير المستغلة",
  },
  pricing_and_go_to_market_strategies: {
    en: "Pricing & Go-to-Market",
    ar: "التسعير واستراتيجيات دخول السوق",
  },
  market_consolidation_and_m_and_a_activity: {
    en: "Market Consolidation & M&A",
    ar: "توحّد السوق ونشاط الاستحواذ",
  },
  competitive_response_risk: {
    en: "Competitive Response Risk",
    ar: "مخاطر الاستجابة التنافسية",
  },
  customer_analysis: { en: "Customer Analysis", ar: "تحليل العملاء" },
  customer_segmentation: { en: "Customer Segmentation", ar: "تجزئة العملاء" },
  customer_personas: { en: "Customer Personas", ar: "شخصيات العملاء" },
  customer_needs_analysis: {
    en: "Customer Needs Analysis",
    ar: "تحليل احتياجات العملاء",
  },
  pain_points_and_problems: {
    en: "Pain Points & Problems",
    ar: "نقاط الألم والمشكلات",
  },
  buying_process_and_decision_criteria: {
    en: "Buying Process & Decision Criteria",
    ar: "عملية الشراء ومعايير القرار",
  },
  customer_journey: { en: "Customer Journey", ar: "رحلة العميل" },
  adoption_barriers_and_switching_costs: {
    en: "Adoption Barriers & Switching Costs",
    ar: "حواجز التبني وتكاليف التحول",
  },
  pestel_analysis: { en: "PESTEL Analysis", ar: "تحليل PESTEL" },
  political_factors: { en: "Political Factors", ar: "العوامل السياسية" },
  economic_factors: { en: "Economic Factors", ar: "العوامل الاقتصادية" },
  social_factors: { en: "Social Factors", ar: "العوامل الاجتماعية" },
  technological_factors: { en: "Technological Factors", ar: "العوامل التقنية" },
  environmental_factors: { en: "Environmental Factors", ar: "العوامل البيئية" },
  legal_factors: { en: "Legal Factors", ar: "العوامل القانونية" },
  interdependencies_between_factors: {
    en: "Interdependencies Between Factors",
    ar: "الترابط بين العوامل",
  },
  porters_five_forces: { en: "Porter's Five Forces", ar: "قوى بورتر الخمس" },
  threat_of_new_entrants: {
    en: "Threat of New Entrants",
    ar: "تهديد الداخلين الجدد",
  },
  bargaining_power_of_suppliers: {
    en: "Bargaining Power of Suppliers",
    ar: "القوة التفاوضية للموردين",
  },
  bargaining_power_of_buyers: {
    en: "Bargaining Power of Buyers",
    ar: "القوة التفاوضية للمشترين",
  },
  threat_of_substitutes: { en: "Threat of Substitutes", ar: "تهديد البدائل" },
  competitive_rivalry: { en: "Competitive Rivalry", ar: "شدة التنافس" },
  interactions_between_forces: {
    en: "Interactions Between Forces",
    ar: "التفاعل بين القوى",
  },
  overall_industry_attractiveness: {
    en: "Overall Industry Attractiveness",
    ar: "جاذبية القطاع الإجمالية",
  },
  swot_analysis: {
    en: "SWOT Analysis",
    ar: "تحليل نقاط القوة والضعف والفرص والتهديدات (SWOT)",
  },
  swot_overview: { en: "SWOT Overview", ar: "نظرة عامة على SWOT" },
  strengths: { en: "Strengths", ar: "نقاط القوة" },
  weaknesses: { en: "Weaknesses", ar: "نقاط الضعف" },
  opportunities: { en: "Opportunities", ar: "الفرص" },
  threats: { en: "Threats", ar: "التهديدات" },
  swot_interactions_and_strategic_implications: {
    en: "Strategic Implications of SWOT",
    ar: "الدلالات الإستراتيجية لـ SWOT",
  },
  feasibility_assessment: { en: "Feasibility Assessment", ar: "تقييم الجدوى" },
  competitor_type: {
    en: "Competitor Type",
    ar: "نوع المنافس",
  },
  geographic_scope: {
    en: "Geographic Scope",
    ar: "النطاق الجغرافي",
  },
  target_customers: {
    en: "Target Customers",
    ar: "العملاء المستهدفون",
  },
  services_and_offering: {
    en: "Services & Offering",
    ar: "الخدمات والعرض",
  },
  strengths: {
    en: "Strengths",
    ar: "نقاط القوة",
  },
  weaknesses: {
    en: "Weaknesses",
    ar: "نقاط الضعف",
  },
  positioning: {
    en: "Competitive Positioning",
    ar: "التموضع التنافسي",
  },
  pricing: {
    en: "Pricing",
    ar: "التسعير",
  },
  threat_level: {
    en: "Threat Level",
    ar: "مستوى التهديد",
  },
  strategic_implication: {
    en: "Strategic Implication",
    ar: "الدلالة الاستراتيجية",
  },

  // Stage 3
  human_resources: { en: "Human Resources", ar: "الموارد البشرية" },
  executive_hr_overview: {
    en: "HR Overview",
    ar: "نظرة عامة على الموارد البشرية",
  },
  workforce_strategy: {
    en: "Workforce Strategy",
    ar: "استراتيجية القوى العاملة",
  },
  organizational_structure: {
    en: "Organizational Structure",
    ar: "الهيكل التنظيمي",
  },
  functional_departments: {
    en: "Functional Departments",
    ar: "الإدارات الوظيفية",
  },
  key_roles_and_responsibilities: {
    en: "Key Roles & Responsibilities",
    ar: "الأدوار والمسؤوليات الرئيسية",
  },
  workforce_planning: { en: "Workforce Planning", ar: "تخطيط القوى العاملة" },
  recruitment_strategy: {
    en: "Recruitment Strategy",
    ar: "استراتيجية التوظيف",
  },
  marketing_strategy: {
    en: "Marketing Strategy",
    ar: "الإستراتيجية التسويقية",
  },
  executive_marketing_overview: {
    en: "Marketing Overview",
    ar: "نظرة عامة تسويقية",
  },
  strategic_marketing_objectives: {
    en: "Strategic Marketing Objectives",
    ar: "الأهداف التسويقية الإستراتيجية",
  },
  target_market_prioritization: {
    en: "Target Market Prioritization",
    ar: "أولويات السوق المستهدف",
  },
  positioning_strategy: {
    en: "Positioning Strategy",
    ar: "استراتيجية التموضع",
  },
  brand_strategy: { en: "Brand Strategy", ar: "استراتيجية العلامة التجارية" },
  marketing_mix: { en: "Marketing Mix", ar: "المزيج التسويقي" },
  operations_plan: { en: "Operations Plan", ar: "خطة العمليات" },
  executive_operations_overview: {
    en: "Operations Overview",
    ar: "نظرة عامة تشغيلية",
  },
  operating_model: { en: "Operating Model", ar: "النموذج التشغيلي" },
  operational_objectives: {
    en: "Operational Objectives",
    ar: "الأهداف التشغيلية",
  },
  service_delivery_model: {
    en: "Service Delivery Model",
    ar: "نموذج تقديم الخدمة",
  },
  production_or_service_workflow: {
    en: "Production / Service Workflow",
    ar: "سير عمل الإنتاج أو الخدمة",
  },
  end_to_end_operational_process: {
    en: "End-to-End Operational Process",
    ar: "العملية التشغيلية الشاملة",
  },
  supply_chain_strategy: {
    en: "Supply Chain Strategy",
    ar: "استراتيجية سلسلة الإمداد",
  },
  technology_strategy: {
    en: "Technology Strategy",
    ar: "الاستراتيجية التقنية",
  },
  executive_technology_overview: {
    en: "Technology Overview",
    ar: "نظرة عامة تقنية",
  },
  technology_strategy_logic: {
    en: "Technology Strategy Logic",
    ar: "منطق الاستراتيجية التقنية",
  },
  technology_objectives: { en: "Technology Objectives", ar: "الأهداف التقنية" },
  core_technology_architecture: {
    en: "Core Technology Architecture",
    ar: "البنية التقنية الأساسية",
  },
  software_and_application_requirements: {
    en: "Software & Application Requirements",
    ar: "متطلبات البرمجيات والتطبيقات",
  },
  infrastructure_requirements: {
    en: "Infrastructure Requirements",
    ar: "متطلبات البنية التحتية",
  },
  data_strategy: { en: "Data Strategy", ar: "استراتيجية البيانات" },
  executive_business_model_overview: {
    en: "Business Model Overview",
    ar: "نظرة عامة على نموذج العمل",
  },
  business_model_logic: { en: "Business Model Logic", ar: "منطق نموذج العمل" },
  value_creation_strategy: {
    en: "Value Creation Strategy",
    ar: "استراتيجية خلق القيمة",
  },
  customer_value_delivery: {
    en: "Customer Value Delivery",
    ar: "تقديم القيمة للعميل",
  },
  value_capture_mechanisms: {
    en: "Value Capture Mechanisms",
    ar: "آليات تحصيل القيمة",
  },
  revenue_streams: { en: "Revenue Streams", ar: "مصادر الإيرادات" },
  pricing_logic: { en: "Pricing Logic", ar: "منطق التسعير" },

  // Stage 4 (financial)
  financial_assumptions: {
    en: "Financial Assumptions",
    ar: "الافتراضات المالية",
  },
  executive_financial_assumptions_overview: {
    en: "Financial Assumptions Overview",
    ar: "نظرة عامة على الافتراضات المالية",
  },
  revenue_assumptions: { en: "Revenue Assumptions", ar: "افتراضات الإيرادات" },
  cost_assumptions: { en: "Cost Assumptions", ar: "افتراضات التكاليف" },
  pricing_assumptions: { en: "Pricing Assumptions", ar: "افتراضات التسعير" },
  sales_growth_assumptions: {
    en: "Sales Growth Assumptions",
    ar: "افتراضات نمو المبيعات",
  },
  market_penetration_assumptions: {
    en: "Market Penetration Assumptions",
    ar: "افتراضات اختراق السوق",
  },
  inflation_assumptions: { en: "Inflation Assumptions", ar: "افتراضات التضخم" },
  startup_costs: { en: "Startup Costs", ar: "تكاليف التأسيس" },
  executive_startup_cost_overview: {
    en: "Startup Cost Overview",
    ar: "نظرة عامة على تكاليف التأسيس",
  },
  capital_expenditures: {
    en: "Capital Expenditures",
    ar: "النفقات الرأسمالية",
  },
  initial_operating_expenses: {
    en: "Initial Operating Expenses",
    ar: "المصروفات التشغيلية الأولية",
  },
  pre_launch_costs: { en: "Pre-Launch Costs", ar: "تكاليف ما قبل الإطلاق" },
  licensing_and_regulatory_costs: {
    en: "Licensing & Regulatory Costs",
    ar: "تكاليف الترخيص والامتثال",
  },
  technology_investment: {
    en: "Technology Investment",
    ar: "الاستثمار التقني",
  },
  facilities_and_equipment: {
    en: "Facilities & Equipment",
    ar: "المرافق والمعدات",
  },
  revenue_model: { en: "Revenue Model", ar: "نموذج الإيرادات" },
  revenue_model_overview: {
    en: "Revenue Model Overview",
    ar: "نظرة عامة على نموذج الإيرادات",
  },
  primary_revenue_streams: {
    en: "Primary Revenue Streams",
    ar: "مصادر الإيراد الرئيسية",
  },
  pricing_strategy_and_monetization: {
    en: "Pricing Strategy & Monetization",
    ar: "استراتيجية التسعير والتحصيل",
  },
  customer_acquisition_and_unit_economics: {
    en: "Customer Acquisition & Unit Economics",
    ar: "اكتساب العملاء واقتصاديات الوحدة",
  },
  revenue_scaling_and_growth_drivers: {
    en: "Revenue Scaling & Growth Drivers",
    ar: "توسيع الإيرادات ومحركات النمو",
  },
  revenue_diversification_and_risk_management: {
    en: "Revenue Diversification & Risk Management",
    ar: "تنويع الإيرادات وإدارة المخاطر",
  },
  revenue_recognition_and_financial_reporting: {
    en: "Revenue Recognition & Reporting",
    ar: "الاعتراف بالإيراد والتقارير المالية",
  },
  financial_forecast: { en: "Financial Forecast", ar: "التوقعات المالية" },
  financial_forecast_overview: {
    en: "Financial Forecast Overview",
    ar: "نظرة عامة على التوقعات المالية",
  },
  income_statement_projections: {
    en: "Income Statement Projections",
    ar: "توقعات قائمة الدخل",
  },
  cash_flow_projections_and_working_capital: {
    en: "Cash Flow Projections & Working Capital",
    ar: "توقعات التدفق النقدي ورأس المال العامل",
  },
  balance_sheet_projections: {
    en: "Balance Sheet Projections",
    ar: "توقعات الميزانية العمومية",
  },
  key_financial_metrics_and_ratios: {
    en: "Key Financial Metrics & Ratios",
    ar: "المؤشرات والنسب المالية الرئيسية",
  },
  scenario_analysis_and_case_development: {
    en: "Scenario Analysis",
    ar: "تحليل السيناريوهات",
  },
  sensitivity_analysis_and_value_drivers: {
    en: "Sensitivity & Value Drivers",
    ar: "تحليل الحساسية ومحركات القيمة",
  },
  breakeven_analysis_and_payback_period: {
    en: "Break-even & Payback Period",
    ar: "نقطة التعادل وفترة الاسترداد",
  },
  roi_and_irr_analysis: {
    en: "ROI & IRR Analysis",
    ar: "تحليل العائد على الاستثمار ومعدل العائد الداخلي",
  },
  variance_analysis_and_monitoring_framework: {
    en: "Variance Analysis & Monitoring",
    ar: "تحليل الانحراف وإطار المراقبة",
  },
  critical_assumptions_and_confidence_assessment: {
    en: "Critical Assumptions & Confidence",
    ar: "الافتراضات الحرجة وتقييم الثقة",
  },
  strategic_implications_and_key_takeaways: {
    en: "Strategic Implications",
    ar: "الدلالات الإستراتيجية",
  },
  cash_flow: { en: "Cash Flow Statement", ar: "قائمة التدفقات النقدية" },
  break_even_analysis: { en: "Break-even Analysis", ar: "تحليل نقطة التعادل" },
  executive_cash_flow_overview: {
    en: "Cash Flow Overview",
    ar: "نظرة عامة على التدفق النقدي",
  },
  operating_cash_flow_analysis: {
    en: "Operating Cash Flow",
    ar: "التدفق النقدي التشغيلي",
  },
  investing_cash_flow_analysis: {
    en: "Investing Cash Flow",
    ar: "التدفق النقدي الاستثماري",
  },
  financing_cash_flow_analysis: {
    en: "Financing Cash Flow",
    ar: "التدفق النقدي التمويلي",
  },
  working_capital_analysis_and_optimization: {
    en: "Working Capital Analysis",
    ar: "تحليل رأس المال العامل",
  },
  cash_conversion_cycle: {
    en: "Cash Conversion Cycle",
    ar: "دورة تحويل النقد",
  },
  cumulative_cash_position_and_breakeven: {
    en: "Cumulative Cash Position & Break-even",
    ar: "المركز النقدي التراكمي ونقطة التعادل",
  },
  roi_analysis: { en: "ROI Analysis", ar: "تحليل العائد على الاستثمار" },
  introduction: { en: "Introduction", ar: "مقدمة" },
  methodology: { en: "Methodology", ar: "المنهجية" },
  key_assumptions: { en: "Key Assumptions", ar: "الافتراضات الرئيسية" },
  roi_metrics: { en: "ROI Metrics", ar: "مؤشرات العائد على الاستثمار" },
  year_by_year_roi_progression: {
    en: "Year-by-Year ROI Progression",
    ar: "تطور العائد على الاستثمار سنويًا",
  },
  interpretation: { en: "Interpretation", ar: "التفسير" },
  comparative_context: { en: "Comparative Context", ar: "السياق المقارن" },
  risks_to_roi: { en: "Risks to ROI", ar: "مخاطر تؤثر على العائد" },
  sensitivity_note: { en: "Sensitivity Note", ar: "ملاحظة حساسية" },
  assumptions_and_limitations: {
    en: "Assumptions & Limitations",
    ar: "الافتراضات والقيود",
  },
  conclusion: { en: "Conclusion", ar: "الخلاصة" },
  sensitivity_analysis: { en: "Sensitivity Analysis", ar: "تحليل الحساسية" },
  executive_overview: { en: "Executive Overview", ar: "نظرة عامة تنفيذية" },
  critical_financial_assumptions: {
    en: "Critical Financial Assumptions",
    ar: "الافتراضات المالية الحرجة",
  },
  key_value_drivers: { en: "Key Value Drivers", ar: "محركات القيمة الرئيسية" },
  base_case_summary: { en: "Base Case Summary", ar: "ملخص السيناريو الأساسي" },
  scenarios: { en: "Scenario Comparison", ar: "مقارنة السيناريوهات" },
  best_case: { en: "Best Case", ar: "أفضل حالة" },
  expected_case: { en: "Expected Case", ar: "الحالة المتوقعة" },
  worst_case: { en: "Worst Case", ar: "أسوأ حالة" },
  narrative: { en: "Narrative", ar: "السرد" },
  revenue_impact: { en: "Revenue Impact", ar: "الأثر على الإيراد" },
  profitability_impact: { en: "Profitability Impact", ar: "الأثر على الربحية" },
  cash_flow_impact: { en: "Cash Flow Impact", ar: "الأثر على التدفق النقدي" },
  break_even_impact: { en: "Break-even Impact", ar: "الأثر على نقطة التعادل" },
  roi_impact: { en: "ROI Impact", ar: "الأثر على العائد على الاستثمار" },
  revenue_sensitivity: { en: "Revenue Sensitivity", ar: "حساسية الإيراد" },
  pricing_sensitivity: { en: "Pricing Sensitivity", ar: "حساسية التسعير" },
  sales_volume_sensitivity: {
    en: "Sales Volume Sensitivity",
    ar: "حساسية حجم المبيعات",
  },
  cost_sensitivity: { en: "Cost Sensitivity", ar: "حساسية التكاليف" },
  operating_expense_sensitivity: {
    en: "Operating Expense Sensitivity",
    ar: "حساسية المصروفات التشغيلية",
  },
  gross_margin_sensitivity: {
    en: "Gross Margin Sensitivity",
    ar: "حساسية الهامش الإجمالي",
  },
  cash_flow_sensitivity: {
    en: "Cash Flow Sensitivity",
    ar: "حساسية التدفق النقدي",
  },
  break_even_sensitivity: {
    en: "Break-even Sensitivity",
    ar: "حساسية نقطة التعادل",
  },
  roi_sensitivity: { en: "ROI Sensitivity", ar: "حساسية العائد على الاستثمار" },
  financial_resilience_assessment: {
    en: "Financial Resilience Assessment",
    ar: "تقييم المرونة المالية",
  },
  early_warning_indicators: {
    en: "Early Warning Indicators",
    ar: "مؤشرات الإنذار المبكر",
  },
  management_response_considerations: {
    en: "Management Response Considerations",
    ar: "اعتبارات استجابة الإدارة",
  },
  overall_financial_robustness: {
    en: "Overall Financial Robustness",
    ar: "المتانة المالية الإجمالية",
  },

  // Stage 5
  governance_framework: { en: "Governance Framework", ar: "إطار الحوكمة" },
  executive_governance_overview: {
    en: "Governance Overview",
    ar: "نظرة عامة على الحوكمة",
  },
  governance_philosophy: { en: "Governance Philosophy", ar: "فلسفة الحوكمة" },
  governance_objectives: { en: "Governance Objectives", ar: "أهداف الحوكمة" },
  governance_structure: { en: "Governance Structure", ar: "هيكل الحوكمة" },
  decision_making_framework: {
    en: "Decision-Making Framework",
    ar: "إطار اتخاذ القرار",
  },
  authority_matrix: { en: "Authority Matrix", ar: "مصفوفة الصلاحيات" },
  accountability_framework: {
    en: "Accountability Framework",
    ar: "إطار المساءلة",
  },
  reporting_lines: { en: "Reporting Lines", ar: "خطوط التقارير" },
  governance_committees: { en: "Governance Committees", ar: "لجان الحوكمة" },
  meeting_structure: { en: "Meeting Structure", ar: "هيكل الاجتماعات" },
  escalation_framework: { en: "Escalation Framework", ar: "إطار التصعيد" },
  risk_governance: { en: "Risk Governance", ar: "حوكمة المخاطر" },
  performance_governance: { en: "Performance Governance", ar: "حوكمة الأداء" },
  compliance_governance: { en: "Compliance Governance", ar: "حوكمة الامتثال" },
  internal_control_framework: {
    en: "Internal Control Framework",
    ar: "إطار الرقابة الداخلية",
  },
  policy_management: { en: "Policy Management", ar: "إدارة السياسات" },
  change_management_governance: {
    en: "Change Management Governance",
    ar: "حوكمة إدارة التغيير",
  },
  stakeholder_communication_governance: {
    en: "Stakeholder Communication Governance",
    ar: "حوكمة التواصل مع أصحاب المصلحة",
  },
  governance_review_process: {
    en: "Governance Review Process",
    ar: "عملية مراجعة الحوكمة",
  },
  governance_maturity_assessment: {
    en: "Governance Maturity Assessment",
    ar: "تقييم نضج الحوكمة",
  },
  governance_readiness_assessment: {
    en: "Governance Readiness Assessment",
    ar: "تقييم جاهزية الحوكمة",
  },
  implementation_roadmap: {
    en: "Implementation Roadmap",
    ar: "خارطة طريق التنفيذ",
  },
  executive_implementation_overview: {
    en: "Implementation Overview",
    ar: "نظرة عامة على التنفيذ",
  },
  implementation_methodology: {
    en: "Implementation Methodology",
    ar: "منهجية التنفيذ",
  },
  implementation_phases: { en: "Implementation Phases", ar: "مراحل التنفيذ" },
  phase_objectives: { en: "Phase Objectives", ar: "أهداف المراحل" },
  major_deliverables: { en: "Major Deliverables", ar: "المخرجات الرئيسية" },
  key_activities: { en: "Key Activities", ar: "الأنشطة الرئيسية" },
  milestones: { en: "Milestones", ar: "المعالم الرئيسية" },
  kpi_framework: { en: "KPI Framework", ar: "إطار مؤشرات الأداء" },
  executive_kpi_overview: { en: "KPI Overview", ar: "نظرة عامة على المؤشرات" },
  kpi_design_methodology: {
    en: "KPI Design Methodology",
    ar: "منهجية تصميم المؤشرات",
  },
  financial_kpis: { en: "Financial KPIs", ar: "المؤشرات المالية" },
  revenue_kpis: { en: "Revenue KPIs", ar: "مؤشرات الإيراد" },
  profitability_kpis: { en: "Profitability KPIs", ar: "مؤشرات الربحية" },
  cash_flow_kpis: { en: "Cash Flow KPIs", ar: "مؤشرات التدفق النقدي" },
  operational_kpis: { en: "Operational KPIs", ar: "المؤشرات التشغيلية" },
  mitigation_plan: { en: "Risk Mitigation Plan", ar: "خطة تخفيف المخاطر" },
  executive_mitigation_overview: {
    en: "Mitigation Overview",
    ar: "نظرة عامة على التخفيف",
  },
  risk_mitigation_methodology: {
    en: "Mitigation Methodology",
    ar: "منهجية التخفيف",
  },
  preventive_controls: { en: "Preventive Controls", ar: "الضوابط الوقائية" },
  detective_controls: { en: "Detective Controls", ar: "الضوابط الاكتشافية" },
  corrective_controls: { en: "Corrective Controls", ar: "الضوابط التصحيحية" },
  strategic_risk_mitigation: {
    en: "Strategic Risk Mitigation",
    ar: "تخفيف المخاطر الإستراتيجية",
  },
  financial_risk_mitigation: {
    en: "Financial Risk Mitigation",
    ar: "تخفيف المخاطر المالية",
  },
  risk_analysis: { en: "Risk Analysis", ar: "تحليل المخاطر" },
  executive_risk_overview: { en: "Risk Overview", ar: "نظرة عامة على المخاطر" },
  risk_assessment_methodology: {
    en: "Risk Assessment Methodology",
    ar: "منهجية تقييم المخاطر",
  },
  strategic_risks: { en: "Strategic Risks", ar: "المخاطر الإستراتيجية" },
  market_risks: { en: "Market Risks", ar: "مخاطر السوق" },
  competitive_risks: { en: "Competitive Risks", ar: "المخاطر التنافسية" },
  financial_risks: { en: "Financial Risks", ar: "المخاطر المالية" },
  operational_risks: { en: "Operational Risks", ar: "المخاطر التشغيلية" },

  // Stage 6
  executive_summary: { en: "Executive Summary", ar: "الملخص التنفيذي" },
  executive_project_overview: {
    en: "Project Overview",
    ar: "نظرة عامة على المشروع",
  },
  strategic_opportunity: {
    en: "Strategic Opportunity",
    ar: "الفرصة الإستراتيجية",
  },
  business_concept_summary: {
    en: "Business Concept Summary",
    ar: "ملخص فكرة المشروع",
  },
  market_attractiveness: { en: "Market Attractiveness", ar: "جاذبية السوق" },
  competitive_position: { en: "Competitive Position", ar: "الموقع التنافسي" },
  customer_opportunity: { en: "Customer Opportunity", ar: "فرصة العملاء" },
  business_model_highlights: {
    en: "Business Model Highlights",
    ar: "أبرز ملامح نموذج العمل",
  },
  operational_readiness: {
    en: "Operational Readiness",
    ar: "الجاهزية التشغيلية",
  },
  financial_highlights: {
    en: "Financial Highlights",
    ar: "أبرز المؤشرات المالية",
  },
  investment_highlights: {
    en: "Investment Highlights",
    ar: "أبرز محفزات الاستثمار",
  },
  risk_overview: { en: "Risk Overview", ar: "نظرة عامة على المخاطر" },
  governance_readiness: { en: "Governance Readiness", ar: "الجاهزية الحوكمية" },
  overall_feasibility: { en: "Overall Feasibility", ar: "الجدوى الإجمالية" },
  critical_success_factors: {
    en: "Critical Success Factors",
    ar: "عوامل النجاح الحرجة",
  },
  executive_conclusions: {
    en: "Executive Conclusions",
    ar: "الاستنتاجات التنفيذية",
  },
  key_takeaways: { en: "Key Takeaways", ar: "أبرز الاستنتاجات" },
  final_review: { en: "Final Quality Review", ar: "المراجعة النهائية للجودة" },
  executive_review_overview: {
    en: "Review Overview",
    ar: "نظرة عامة على المراجعة",
  },
  overall_study_completeness: {
    en: "Overall Study Completeness",
    ar: "اكتمال الدراسة الإجمالي",
  },
  internal_consistency_assessment: {
    en: "Internal Consistency",
    ar: "الاتساق الداخلي",
  },
  cross_module_alignment: {
    en: "Cross-Module Alignment",
    ar: "الاتساق بين الوحدات",
  },
  executive_decision_support_quality: {
    en: "Decision-Support Quality",
    ar: "جودة دعم القرار",
  },
  data_integrity_assessment: { en: "Data Integrity", ar: "سلامة البيانات" },
  assumption_consistency: {
    en: "Assumption Consistency",
    ar: "اتساق الافتراضات",
  },
  analytical_quality_assessment: {
    en: "Analytical Quality",
    ar: "جودة التحليل",
  },
  report_readiness_assessment: { en: "Report Readiness", ar: "جاهزية التقرير" },
  client_decision_readiness: {
    en: "Client Decision Readiness",
    ar: "جاهزية القرار للعميل",
  },
  remaining_limitations: { en: "Remaining Limitations", ar: "القيود المتبقية" },
  overall_professional_quality: {
    en: "Overall Professional Quality",
    ar: "الجودة المهنية الإجمالية",
  },
  final_independent_opinion: {
    en: "Final Independent Opinion",
    ar: "الرأي المستقل النهائي",
  },
  implementation_readiness: {
    en: "Implementation Readiness",
    ar: "جاهزية التنفيذ",
  },
  executive_readiness_overview: {
    en: "Readiness Overview",
    ar: "نظرة عامة على الجاهزية",
  },
  readiness_dimensions: { en: "Readiness Dimensions", ar: "أبعاد الجاهزية" },
  critical_readiness_gaps: {
    en: "Critical Readiness Gaps",
    ar: "فجوات الجاهزية الحرجة",
  },
  preconditions_for_launch: {
    en: "Preconditions for Launch",
    ar: "الشروط المسبقة للإطلاق",
  },
  readiness_score: { en: "Readiness Score", ar: "درجة الجاهزية" },
  go_live_readiness_assessment: {
    en: "Go-Live Readiness",
    ar: "تقييم جاهزية الإطلاق",
  },
  investment_recommendation: {
    en: "Investment Recommendation",
    ar: "التوصية الاستثمارية",
  },
  executive_investment_overview: {
    en: "Investment Overview",
    ar: "نظرة عامة استثمارية",
  },
  investment_thesis: { en: "Investment Thesis", ar: "الأطروحة الاستثمارية" },
  strategic_investment_rationale: {
    en: "Strategic Rationale",
    ar: "المبرر الإستراتيجي",
  },
  commercial_attractiveness: {
    en: "Commercial Attractiveness",
    ar: "الجاذبية التجارية",
  },
  financial_attractiveness: {
    en: "Financial Attractiveness",
    ar: "الجاذبية المالية",
  },
  risk_return_assessment: {
    en: "Risk-Return Assessment",
    ar: "تقييم المخاطرة مقابل العائد",
  },
  capital_efficiency_assessment: {
    en: "Capital Efficiency",
    ar: "كفاءة رأس المال",
  },
  competitive_position_assessment: {
    en: "Competitive Position Assessment",
    ar: "تقييم الموقع التنافسي",
  },
  organizational_readiness_assessment: {
    en: "Organizational Readiness",
    ar: "الجاهزية التنظيمية",
  },
  investment_strengths: {
    en: "Investment Strengths",
    ar: "نقاط قوة الاستثمار",
  },
  investment_constraints: {
    en: "Investment Constraints",
    ar: "قيود الاستثمار",
  },
  investment_conditions: { en: "Investment Conditions", ar: "شروط الاستثمار" },
  critical_decision_factors: {
    en: "Critical Decision Factors",
    ar: "عوامل القرار الحرجة",
  },
  go_conditional_go_no_go_assessment: {
    en: "Go / Conditional-Go / No-Go",
    ar: "قرار المضي أو المضي المشروط أو التوقف",
  },
  investment_confidence_level: {
    en: "Investment Confidence Level",
    ar: "مستوى الثقة الاستثمارية",
  },

  feasibility_score: { en: "Feasibility Score", ar: "مؤشر الجدوى" },
  recommendations: { en: "Recommendations", ar: "التوصيات" },
  executive_recommendations: {
    en: "Executive Recommendations",
    ar: "التوصيات النهائية",
  },
  key_findings: { en: "Key Findings", ar: "أبرز النتائج" },
  references: { en: "References", ar: "المراجع" },
  appendix: { en: "Appendix", ar: "الملحق" },
};

// Fixed labels the report engine itself needs (cover, TOC, boxes…)
const UI_LABELS = {
  reportTitle: { en: "Feasibility Study", ar: "دراسة الجدوى" },
  preparedFor: { en: "Prepared for", ar: "أُعدت لصالح" },
  project: { en: "Project", ar: "المشروع" },
  studyDate: { en: "Study Date", ar: "تاريخ الدراسة" },
  professionalTitle: {
    en: "Confidential Feasibility Study Report",
    ar: "تقرير دراسة جدوى سرّي",
  },
  logoPlaceholder: { en: "[ COMPANY LOGO ]", ar: "[ شعار الشركة ]" },
  confidential: { en: "Strictly Confidential", ar: "سرّي للغاية" },
  copyrightPageTitle: { en: "Copyright", ar: "حقوق النشر" },
  copyrightBody: {
    en: "This document and its contents are confidential and proprietary. No part of this report may be reproduced, distributed, or transmitted in any form or by any means without prior written permission.",
    ar: "هذا التقرير ومحتوياته سرّي ومملوك لأصحابه. لا يجوز إعادة إنتاج أو توزيع أو نقل أي جزء منه بأي شكل أو بأي وسيلة دون إذن كتابي مسبق.",
  },
  disclaimerTitle: { en: "Disclaimer", ar: "إخلاء المسؤولية" },
  disclaimerBody: {
    en: "This feasibility study was prepared with the assistance of artificial intelligence based on the data and assumptions provided at the time of the engagement. It is intended for informational and planning purposes only and does not constitute financial, legal, tax, or investment advice. All projections are estimates based on stated assumptions and are subject to change. Readers should conduct independent verification and seek qualified professional advice before making business or investment decisions.",
    ar: "أُعدت دراسة الجدوى هذه بمساعدة الذكاء الاصطناعي استنادًا إلى البيانات والافتراضات المقدَّمة وقت إعداد الدراسة. وهي مُعدَّة لأغراض إرشادية وتخطيطية فقط، ولا تُعد استشارة مالية أو قانونية أو ضريبية أو استثمارية. جميع التوقعات هي تقديرات مبنية على افتراضات معلنة وقابلة للتغيّر. يُنصح بالتحقق المستقل واستشارة جهات مختصة قبل اتخاذ أي قرارات تجارية أو استثمارية.",
  },
  tableOfContents: { en: "Table of Contents", ar: "جدول المحتويات" },
  listOfTables: { en: "List of Tables", ar: "قائمة الجداول" },
  listOfFigures: { en: "List of Figures", ar: "قائمة الأشكال" },
  tableLabel: { en: "Table", ar: "جدول" },
  figureLabel: { en: "Figure", ar: "شكل" },
  generatedBy: {
    en: "Generated by AI Feasibility Platform",
    ar: "تم إنشاؤه بواسطة منصة دراسات الجدوى الذكية",
  },
  headerTitle: { en: "Feasibility Study Report", ar: "تقرير دراسة الجدوى" },
  headerConfidential: { en: "Confidential", ar: "سرّي" },
  page: { en: "Page", ar: "صفحة" },
  of: { en: "of", ar: "من" },
  stage: { en: "Part", ar: "الجزء" },
  keyFindings: { en: "Key Findings", ar: "أبرز النتائج" },
  keyAssumptions: { en: "Key Assumptions", ar: "الافتراضات الرئيسية" },
  keyTakeaways: { en: "Key Takeaways", ar: "أبرز الاستنتاجات" },
  kpiSummary: { en: "KPI Summary", ar: "ملخص المؤشرات" },
  references: { en: "References", ar: "المراجع" },
  appendix: { en: "Appendix", ar: "الملحق" },
  metric: { en: "Metric", ar: "المؤشر" },
  value: { en: "Value", ar: "القيمة" },
  scenario: { en: "Scenario", ar: "السيناريو" },
  dimension: { en: "Dimension", ar: "البُعد" },
  overallScore: { en: "Overall Score", ar: "الدرجة الإجمالية" },
  rating: { en: "Rating", ar: "التصنيف" },
  determination: { en: "Determination", ar: "القرار" },
  category: { en: "Category", ar: "الفئة" },
  riskLevel: { en: "Risk Level", ar: "مستوى الخطورة" },
  tocUpdateNote: {
    en: 'Right-click and choose "Update Field" to populate the Table of Contents.',
    ar: "انقر بزر الفأرة الأيمن واختر «تحديث الحقل» لعرض جدول المحتويات.",
  },
  introLine: {
    en: "This report was prepared following a structured multi-stage feasibility methodology covering strategic, market, operational, financial, and risk dimensions.",
    ar: "أُعد هذا التقرير وفق منهجية متعددة المراحل لدراسة الجدوى، تغطي الأبعاد الإستراتيجية والسوقية والتشغيلية والمالية ومخاطر المشروع.",
  },
  methodologyTitle: { en: "Methodology", ar: "المنهجية" },
  methodologyBody: {
    en: "The analysis integrates macro-level country and industry research, competitive and customer intelligence, operating and financial modeling, and a structured risk and governance review. Each section builds on validated outputs from the preceding stage to preserve internal consistency across the study.",
    ar: "يدمج التحليل بحثًا كليًا على مستوى الدولة والقطاع، وذكاءً تنافسيًا ومعرفة بالعملاء، ونمذجة تشغيلية ومالية، ومراجعة منظّمة للمخاطر والحوكمة. يبني كل قسم على مخرجات معتمدة من المرحلة السابقة للحفاظ على الاتساق الداخلي للدراسة.",
  },
};

const ARABIC_UI_OVERRIDES = {
  reportTitle: "دراسة الجدوى السوقية",
  continuity_risks: "مخاطر الاستمرارية",
  stated_procedures: "الإجراءات المذكورة",
  consideration: "الاعتبار",
  risk_id: "معرف المخاطر",
  risk_source: "مصدر المخاطر",
  monitoring_area: "مجال المراقبة",
  action: "الإجراء",
  expected_operational_outcome: "النتيجة التشغيلية المتوقعة",
  preparedFor: "أعدت لصالح",
  project: "المشروع",
  studyDate: "تاريخ الدراسة",
  professionalTitle: "تقرير دراسة جدوى سوقية احترافي",
  logoPlaceholder: "[ شعار الشركة ]",
  confidential: "الجبرية التجارية",
  copyrightPageTitle: "حقوق النشر",
  copyrightBody:
    "هذا التقرير ومحتوياته مملوكة لأصحابه، ولا يجوز إعادة إنتاجه أو توزيعه أو نقله دون إذن كتابي مسبق.",
  disclaimerTitle: "إخلاء المسؤولية",
  disclaimerBody:
    "أعدت هذه الدراسة بناء على البيانات والافتراضات المتاحة وقت إعداد التقرير. وهي مخصصة لأغراض التخطيط والدعم التحليلي، ولا تعد استشارة مالية أو قانونية أو ضريبية أو استثمارية نهائية. ينبغي التحقق من الأرقام والافتراضات قبل اتخاذ أي قرار تنفيذي.",
  tableOfContents: "جدول المحتويات",
  listOfTables: "قائمة الجداول",
  listOfFigures: "قائمة الأشكال",
  tableLabel: "جدول",
  figureLabel: "شكل",
  generatedBy: "الجبرية التجارية",
  headerTitle: "تقرير دراسة الجدوى السوقية",
  headerConfidential: "الجبرية التجارية",
  page: "صفحة",
  of: "من",
  stage: "الجزء",
  keyFindings: "أبرز النتائج",
  keyAssumptions: "الافتراضات الرئيسية",
  keyTakeaways: "أبرز الاستنتاجات",
  kpiSummary: "ملخص المؤشرات",
  references: "المراجع",
  appendix: "الملحق",
  metric: "المؤشر",
  value: "القيمة",
  scenario: "السيناريو",
  dimension: "البعد",
  overallScore: "الدرجة الإجمالية",
  rating: "التصنيف",
  determination: "القرار",
  category: "الفئة",
  riskLevel: "مستوى الخطورة",
  tocUpdateNote:
    "لتحديث الفهرس وأرقام الصفحات داخل Word: اضغط بزر الفأرة الأيمن على الفهرس واختر تحديث الحقل.",
  introLine:
    "أعد هذا التقرير وفق منهجية متعددة المراحل لدراسة الجدوى السوقية، تغطي ملاءمة السوق، وحجم الفرصة، وتحليل الطلب والعرض، وسلوك العملاء، والمنافسة، ومخاطر دخول السوق.",
  methodologyTitle: "المنهجية",
  methodologyBody:
    "يركز التحليل على تقييم جاذبية السوق وقابلية الدخول، من خلال قراءة البيئة الكلية والقطاعية، وتحليل حجم السوق والطلب والعرض، وفهم شرائح العملاء والمنافسين، ثم ربط النتائج بتوصيات قابلة للاستخدام في قرار دخول السوق.",
};

const t = (key, lang) =>
  lang === "ar" && ARABIC_UI_OVERRIDES[key]
    ? ARABIC_UI_OVERRIDES[key]
    : UI_LABELS[key]
      ? UI_LABELS[key][lang]
      : key;

// Translate a raw JSON key into a professional heading. Falls back
// to a prettified version of the key itself if it isn't in the
// dictionary yet (so nothing ever silently disappears).
const prettifyKey = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const ARABIC_HEADING_OVERRIDES = {
  dependency: "الاعتمادية",
  relevance: "الأهمية التشغيلية",
  operational_relevance: "الأهمية التشغيلية",

  capacity_impact: "الأثر على الطاقة الإنتاجية",
  stage_or_factor: "المرحلة أو العامل",

  effect: "الأثر",
  operational_effect: "الأثر التشغيلي",

  upstream_effect: "الأثر السابق",
  downstream_effect: "الأثر اللاحق",
  control_required: "إجراء الضبط المطلوب",

  measurement_stage: "مرحلة القياس",

  automation_role: "دور الأتمتة",
  technology_or_system: "التقنية أو النظام",
  Opportunity: "الفرصة",
  affected_area: "المجال المتأثر",
  implementation_impact: "أثر التنفيذ",
  unit: "الوحدة",
  capacity_decision_impact: "أثر القرار على الطاقة",
  operational_dependency: "الاعتمادية التشغيلية",
  opportunity: "الفرصة",
  skill_or_role_adjustment: "تعديل المهارة أو الدور",
  stage1: "الجزء الأول: تعريف السوق والبيئة الخارجية",
  stage2: "الجزء الثاني: جاذبية السوق والعملاء والمنافسة",
  stage3: "الجزء الثالث: استراتيجية دخول السوق ونموذج التنفيذ",
  stage4: "الجزء الرابع: الجدوى المالية المرتبطة بالسوق",
  stage5: "الجزء الخامس: مخاطر السوق والجاهزية",
  stage6: "الجزء السادس: الحكم السوقي النهائي والتوصيات",
  introduction: "المقدمة",
  business_overview: "نظرة عامة على المشروع",
  business_background: "خلفية المشروع",
  industry_context: "السياق القطاعي",
  problem_statement: "بيان المشكلة",
  value_proposition: "القيمة المقترحة",
  business_model: "نموذج العمل",
  strategic_objectives: "الأهداف الاستراتيجية",
  equipment_inventory: "حصر المعدات",
  main_equipment_assessment: "تقييم المعدات الرئيسية",
  support_equipment_assessment: "تقييم المعدات المساندة",
  equipment_suitability: "ملاءمة المعدات",
  equipment_capacity_alignment: "مواءمة المعدات مع الطاقة الإنتاجية",
  equipment_specification_requirements: "متطلبات مواصفات المعدات",
  equipment_selection_criteria: "معايير اختيار المعدات",
  equipment_dependencies: "اعتماديات المعدات",
  maintenance_requirements: "متطلبات الصيانة",
  maintenance_strategy: "استراتيجية الصيانة",
  preventive_maintenance_requirements: "متطلبات الصيانة الوقائية",
  maintenance_cycle: "دورة الصيانة",
  spare_parts_requirements: "متطلبات قطع الغيار",
  equipment_life_assessment: "تقييم العمر التشغيلي للمعدات",
  technical_risk_register: "سجل المخاطر الفنية",
  operational_risk_register: "سجل المخاطر التشغيلية",
  risk_category: "فئة المخاطر",
  risk_description: "وصف المخاطر",
  risk_cause: "سبب المخاطر",
  risk_trigger: "مؤشر تحقق المخاطر",
  probability_assessment: "تقييم الاحتمالية",
  impact_assessment: "تقييم الأثر",
  risk_level: "مستوى المخاطر",
  overall_risk_matrix: "مصفوفة المخاطر الإجمالية",
  risk_priority: "أولوية المخاطر",
  critical_risks: "المخاطر الحرجة",
  high_priority_risks: "المخاطر عالية الأولوية",
  technical_failure_risks: "مخاطر الأعطال الفنية",
  equipment_failure_risks: "مخاطر أعطال المعدات",
  process_failure_risks: "مخاطر تعطل العمليات",
  capacity_risks: "مخاطر الطاقة الإنتاجية",
  infrastructure_risks: "مخاطر البنية التحتية",
  utility_risks: "مخاطر المرافق",
  technology_risks: "مخاطر التقنية",
  maintenance_risks: "مخاطر الصيانة",
  quality_risks: "مخاطر الجودة",
  safety_risks: "مخاطر السلامة",
  environmental_risks: "المخاطر البيئية",
  workforce_risks: "مخاطر القوى العاملة",
  automation_risks: "مخاطر الأتمتة",
  external_dependency_risks: "مخاطر الاعتماديات الخارجية",
  project_overview: "نظرة عامة على المشروع",

  technical_operating_model_summary: "ملخص النموذج الفني والتشغيلي",

  capacity_summary: "ملخص الطاقة الإنتاجية",

  site_and_infrastructure_summary: "ملخص الموقع والبنية التحتية",

  equipment_and_technology_summary: "ملخص المعدات والتقنيات",

  operations_process_summary: "ملخص العمليات التشغيلية",

  quality_and_storage_summary: "ملخص الجودة والتخزين",

  workforce_summary: "ملخص القوى العاملة",

  safety_and_environment_summary: "ملخص السلامة والبيئة",

  automation_summary: "ملخص الأتمتة",

  critical_technical_risks: "المخاطر الفنية والتشغيلية الحرجة",

  critical_information_gaps: "فجوات المعلومات الحرجة",

  critical_pre_operation_requirements: "المتطلبات الحرجة قبل التشغيل",

  overall_technical_feasibility: "التقييم العام للجدوى الفنية",

  overall_operational_readiness: "التقييم العام للجاهزية التشغيلية",

  decision_conditions: "شروط القرار",

  recommended_next_steps: "الخطوات التالية الموصى بها",

  final_executive_conclusion: "الخلاصة التنفيذية النهائية",
  risk_mitigation: "معالجة المخاطر",
  mitigation_actions: "إجراءات التخفيف",
  preventive_actions: "الإجراءات الوقائية",
  corrective_actions: "الإجراءات التصحيحية",
  contingency_actions: "إجراءات الطوارئ",
  risk_owner: "مسؤول المخاطر",
  monitoring_requirements: "متطلبات المراقبة",
  residual_risk: "المخاطر المتبقية",

  implementation_requirements: "متطلبات التنفيذ",
  implementation_readiness: "جاهزية التنفيذ",
  implementation_prerequisites: "المتطلبات السابقة للتنفيذ",
  technical_prerequisites: "المتطلبات الفنية السابقة",
  operational_prerequisites: "المتطلبات التشغيلية السابقة",
  regulatory_prerequisites: "المتطلبات التنظيمية السابقة",
  site_readiness_requirements: "متطلبات جاهزية الموقع",
  infrastructure_readiness_requirements: "متطلبات جاهزية البنية التحتية",
  equipment_procurement_requirements: "متطلبات شراء المعدات",
  technology_procurement_requirements: "متطلبات شراء التقنية",
  installation_requirements: "متطلبات التركيب",
  commissioning_requirements: "متطلبات التشغيل التجريبي",
  testing_and_validation_requirements: "متطلبات الاختبار والتحقق",
  operational_startup_requirements: "متطلبات بدء التشغيل",
  training_and_handover_requirements: "متطلبات التدريب والتسليم",
  documentation_requirements: "متطلبات التوثيق",
  permit_requirements: "متطلبات التراخيص",
  compliance_requirements: "متطلبات الامتثال",
  implementation_dependencies: "اعتماديات التنفيذ",
  critical_path_items: "عناصر المسار الحرج",
  implementation_gaps: "فجوات التنفيذ",
  readiness_gaps: "فجوات الجاهزية",
  go_live_requirements: "متطلبات بدء التشغيل الفعلي",
  technology_assessment: "تقييم التقنيات",
  technology_type: "نوع التقنية",
  technology_suitability: "ملاءمة التقنية",
  technology_requirements: "المتطلبات التقنية",
  custom_system_assessment: "تقييم الحاجة إلى نظام مخصص",
  system_integration_requirements: "متطلبات تكامل الأنظمة",
  technical_vendor_requirements: "متطلبات الموردين الفنيين",
  workforce_requirements: "متطلبات القوى العاملة",
  workforce_structure: "هيكل القوى العاملة",
  workforce_capacity: "طاقة القوى العاملة",
  staffing_assessment: "تقييم الاحتياج الوظيفي",
  staffing_requirements: "متطلبات التوظيف",
  employee_count_assessment: "تقييم عدد الموظفين",
  roles_and_responsibilities: "الأدوار والمسؤوليات",
  job_roles_assessment: "تقييم الأدوار الوظيفية",
  job_descriptions: "الوصف الوظيفي",
  skills_requirements: "متطلبات المهارات",
  competency_requirements: "متطلبات الكفاءات",
  training_requirements: "متطلبات التدريب",
  training_plan: "خطة التدريب",
  technical_training_requirements: "متطلبات التدريب الفني",
  operational_training_requirements: "متطلبات التدريب التشغيلي",
  workforce_readiness: "جاهزية القوى العاملة",
  workforce_gaps: "فجوات القوى العاملة",

  safety_assessment: "تقييم السلامة",
  operational_safety: "السلامة التشغيلية",
  safety_requirements: "متطلبات السلامة",
  occupational_safety_requirements: "متطلبات السلامة المهنية",
  ppe_requirements: "متطلبات معدات الوقاية الشخصية",
  emergency_requirements: "متطلبات الطوارئ",
  emergency_response_requirements: "متطلبات الاستجابة للطوارئ",
  fire_safety_requirements: "متطلبات السلامة من الحريق",
  evacuation_requirements: "متطلبات الإخلاء",
  safety_controls: "ضوابط السلامة",
  safety_training_requirements: "متطلبات التدريب على السلامة",
  safety_compliance_requirements: "متطلبات الامتثال للسلامة",

  environmental_assessment: "التقييم البيئي",
  environmental_requirements: "المتطلبات البيئية",
  waste_assessment: "تقييم النفايات",
  waste_rate: "معدل النفايات",
  waste_management_requirements: "متطلبات إدارة النفايات",
  environmental_controls: "الضوابط البيئية",
  environmental_compliance_requirements: "متطلبات الامتثال البيئي",
  environmental_operational_risks: "المخاطر البيئية التشغيلية",

  automation_assessment: "تقييم الأتمتة",
  automation_level: "مستوى الأتمتة",
  automation_requirements: "متطلبات الأتمتة",
  automation_opportunities: "فرص الأتمتة",
  automation_constraints: "قيود الأتمتة",
  digital_operations_requirements: "متطلبات التشغيل الرقمي",
  monitoring_system_requirements: "متطلبات أنظمة المراقبة",
  control_system_requirements: "متطلبات أنظمة التحكم",
  data_collection_requirements: "متطلبات جمع البيانات",
  operational_reporting_requirements: "متطلبات التقارير التشغيلية",
  automation_readiness: "جاهزية الأتمتة",
  automation_gaps: "فجوات الأتمتة",
  process_flow_assessment: "تقييم سير العملية",
  process_flow: "سير العملية",
  process_stages: "مراحل العملية",
  process_stage_assessment: "تقييم مراحل العملية",
  process_stages_count: "عدد مراحل العملية",
  stage_timing: "أزمنة المراحل",
  process_timing_assessment: "تقييم أزمنة العمليات",
  workflow_efficiency: "كفاءة سير العمل",
  workflow_dependencies: "اعتماديات سير العمل",
  operational_bottlenecks: "نقاط الاختناق التشغيلية",
  external_parties_assessment: "تقييم الأطراف الخارجية",
  external_dependencies: "الاعتماديات الخارجية",
  process_control_requirements: "متطلبات ضبط العمليات",
  operational_controls: "الضوابط التشغيلية",

  quality_assessment: "تقييم الجودة",
  quality_requirements: "متطلبات الجودة",
  quality_standards: "معايير الجودة",
  quality_control_requirements: "متطلبات ضبط الجودة",
  inspection_requirements: "متطلبات الفحص",
  testing_requirements: "متطلبات الاختبار",
  nonconformity_management: "إدارة حالات عدم المطابقة",
  quality_documentation: "توثيق الجودة",

  storage_assessment: "تقييم التخزين",
  storage_requirements: "متطلبات التخزين",
  material_handling_requirements: "متطلبات مناولة المواد",
  inventory_handling_requirements: "متطلبات مناولة المخزون",
  storage_conditions: "ظروف التخزين",
  storage_capacity_requirements: "متطلبات السعة التخزينية",
  warehouse_requirements: "متطلبات المستودع",
  layout_assessment: "تقييم التخطيط الداخلي",
  layout_requirements: "متطلبات التخطيط الداخلي",
  material_flow_requirements: "متطلبات تدفق المواد",
  country_analysis: "تحليل الدولة",
  industry_analysis: "تحليل القطاع",
  market_analysis: "تحليل السوق",
  customer_analysis: "تحليل العملاء",
  competitor_analysis: "تحليل المنافسين",
  swot_analysis: "تحليل نقاط القوة والضعف والفرص والتهديدات (SWOT)",
  pestel_analysis: "تحليل PESTEL",
  porters_five_forces: "قوى بورتر الخمس",
  operations_plan: "خطة العمليات",
  marketing_strategy: "استراتيجية التسويق",
  human_resources: "الموارد البشرية",
  technology_strategy: "استراتيجية التقنية",
  legal_analysis: "التحليل القانوني",
  financial_assumptions: "الافتراضات المالية",
  startup_costs: "تكاليف التأسيس",
  revenue_model: "نموذج الإيرادات",
  financial_forecast: "التوقعات المالية",
  cash_flow: "التدفقات النقدية",
  break_even_analysis: "تحليل نقطة التعادل",
  roi_analysis: "تحليل العائد على الاستثمار",
  sensitivity_analysis: "تحليل الحساسية",
  risk_analysis: "تحليل المخاطر",
  governance_framework: "إطار الحوكمة",
  mitigation_plan: "خطة التخفيف",
  implementation_roadmap: "خارطة التنفيذ",
  kpi_framework: "إطار مؤشرات الأداء",
  executive_summary: "الملخص التنفيذي",
  final_review: "المراجعة النهائية",
  investment_recommendation: "التوصية الاستثمارية",
  implementation_readiness: "جاهزية التنفيذ",
  recommendations: "التوصيات",
  references: "المراجع",
  appendix: "الملحق",
  technical_overview: "النظرة العامة الفنية والتشغيلية",
  capacity_analysis: "تحليل الطاقة الإنتاجية",
  site_infrastructure_analysis: "تحليل الموقع والبنية التحتية",
  project_technical_profile: "الملف الفني للمشروع",
  product_or_service_technical_nature: "الطبيعة الفنية للمنتج أو الخدمة",
  operating_model: "النموذج التشغيلي",
  core_operational_requirements: "المتطلبات التشغيلية الأساسية",
  critical_technical_dependencies: "الاعتماديات الفنية الحرجة",
  technical_complexity: "التعقيد الفني",
  current_readiness: "الجاهزية الحالية",
  key_readiness_gaps: "فجوات الجاهزية الرئيسية",
  critical_design_considerations: "اعتبارات التصميم الحرجة",
  information_gaps: "فجوات المعلومات",

  production_or_service_unit: "وحدة الإنتاج أو الخدمة",
  target_monthly_capacity: "الطاقة الشهرية المستهدفة",
  maximum_capacity: "الطاقة القصوى",
  capacity_gap_assessment: "تقييم فجوة الطاقة",
  shift_structure: "هيكل الورديات",
  daily_shifts: "عدد الورديات اليومية",
  shift_hours: "ساعات الوردية",
  capacity_consistency_assessment: "تقييم اتساق الطاقة",
  process_time_implications: "آثار أزمنة العمليات",
  potential_bottlenecks: "نقاط الاختناق المحتملة",
  capacity_buffer_requirements: "متطلبات هامش الطاقة",
  scalability_assessment: "تقييم قابلية التوسع",
  trial_operation_assessment: "تقييم التشغيل التجريبي",
  capacity_validation_requirements: "متطلبات التحقق من الطاقة",

  site_type_assessment: "تقييم نوع الموقع",
  required_site_type: "نوع الموقع المطلوب",
  operational_rationale: "المبررات التشغيلية",
  space_assessment: "تقييم المساحة",
  space_drivers: "العوامل المؤثرة في المساحة",
  verification_required: "التحقق المطلوب",
  location_assessment: "تقييم الموقع",
  preferred_location: "الموقع المفضل",
  operational_considerations: "الاعتبارات التشغيلية",
  constraints: "القيود",
  electricity_assessment: "تقييم الكهرباء",
  equipment_technology_analysis: "تحليل المعدات والتقنيات",
  operations_process_analysis: "تحليل العمليات التشغيلية",
  quality_storage_analysis: "تحليل الجودة والتخزين",

  workforce_analysis: "تحليل القوى العاملة",
  safety_environment_analysis: "تحليل السلامة والبيئة",
  automation_analysis: "تحليل الأتمتة",

  technical_risk_analysis: "تحليل المخاطر الفنية والتشغيلية",
  implementation_requirements: "متطلبات التنفيذ",

  executive_summary: "الملخص التنفيذي",
  key_findings: "أبرز النتائج",
  key_takeaways: "أبرز الاستنتاجات",
  key_assumptions: "الافتراضات الرئيسية",
  feasibility_score: "مؤشر الجدوى",
  readiness_score: "درجة الجاهزية",
  investment_highlights: "أبرز نقاط الاستثمار",
  investment_strengths: "نقاط القوة الاستثمارية",
  investment_constraints: "قيود الاستثمار",
  investment_conditions: "شروط الاستثمار",
  go_conditional_go_no_go_assessment: "قرار الاستثمار",
  determination: "القرار",
  justification: "المبررات",
  overall_score: "الدرجة الإجمالية",
  scenario: "السيناريو",
  metric: "المؤشر",
  value: "القيمة",
  category: "الفئة",
  rating: "التصنيف",
  risk_level: "مستوى الخطورة",
  year: "السنة",
  month: "الشهر",
  period: "الفترة",
  item: "البند",
  name: "الاسم",
  amount: "المبلغ",
  cost: "التكلفة",
  price: "السعر",
  revenue: "الإيرادات",
  profit: "الربح",
  margin: "الهامش",
  growth: "النمو",
  share: "الحصة",
  total: "الإجمالي",
  best_case: "أفضل سيناريو",
  expected_case: "السيناريو المتوقع",
  base_case: "السيناريو الأساسي",
  worst_case: "أسوأ سيناريو",
  low: "منخفض",
  medium: "متوسط",
  high: "مرتفع",
  executive_conclusion: "الخلاصة التنفيذية",

  strategic_risks: "المخاطر الاستراتيجية",
  strategic_risk_affecting_business_model: STRATEGIC_BUSINESS_MODEL_RISK_AR,
  strategic_risks_affecting_business_model: STRATEGIC_BUSINESS_MODEL_RISK_AR,
  strategic_المخاطر_affecting_business_model: STRATEGIC_BUSINESS_MODEL_RISK_AR,
  market_risks: "مخاطر السوق",
  competitive_risks: "المخاطر التنافسية",
  financial_risks: "المخاطر المالية",
  operational_risks: "المخاطر التشغيلية",

  critical_success_factors: "عوامل النجاح الحاسمة",

  economic_country_overview: "النظرة الاقتصادية العامة للدولة",
  macroeconomic_indicators: "مؤشرات الاقتصاد الكلي",
  political_environment: "البيئة السياسية",
  investment_climate: "مناخ الاستثمار",
  infrastructure: "البنية التحتية",
  digital_readiness: "الجاهزية الرقمية",
  demographic_profile: "الملف الديموغرافي",
  regulatory_environment: "البيئة التنظيمية",
  business_environment: "بيئة الأعمال",
  opportunities: "الفرص",

  market_opportunities: "فرص السوق",
  market_risks_and_constraints: "مخاطر وقيود السوق",
  competitive_environment: "البيئة التنافسية",
  market_entry_conditions: "شروط دخول السوق",
  market_entry_requirements: "متطلبات دخول السوق",
  market_attractiveness: "جاذبية السوق",
  market_feasibility: "الجدوى السوقية",
  market_readiness: "جاهزية السوق",

  customer_segments: "شرائح العملاء",
  customer_needs: "احتياجات العملاء",
  customer_preferences: "تفضيلات العملاء",
  buying_behavior: "سلوك الشراء",
  purchase_drivers: "محركات الشراء",

  competitive_landscape: "المشهد التنافسي",
  competitive_advantages: "المزايا التنافسية",
  competitive_disadvantages: "نقاط الضعف التنافسية",
  competitive_positioning: "التموضع التنافسي",

  market_entry_strategy: "استراتيجية دخول السوق",
  go_to_market_strategy: "استراتيجية الوصول إلى السوق",
  positioning_strategy: "استراتيجية التموضع",
  pricing_strategy: "استراتيجية التسعير",
  distribution_strategy: "استراتيجية التوزيع",

  immediate_priorities: "الأولويات العاجلة",
  post_entry_priorities: "أولويات ما بعد دخول السوق",
  strategic_implications: "الآثار الاستراتيجية",
  final_assessment: "التقييم النهائي",
  final_decision: "القرار النهائي",
};

Object.assign(ARABIC_HEADING_OVERRIDES, {
  the_opportunity: "الفرصة السوقية",
  market_definition: "تعريف السوق محل الدراسة",
  market_size_and_opportunity: "حجم السوق والفرصة المتاحة",
  total_addressable_market_tam: "إجمالي السوق القابل للاستهداف (TAM)",

  serviceable_addressable_market_sam: "السوق المتاح للخدمة (SAM)",

  serviceable_obtainable_market_som: "السوق الممكن الاستحواذ عليه (SOM)",
  demand_analysis: "تحليل الطلب",
  supply_analysis: "تحليل العرض",
  customer_behavior: "سلوك العملاء وآلية الشراء",
  market_segmentation: "تجزئة السوق",
  market_trends: "اتجاهات السوق",
  market_trends_and_drivers: "اتجاهات السوق ومحركاته",
  growth_forecast: "توقعات نمو السوق",
  market_gaps: "الفجوات السوقية",
  market_readiness_assessment: "تقييم جاهزية السوق",
  overall_market_feasibility_verdict: "الحكم النهائي على الجدوى السوقية",
  overall_market_risk_verdict: "الحكم النهائي على مخاطر السوق",
  external_environment_verdict: "حكم البيئة الخارجية",
  business_environment: "بيئة الأعمال",
  industry_opportunities: "فرص القطاع",
  industry_structure_score: "درجة هيكل القطاع",
  barriers_to_entry: "حواجز دخول السوق",
  buyer_power: "قوة المشترين",
  supplier_power: "قوة الموردين",
  substitution_threat: "تهديد البدائل",
  force_scores: "درجات القوى التنافسية",
  overall_rating: "التقييم العام",
  competitive_advantage: "الميزة التنافسية",
  competitive_alternatives_and_differentiation: "البدائل التنافسية والتميّز",
  competitive_positioning_strategy: "استراتيجية التموضع التنافسي",
  market_positioning_strategy: "استراتيجية التموضع في السوق",
  target_customer_strategy: "استراتيجية العميل المستهدف",
  customer_acquisition_strategy: "استراتيجية اكتساب العملاء",
  customer_retention_strategy: "استراتيجية الاحتفاظ بالعملاء",
  distribution_channel_strategy: "استراتيجية قنوات التوزيع",
  go_to_market_strategy: "استراتيجية دخول السوق",
  go_to_market_logic_assessment: "تقييم منطق دخول السوق",
  market_prioritization: "ترتيب أولويات الأسواق",
  market_entry_roadmap: "خارطة دخول السوق",
  post_entry_monitoring_priorities: "أولويات المتابعة بعد دخول السوق",
  strategic_market_recommendations: "التوصيات السوقية الاستراتيجية",
  stage2_feasibility_impact: "أثر تحليل السوق على الجدوى",
  strategic_fit: "الملاءمة الاستراتيجية للسوق",
  value_proposition_strategy: "استراتيجية القيمة المقترحة",
  value_proposition_market_alignment: "مواءمة القيمة المقترحة مع السوق",
  customer_segment_alignment: "مواءمة شرائح العملاء",
  market_fit_assessment: "تقييم ملاءمة السوق",
  market_scalability_assessment: "تقييم قابلية التوسع السوقي",
  business_model_sustainability_under_market_conditions:
    "استدامة نموذج العمل في ظروف السوق",
  revenue_logic_market_appropriateness: "ملاءمة منطق الإيرادات للسوق",
  strategic_positioning_alignment: "مواءمة التموضع الاستراتيجي",
  competitive_differentiation_assessment: "تقييم التميّز التنافسي",
  overall_business_model_viability: "قابلية نموذج العمل سوقيًا",
  overall_marketing_strategy_viability: "قابلية استراتيجية التسويق سوقيًا",
  commercial_consistency: "الاتساق التجاري",
  governance_consistency: "اتساق الحوكمة",
  operational_consistency: "الاتساق التشغيلي",
  financial_consistency: "الاتساق المالي",
  risk_consistency: "اتساق المخاطر",
  strategic_consistency: "الاتساق الاستراتيجي",
  strategic_coherence_assessment: "تقييم الاتساق الاستراتيجي",
  country_and_political_risks: "مخاطر الدولة والسياسة",
  industry_and_regulatory_risks: "مخاطر القطاع والتنظيم",
  commercial_and_pricing_risks: "المخاطر التجارية والتسعيرية",
  go_to_market_and_adoption_risks: "مخاطر دخول السوق والتبني",
  supply_chain_and_market_access_risks: "مخاطر سلسلة الإمداد والوصول للسوق",
  technology_and_market_relevance_risks: "مخاطر التقنية وملاءمتها للسوق",
  brand_and_reputation_risks: "مخاطر العلامة والسمعة",
  material_risks_and_manageability: "المخاطر الجوهرية وقابلية إدارتها",
  residual_market_risk_evaluation: "تقييم مخاطر السوق المتبقية",
  market_entry_risk_mitigation: "تخفيف مخاطر دخول السوق",
  market_accessibility_risk_mitigation: "تخفيف مخاطر الوصول للسوق",
  customer_adoption_risk_mitigation: "تخفيف مخاطر تبني العملاء",
  competitive_positioning_risk_mitigation: "تخفيف مخاطر التموضع التنافسي",
  distribution_channel_risk_mitigation: "تخفيف مخاطر قنوات التوزيع",
  pricing_commercial_risk_mitigation: "تخفيف مخاطر التسعير والمخاطر التجارية",
  regulatory_market_risk_mitigation: "تخفيف المخاطر التنظيمية السوقية",
  industry_dynamics_risk_mitigation: "تخفيف مخاطر ديناميكيات القطاع",
  supply_chain_market_availability_risk_mitigation:
    "تخفيف مخاطر توفر السوق وسلسلة الإمداد",
  key_value_drivers: "محركات القيمة الرئيسية",
  critical_decision_factors: "عوامل القرار الحرجة",
  immediate_executive_priorities: "الأولويات التنفيذية العاجلة",
  assumptions_and_limitations: "الافتراضات والقيود",
  impact_assessment: "تقييم الأثر",
  probability_assessment: "تقييم الاحتمالية",
  residual_risks: "المخاطر المتبقية",
  risk_prioritization: "ترتيب أولويات المخاطر",
  accessed_date: "تاريخ الوصول",
  analysis_horizon: "أفق التحليل",
  assumption: "الافتراض",
  assumption_change: "تغير الافتراض",
  base_value: "القيمة الأساسية",
  basis: "أساس التقدير",
  commentary: "تعليق تحليلي",
  conclusion: "الخلاصة",
  amount: "المبلغ",
  percentage: "النسبة",
  timeline: "الجدول الزمني",
  scenario_name: "اسم السيناريو",
  driver: "المحرك",
  impact_description: "وصف الأثر",
  rationale_for_selection: "مبررات الاختيار",
  success_factors: "عوامل النجاح",
  strategic_implications: "الدلالات الاستراتيجية",
  critical_risks: "المخاطر الحرجة",
  risks: "المخاطر",
  decision_area: "مجال القرار",
  decision_gates: "بوابات القرار",
  approval_threshold: "حد الاعتماد",
  mandate: "التفويض",
  mandate_statement: "بيان التفويض",
  composition: "التكوين",
  committee_name: "اسم اللجنة",
  authority_level: "مستوى الصلاحية",
  reporting_structure: "هيكل التقارير",
  measurement_frequency: "وتيرة القياس",
  corrective_action_triggers: "محفزات الإجراءات التصحيحية",
  dashboard_recommendations: "توصيات لوحة المتابعة",
  kpi_ownership: "ملكية المؤشرات",
  risk_ownership: "ملكية المخاطر",
  resource_allocation: "تخصيص الموارد",
  dependencies: "الاعتماديات",
  critical_path: "المسار الحرج",
  readiness_criteria: "معايير الجاهزية",
  resource_readiness: "جاهزية الموارد",
  dependency_readiness: "جاهزية الاعتماديات",
  process_readiness: "جاهزية العمليات",
  strategic_readiness: "الجاهزية الاستراتيجية",
  financial_readiness: "الجاهزية المالية",
  risk_readiness: "جاهزية المخاطر",
  infrastructure_readiness: "جاهزية البنية التحتية",
  decision_making_readiness: "جاهزية اتخاذ القرار",
  adaptability_assessment: "تقييم القدرة على التكيف",
  operational_readiness_assessment: "تقييم الجاهزية التشغيلية",
  technology_readiness_assessment: "تقييم الجاهزية التقنية",
  hr_readiness_assessment: "تقييم جاهزية الموارد البشرية",
  human_capital_readiness: "جاهزية رأس المال البشري",
  organizational_readiness: "الجاهزية التنظيمية",
  organizational_governance_model: "نموذج الحوكمة التنظيمية",
  leadership_readiness: "جاهزية القيادة",
  leadership_structure: "هيكل القيادة",
  employee_onboarding: "تهيئة الموظفين",
  employee_engagement: "اندماج الموظفين",
  learning_and_development: "التعلم والتطوير",
  compensation_philosophy: "فلسفة التعويضات",
  talent_acquisition: "استقطاب المواهب",
  succession_planning: "تخطيط التعاقب",
  workforce_capacity_planning: "تخطيط الطاقة البشرية",
  workforce_risks: "مخاطر القوى العاملة",
  human_resource_setup_costs: "تكاليف تأسيس الموارد البشرية",
  hr_policies_framework: "إطار سياسات الموارد البشرية",
  hr_kpis: "مؤشرات الموارد البشرية",
  human_resource_kpis: "مؤشرات أداء الموارد البشرية",
  customer_kpis: "مؤشرات العملاء",
  marketing_kpis: "مؤشرات التسويق",
  sales_kpis: "مؤشرات المبيعات",
  governance_kpis: "مؤشرات الحوكمة",
  technology_kpis: "مؤشرات التقنية",
  implementation_kpis: "مؤشرات التنفيذ",
  risk_management_kpis: "مؤشرات إدارة المخاطر",
  marketing_risks: "مخاطر التسويق",
  environmental_risks: "المخاطر البيئية",
  industry_risks: "مخاطر القطاع",
  technology_risks: "المخاطر التقنية",
  startup_cost_risks: "مخاطر تكاليف التأسيس",
  brand_reputation_risk_mitigation: "تخفيف مخاطر العلامة والسمعة",
  go_to_market_risk_mitigation: "تخفيف مخاطر دخول السوق",
  risk_escalation_framework: "إطار تصعيد المخاطر",
  risk_integration: "دمج المخاطر",
  risk_interdependencies: "الترابط بين المخاطر",
  overall_risk_matrix: "مصفوفة المخاطر الإجمالية",
  monitoring_and_review_process: "عملية المتابعة والمراجعة",
  technology_trends: "اتجاهات التقنية",
  technology_readiness: "الجاهزية التقنية",
  technology_scalability: "قابلية التوسع التقني",
  technology_stack_rationale: "مبررات البنية التقنية",
  technology_cost_considerations: "اعتبارات التكلفة التقنية",
  automation_strategy: "استراتيجية الأتمتة",
  cloud_and_hosting_strategy: "استراتيجية السحابة والاستضافة",
  cybersecurity_strategy: "استراتيجية الأمن السيبراني",
  integration_strategy: "استراتيجية التكامل",
  vendor_and_technology_partner_strategy:
    "استراتيجية الموردين والشركاء التقنيين",
  financial_analytics_requirements: "متطلبات التحليلات المالية",
  operational_technology_requirements: "المتطلبات التقنية التشغيلية",
  facilities_and_infrastructure_requirements: "متطلبات المرافق والبنية التحتية",
  capacity_planning: "تخطيط الطاقة الاستيعابية",
  procurement_strategy: "استراتيجية المشتريات",
  inventory_strategy: "استراتيجية المخزون",
  quality_management_framework: "إطار إدارة الجودة",
  standard_operating_procedures: "إجراءات التشغيل القياسية",
  business_continuity_considerations: "اعتبارات استمرارية الأعمال",
  operational_governance: "الحوكمة التشغيلية",
  organization: "المنظمة",
  organizational_culture: "الثقافة التنظيمية",
  board_oversight_responsibilities: "مسؤوليات إشراف مجلس الإدارة",
  executive_management_responsibilities: "مسؤوليات الإدارة التنفيذية",
  governance_touchpoints: "نقاط ارتباط الحوكمة",
  it_governance: "حوكمة تقنية المعلومات",
  performance_management: "إدارة الأداء",
  performance_thresholds: "حدود الأداء",
  progress_monitoring: "متابعة التقدم",
  capital_investment_assumptions: "افتراضات الاستثمار الرأسمالي",
  financing_assumptions: "افتراضات التمويل",
  tax_assumptions: "الافتراضات الضريبية",
  foreign_exchange_assumptions: "افتراضات أسعار الصرف",
  working_capital_assumptions: "افتراضات رأس المال العامل",
  working_capital_requirements: "متطلبات رأس المال العامل",
  cost_structure: "هيكل التكلفة",
  fixed_costs: "التكاليف الثابتة",
  variable_costs: "التكاليف المتغيرة",
  contribution_margin: "هامش المساهمة",
  contribution_margin_ratio: "نسبة هامش المساهمة",
  unit_contribution_margin: "هامش مساهمة الوحدة",
  margin_of_safety: "هامش الأمان",
  operating_leverage: "الرافعة التشغيلية",
  degree_of_operating_leverage: "درجة الرافعة التشغيلية",
  break_even_point: "نقطة التعادل",
  break_even_revenue: "إيرادات نقطة التعادل",
  break_even_units: "وحدات نقطة التعادل",
  break_even_period: "فترة الوصول للتعادل",
  break_even_scenarios: "سيناريوهات نقطة التعادل",
  resulting_break_even: "نقطة التعادل الناتجة",
  time_to_break_even: "المدة للوصول إلى التعادل",
  drivers_affecting_break_even: "العوامل المؤثرة في نقطة التعادل",
  payback_period: "فترة الاسترداد",
  total_investment: "إجمالي الاستثمار",
  cumulative_investment: "الاستثمار التراكمي",
  cumulative_return: "العائد التراكمي",
  cumulative_net_return: "صافي العائد التراكمي",
  simple_roi_percentage: "نسبة العائد البسيط على الاستثمار",
  annualized_roi_percentage: "نسبة العائد السنوي على الاستثمار",
  revenue_assumptions_and_key_drivers: "افتراضات الإيرادات ومحركاتها الرئيسية",
  cash_flow_assumptions_and_validation: "افتراضات التدفق النقدي والتحقق منها",
  cash_flow_risks_and_mitigation: "مخاطر التدفق النقدي وتخفيفها",
  cash_reserve_strategy_and_adequacy: "استراتيجية الاحتياطي النقدي وكفايته",
  funding_requirements_and_rounds_strategy:
    "متطلبات التمويل واستراتيجية الجولات",
  liquidity_kpis_and_monitoring_framework: "مؤشرات السيولة وإطار المتابعة",
  net_position: "صافي المركز",
  customer_lifetime_value_and_retention: "قيمة العميل الدائمة والاحتفاظ",
  pricing_strategy: "استراتيجية التسعير",
  marketing_communication_strategy: "استراتيجية الاتصال التسويقي",
  marketing_launch_costs: "تكاليف الإطلاق التسويقي",
  marketing_mix_strategy: "استراتيجية المزيج التسويقي",
  critical_marketing_success_factors: "عوامل نجاح التسويق الحرجة",
  executive_marketing_strategy: "الاستراتيجية التسويقية التنفيذية",
  business_model_as_presented: "نموذج العمل كما هو مقدم",
  executive_business_model_assessment: "تقييم نموذج العمل التنفيذي",
  alignment_with_business_model_and_financial_assumptions:
    "المواءمة مع نموذج العمل والافتراضات المالية",
  stage3_marketing_strategy_input: "مدخلات استراتيجية التسويق للمرحلة الثالثة",
  stage3_business_strategy_synthesis:
    "تلخيص استراتيجية الأعمال للمرحلة الثالثة",
  stage3_strategic_implications: "الدلالات الاستراتيجية للمرحلة الثالثة",
  stage3_human_resources_readiness_impact:
    "أثر جاهزية الموارد البشرية في المرحلة الثالثة",
  stage3_technology_strategy_readiness_impact:
    "أثر جاهزية الاستراتيجية التقنية في المرحلة الثالثة",
  competitor_type: "نوع المنافس",
  geographic_scope: "النطاق الجغرافي",
  target_customers: "العملاء المستهدفون",
  services_and_offering: "الخدمات والعرض",
  strengths: "نقاط القوة",
  weaknesses: "نقاط الضعف",
  positioning: "التموضع التنافسي",
  pricing: "التسعير",
  threat_level: "مستوى التهديد",
  strategic_implication: "الدلالة الاستراتيجية",
});
// =========================================================
// TECHNICAL / OPERATIONAL — COMPLETE ARABIC HEADINGS
// =========================================================
Object.assign(ARABIC_HEADING_OVERRIDES, {
  technical_complexity: "التعقيد الفني",
  technical_complexity_assessment: "تقييم التعقيد الفني",

  current_readiness: "الجاهزية الحالية",
  readiness_assessment: "تقييم الجاهزية",
  technical_readiness: "الجاهزية الفنية",
  technical_readiness_assessment: "تقييم الجاهزية الفنية",
  operational_readiness: "الجاهزية التشغيلية",
  operational_readiness_assessment: "تقييم الجاهزية التشغيلية",

  water: "المياه",
  water_assessment: "تقييم متطلبات المياه",
  electricity_assessment: "تقييم متطلبات الكهرباء",

  network: "الشبكة",
  network_and_digital_infrastructure: "الشبكة والبنية التحتية الرقمية",
  digital_infrastructure: "البنية التحتية الرقمية",

  safety_infrastructure: "البنية التحتية للسلامة",
  safety_infrastructure_assessment: "تقييم البنية التحتية للسلامة",

  permit_and_regulatory_readiness: "جاهزية التراخيص والمتطلبات التنظيمية",
  permit_regulatory_readiness: "جاهزية التراخيص والمتطلبات التنظيمية",
  regulatory_readiness: "الجاهزية التنظيمية",

  stated_requirements: "المتطلبات المقدمة",
  stated_quantity: "الكمية المقدمة",
  requirement_status: "حالة المتطلب",
  stated_equipment: "المعدات المقدمة",
  stated_technologies: "التقنيات المقدمة",
  stated_standards: "المعايير المقدمة",
  stated_workforce_profile: "ملف القوى العاملة المقدم",
  stated_roles_or_functions: "الأدوار والوظائف المقدمة",
  stated_shift_structure: "هيكل الورديات المقدم",
  stated_training_requirements: "متطلبات التدريب المقدمة",
  stated_useful_life: "العمر التشغيلي المقدم",
  stated_cycle: "دورة الصيانة المقدمة",
  stated_need: "الاحتياج المقدم",
  stated_stage_count: "عدد المراحل المقدم",

  operational_roles: "الأدوار التشغيلية",
  operational_use_cases: "حالات الاستخدام التشغيلية",
  operational_implications: "الآثار التشغيلية",
  operational_considerations: "الاعتبارات التشغيلية",
  operational_layout_implications: "آثار التخطيط التشغيلي",
  operational_metrics_to_measure: "المؤشرات التشغيلية المطلوب قياسها",

  validation_required: "التحقق المطلوب",
  validation_actions: "إجراءات التحقق",
  recommended_validation: "التحقق الموصى به",
  technical_validation_required: "التحقق الفني المطلوب",
  procurement_validation_requirements: "متطلبات التحقق قبل الشراء",
  capacity_validation_requirements: "متطلبات التحقق من الطاقة",

  missing_information: "المعلومات الناقصة",
  information_gaps: "فجوات المعلومات",
  why_it_matters: "أهمية المعلومة",
  recommended_action: "الإجراء الموصى به",

  process_overview: "نظرة عامة على العملية",
  process_clarity_assessment: "تقييم وضوح العملية",
  process_stages_assessment: "تقييم مراحل العملية",
  stage_timing_assessment: "تقييم أزمنة المراحل",
  process_scalability_assessment: "تقييم قابلية توسع العملية",
  process_control_points: "نقاط ضبط العملية",
  process_safety_risks: "مخاطر سلامة العملية",

  stage_or_activity: "المرحلة أو النشاط",
  stated_timing: "الزمن المقدم",
  operational_interpretation: "التفسير التشغيلي",

  handoff_points: "نقاط التسليم بين المراحل",
  handoff: "نقطة التسليم",
  parties_involved: "الأطراف المعنية",

  external_party_dependencies: "الاعتماديات على الأطراف الخارجية",
  external_party: "الطرف الخارجي",
  dependency_level: "مستوى الاعتماد",
  continuity_risks: "مخاطر الاستمرارية",

  workflow_dependencies: "اعتماديات سير العمل",
  critical_equipment_dependencies: "اعتماديات المعدات الحرجة",
  storage_process_dependencies: "اعتماديات عملية التخزين",

  potential_bottlenecks: "نقاط الاختناق المحتملة",
  bottleneck: "نقطة الاختناق",
  cause: "السبب",
  operational_impact: "الأثر التشغيلي",
  validation_method: "طريقة التحقق",

  standardization_requirements: "متطلبات التوحيد القياسي",
  sop_requirements: "متطلبات إجراءات التشغيل القياسية",

  automation_opportunities: "فرص الأتمتة",
  automation_workforce_implications: "آثار الأتمتة على القوى العاملة",

  equipment_safety_risks: "مخاطر سلامة المعدات",
  technical_risks: "المخاطر الفنية",
  operational_risks: "المخاطر التشغيلية",

  quality_standards_assessment: "تقييم معايير الجودة",
  quality_control_points: "نقاط ضبط الجودة",
  quality_measurement_requirements: "متطلبات قياس الجودة",
  quality_documentation_requirements: "متطلبات توثيق الجودة",
  quality_readiness_assessment: "تقييم جاهزية الجودة",

  nonconformance_management_requirements: "متطلبات إدارة حالات عدم المطابقة",

  storage_requirements_assessment: "تقييم متطلبات التخزين",
  storage_space_implications: "آثار مساحة التخزين",
  storage_safety_requirements: "متطلبات سلامة التخزين",
  storage_risks: "مخاطر التخزين",
  storage_readiness_assessment: "تقييم جاهزية التخزين",

  role_structure_assessment: "تقييم هيكل الأدوار",
  role_responsibility_mapping: "توزيع الأدوار والمسؤوليات",
  critical_roles: "الأدوار الحرجة",

  supervision_and_coordination_requirements: "متطلبات الإشراف والتنسيق",

  shift_coverage_assessment: "تقييم تغطية الوردية",
  coverage_requirements_to_validate: "متطلبات التحقق من التغطية",

  training_requirements_assessment: "تقييم متطلبات التدريب",
  training_priorities: "أولويات التدريب",

  equipment_and_technology_skill_requirements:
    "متطلبات مهارات المعدات والتقنية",

  quality_and_safety_competency_requirements: "متطلبات كفاءات الجودة والسلامة",

  workforce_continuity_requirements: "متطلبات استمرارية القوى العاملة",

  workforce_scalability_assessment: "تقييم قابلية توسع القوى العاملة",

  workforce_readiness_assessment: "تقييم جاهزية القوى العاملة",

  operational_safety_assessment: "تقييم السلامة التشغيلية",
  site_safety_requirements: "متطلبات سلامة الموقع",

  equipment_lifecycle_assessment: "تقييم دورة الحياة التشغيلية للمعدات",

  maintenance_assessment: "تقييم الصيانة",

  scalability_status: "حالة قابلية التوسع",
  requirements_for_scaling: "متطلبات التوسع",

  clarity_level: "مستوى الوضوح",
  readiness_level: "مستوى الجاهزية",
  status: "الحالة",
  rationale: "المبررات",
  assessment: "التقييم",

  gaps: "الفجوات",
  constraints: "القيود",
  critical_dependencies: "الاعتماديات الحرجة",
  expansion_implications: "آثار التوسع",

  functional_requirements_to_define: "المتطلبات الوظيفية المطلوب تحديدها",

  systems_integration_requirements: "متطلبات تكامل الأنظمة",

  lifecycle_considerations: "اعتبارات دورة الحياة",

  support_equipment_required: "الحاجة إلى معدات مساندة",

  operational_reason: "المبرر التشغيلي",
  priority: "الأولوية",

  technology: "التقنية",
  process_area: "مجال العملية",
  potential_automation_role: "دور الأتمتة المحتمل",
  expected_operational_benefit: "الفائدة التشغيلية المتوقعة",

  role_or_function: "الدور أو الوظيفة",
  core_responsibilities: "المسؤوليات الأساسية",
  process_dependency: "الارتباط بالعملية",

  role_or_skill: "الدور أو المهارة",
  why_critical: "سبب الأهمية",
  operational_impact_if_unavailable: "الأثر التشغيلي عند عدم التوفر",
  continuity_requirement: "متطلب الاستمرارية",

  training_area: "مجال التدريب",
  target_role_or_group: "الدور أو الفئة المستهدفة",

  skill_area: "مجال المهارة",
  related_equipment_or_system: "المعدة أو النظام المرتبط",
  required_capability: "القدرة المطلوبة",

  area: "المجال",
  potential_workforce_effect: "الأثر المحتمل على القوى العاملة",
  reskilling_or_role_change_required: "متطلبات إعادة التأهيل أو تعديل الدور",

  process_stage: "مرحلة العملية",
  quality_control_requirement: "متطلب ضبط الجودة",
  reason: "السبب",
  evidence_or_record_required: "الدليل أو السجل المطلوب",

  quality_metric_or_check: "مؤشر أو فحص الجودة",
  measurement_method_to_define: "طريقة القياس المطلوب تحديدها",

  factor: "العامل",
  site_impact: "الأثر على الموقع",

  constraint: "القيد",

  equipment_or_function: "المعدة أو الوظيفة",
  failure_impact: "أثر التعطل",

  integration_area: "مجال التكامل",

  requirement: "المتطلب",
});
// =========================================================
// TECHNICAL / OPERATIONAL — REMAINING ARABIC TITLES
// =========================================================
Object.assign(ARABIC_HEADING_OVERRIDES, {
  continuity: "الاستمرارية",
  continuity_risks: "مخاطر الاستمرارية",

  stated_procedures: "الإجراءات المذكورة",
  consideration: "الاعتبار",

  id: "معرف المخاطر",
  risk_id: "معرف المخاطر",

  source: "مصدر المخاطر",
  risk_source: "مصدر المخاطر",

  monitoring_area: "مجال المراقبة",

  action: "الإجراء",

  expected_operational_outcome: "النتيجة التشغيلية المتوقعة",
  storage_safety_considerations: "اعتبارات سلامة التخزين",
  potential_impact: "الأثر المحتمل",
  operational_risk: "المخاطر التشغيلية",
  safety_readiness: "جاهزية السلامة",
  safety_readiness_assessment: "تقييم جاهزية السلامة",
  required_evidence_or_check: "الدليل أو الفحص المطلوب",
  purpose: "الغرض",
  control_point: "نقطة الضبط",
  process_impact: "أثر العملية",
  management_priority: "أولوية الإدارة",
  impact: "الأثر",
  manual_or_semi_manual_areas: "المجالات اليدوية أو شبه اليدوية",
  recommended_mode: "النمط الموصى به",
  consequence_if_not_met: "العواقب في حال عدم الاستيفاء",
  required_evidence: "الدليل المطلوب",
  decision_condition: "شرط القرار",
  gate: "بوابة التحقق",
  immediate_management_priority: "الأولوية الإدارية الفورية",
  operational_role: "الدور التشغيلي",
  maintenance_requirements_to_validate: "متطلبات الصيانة المطلوب التحقق منها",
  stage_consistency: "اتساق المراحل",
  role: "الدور",
  continuity_risks: "مخاطر الاستمرارية",
  recommended_control: "إجراء الضبط الموصى به",
  critical_gaps: "الفجوات الحرجة",
  workforce_safety_requirements: "متطلبات سلامة القوى العاملة",
  target_group: "الفئة المستهدفة",
  workforce_implications: "آثار القوى العاملة",
  safety_implications: "آثار السلامة",
  automation_scalability: "قابلية توسع الأتمتة",
  future_requirements: "المتطلبات المستقبلية",
  required_action: "الإجراء المطلوب",
  decision_impact: "أثر القرار",
  gap: "الفجوة",
  automation_candidates: "العمليات المرشحة للأتمتة",
  process_automation_readiness: "جاهزية أتمتة العمليات",
  automation_readiness_assessment: "تقييم جاهزية الأتمتة",
  automation_scalability: "قابلية توسع الأتمتة",
  capacity_related_risks: "المخاطر المرتبطة بالطاقة الإنتاجية",
  equipment_and_technology_risks: "مخاطر المعدات والتقنية",
  automation_and_data_risks: "مخاطر الأتمتة والبيانات",

  risk_monitoring_requirements: "متطلبات مراقبة المخاطر",
  risk_governance_requirements: "متطلبات حوكمة المخاطر",

  critical_prerequisites: "المتطلبات المسبقة الحرجة",

  technical_design_requirements: "متطلبات التصميم الفني",
  site_and_infrastructure_requirements: "متطلبات الموقع والبنية التحتية",
  equipment_and_technology_requirements: "متطلبات المعدات والتقنية",
  process_design_requirements: "متطلبات تصميم العمليات",
  quality_and_storage_requirements: "متطلبات الجودة والتخزين",
  workforce_and_training_requirements: "متطلبات القوى العاملة والتدريب",
  safety_and_environment_requirements: "متطلبات السلامة والبيئة",
  automation_and_system_requirements: "متطلبات الأتمتة والأنظمة",

  validation_gates: "بوابات التحقق",

  trial_operation_requirements: "متطلبات التشغيل التجريبي",
  objectives: "الأهداف",
  items_to_validate: "العناصر المطلوب التحقق منها",
  evidence_to_collect: "األدلة المطلوب جمعها",
  operational_acceptance_requirements: "متطلبات القبول التشغيلي",

  remaining_gaps: "الفجوات المتبقية",
  conditions_before_full_operation: "الشروط السابقة للتشغيل الكامل",
  priority_actions: "الإجراءات ذات الأولوية",

  key_points: "النقاط الرئيسية",

  overall_implementation_readiness: "الجاهزية العامة للتنفيذ",
  overall_risk_assessment: "التقييم العام للمخاطر",
});
const ARABIC_TERM_REPLACEMENTS = [
  [
    /\bStrategic\s+Risk(s)?\s+Affecting\s+Business\s+Model\b/gi,
    STRATEGIC_BUSINESS_MODEL_RISK_AR,
  ],
  [
    /\bStrategic\s+(المخاطر|مخاطر)\s+Affecting\s+Business\s+Model\b/gi,
    STRATEGIC_BUSINESS_MODEL_RISK_AR,
  ],
  ["Level", "المستوى"],
  ["Unit", "الوحدة"],
  ["Automation Opportunity", "فرصة الأتمتة"],
  ["Automation Scalability", "قابلية توسع الأتمتة"],
  ["Incident Management Requirements", "متطلبات إدارة الحوادث"],
  ["Control Requirements", "متطلبات الضبط"],
  ["Safety Documentation Requirements", "متطلبات توثيق السلامة"],
  ["Integration Requirements", "متطلبات التكامل"],
  ["Systems Involved", "الأنظمة المعنية"],
  ["Manual Fallback Requirements", "متطلبات الرجوع اليدوي"],
  ["Data", "البيانات"],
  ["Prerequisites", "المتطلبات المسبقة"],
  ["Opportunity", "الفرصة"],
  ["Emergency Preparedness", "الاستعداد للطوارئ"],
  ["Required Elements", "العناصر المطلوبة"],
  ["Stated Waste Rate", "معدل الهدر المعلن"],
  ["Operational Environmental Considerations", "الاعتبارات البيئية التشغيلية"],

  ["Technology Dependencies", "الاعتماديات التقنية"],
  ["Quality Implications", "آثار الجودة"],
  ["Custom System Implications", "آثار النظام المخصص"],
  ["Custom System Needed", "الحاجة إلى نظام مخصص"],
  ["Functional Areas To Define", "المجالات الوظيفية المطلوب تحديدها"],

  ["Infrastructure Related Risks", "مخاطر البنية التحتية"],
  ["Process and Quality Risks", "مخاطر العمليات والجودة"],
  ["Safety and Environment Risks", "مخاطر السلامة والبيئة"],
  ["Scalability Risks", "مخاطر قابلية التوسع"],
  ["Assumption and Information Risks", "مخاطر الافتراضات والمعلومات"],

  ["Required", "مطلوب"],
  ["Dependencies", "الاعتماديات"],
  ["Continuity", "الاستمرارية"],
  ["Implications", "الآثار"],
  ["Considerations", "الاعتبارات"],
  ["Infrastructure Related", "المرتبطة بالبنية التحتية"],
  ["Scalability", "قابلية التوسع"],
  ["Assumption", "الافتراض"],
  ["Information", "المعلومات"],
  ["Process", "العمليات"],
  ["Quality", "الجودة"],
  ["Safety", "السلامة"],
  ["Environment", "البيئة"],
  ["Trial Output", "مخرجات التشغيل التجريبي"],
  ["Infrastructure Constraints", "قيود البنية التحتية"],
  ["Stated Support Equipment", "المعدات المساندة المقدمة"],
  ["Maintenance Required", "الحاجة إلى الصيانة"],
  ["Employees Count", "عدد الموظفين"],
  ["Automation Fit", "ملاءمة الأتمتة"],
  ["Overall Implementation Readiness", "الجاهزية العامة للتنفيذ"],
  ["Overall Risk Level", "مستوى المخاطر الإجمالي"],
  ["Overall Risk Assessment", "التقييم العام للمخاطر"],
  ["Risk Register", "سجل المخاطر"],
  ["Register", "السجل"],
  ["Id:", "المعرف:"],
  ["Source:", "المصدر:"],
  ["Existing Or Implied Controls", "الضوابط القائمة أو الضمنية"],
  ["Recommended Controls", "الضوابط الموصى بها"],
  ["Overall", "الإجمالي"],
  [/\bBusiness\s+Model\b/gi, "نموذج العمل"],
  [/\bAffecting\b/gi, "المؤثرة على"],
  [/\bStrategic\b/gi, "الاستراتيجية"],
  [/\bExecutive\b/gi, "تنفيذي"],
  [/\bSummary\b/gi, "ملخص"],
  [/\bOverview\b/gi, "نظرة عامة"],
  [/\bAnalysis\b/gi, "تحليل"],
  [/\bAssessment\b/gi, "تقييم"],
  [/\bStrategy\b/gi, "استراتيجية"],
  [/\bPlan\b/gi, "خطة"],
  [/\bFramework\b/gi, "إطار"],
  [/\bRecommendation(s)?\b/gi, "توصيات"],
  [/\bMarket\b/gi, "السوق"],
  [/\bCustomer(s)?\b/gi, "العملاء"],
  [/\bCompetitor(s)?\b/gi, "المنافسين"],
  [/\bFinancial\b/gi, "مالي"],
  [/\bFinance\b/gi, "التمويل"],
  [/\bRevenue\b/gi, "الإيرادات"],
  [/\bCost(s)?\b/gi, "التكاليف"],
  [/\bProfit\b/gi, "الربح"],
  [/\bRisk(s)?\b/gi, "المخاطر"],
  [/\bOperations\b/gi, "العمليات"],
  [/\bOperational\b/gi, "تشغيلي"],
  [/\bTechnology\b/gi, "التقنية"],
  [/\bHuman Resources\b/gi, "الموارد البشرية"],
  [/\bImplementation\b/gi, "التنفيذ"],
  [/\bGovernance\b/gi, "الحوكمة"],
  [/\bReadiness\b/gi, "الجاهزية"],
  [/\bScore\b/gi, "درجة"],
  [/\bIndicator(s)?\b/gi, "مؤشرات"],
  [/\bKPI(s)?\b/gi, "مؤشرات الأداء"],
  [/\bPie Distribution\b/gi, "توزيع دائري"],
  [/\bKPI Indicators\b/gi, "مؤشرات الأداء"],
  [/\bGauge\b/gi, "مؤشر"],
  [/\bChart\b/gi, "رسم بياني"],
  [/\bFigure\b/gi, "شكل"],
  [/\bTable\b/gi, "جدول"],
  [/\band\b/gi, "و"],
  [/\bof\b/gi, "من"],
  [/\bfor\b/gi, "لـ"],
];

const arabicFallbackHeading = (key) => {
  const normalized = String(key).toLowerCase().trim();
  if (ARABIC_HEADING_OVERRIDES[normalized]) {
    return ARABIC_HEADING_OVERRIDES[normalized];
  }

  const translated = prettifyKey(key)
    .replace(/\bAnd\b/g, "و")
    .replace(/\bAnalysis\b/g, "تحليل")
    .replace(/\bAssessment\b/g, "تقييم")
    .replace(/\bPlan\b/g, "خطة")
    .replace(/\bStrategy\b/g, "استراتيجية")
    .replace(/\bOverview\b/g, "نظرة عامة")
    .replace(/\bSummary\b/g, "ملخص")
    .replace(/\bRecommendation\b/g, "توصية")
    .replace(/\bRecommendations\b/g, "توصيات")
    .replace(/\bFinancial\b/g, "مالي")
    .replace(/\bMarket\b/g, "السوق")
    .replace(/\bCustomer\b/g, "العملاء")
    .replace(/\bCompetitor\b/g, "المنافسين")
    .replace(/\bRisk\b/g, "المخاطر")
    .replace(/\bRisks\b/g, "المخاطر")
    .replace(/\bOperations\b/g, "العمليات")
    .replace(/\bRevenue\b/g, "الإيرادات")
    .replace(/\bCosts\b/g, "التكاليف")
    .replace(/\bCash Flow\b/g, "التدفقات النقدية")
    .trim();
  return translated || prettifyKey(key);
};

const translateKey = (key, lang) => {
  const normalized = String(key).toLowerCase().trim();
  if (isStrategicBusinessModelRiskHeading(normalized)) {
    return lang === "ar" || containsArabic(key)
      ? STRATEGIC_BUSINESS_MODEL_RISK_AR
      : STRATEGIC_BUSINESS_MODEL_RISK_EN;
  }
  const entry = HEADING_DICTIONARY[normalized];

  if (entry && entry[lang]) {
    return entry[lang];
  }

  if (lang === "ar") {
    return arabicFallbackHeading(normalized);
  }

  return prettifyKey(key);
};

const arabicizeLabel = (value, lang, fallbackKey = "metric") => {
  const text = String(value ?? "").trim();
  if (lang === "ar") {
    const exactInlineMap = {
      Level: "المستوى",
      Unit: "الوحدة",
      "Trial Output": "مخرجات التشغيل التجريبي",
      "Required Area": "المساحة المطلوبة",
      "Permit Required": "الحاجة إلى تصريح",
      "Stated Permits": "التصاريح المقدمة",
      "Infrastructure Constraints": "قيود البنية التحتية",
      "Stated Support Equipment": "المعدات المساندة المقدمة",
      "Maintenance Required": "الحاجة إلى الصيانة",
      "Employees Count": "عدد الموظفين",
      "Stated Automation Level": "مستوى الأتمتة المقدم",
      "Automation Fit": "ملاءمة الأتمتة",
      "Overall Risk Level": "مستوى المخاطر الإجمالي",
      "Overall Risk Assessment": "تقييم المخاطر الإجمالي",
    };

    if (exactInlineMap[text]) {
      return exactInlineMap[text];
    }
  }
  if (lang !== "ar" || !text) return text;

  const normalized = text
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[_/-]+/g, " ")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "");

  // 1. Exact Arabic heading dictionary
  if (ARABIC_HEADING_OVERRIDES[normalized]) {
    return ARABIC_HEADING_OVERRIDES[normalized];
  }

  if (HEADING_DICTIONARY[normalized]?.ar) {
    return HEADING_DICTIONARY[normalized].ar;
  }

  // 2. Already completely Arabic
  if (containsArabic(text) && !LATIN_REGEX.test(text)) {
    return text;
  }

  // 3. Mixed Arabic / English or pure English:
  // try translating the whole heading key first
  const translatedKey = translateKey(normalized, "ar");

  if (
    translatedKey &&
    containsArabic(translatedKey) &&
    !LATIN_REGEX.test(translatedKey)
  ) {
    return translatedKey;
  }

  // 4. Final word-by-word fallback
  let translated = prettifyKey(text);

  ARABIC_TERM_REPLACEMENTS.forEach(([pattern, replacement]) => {
    translated = translated.replace(pattern, replacement);
  });

  translated = translated.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

  // Never allow a mixed heading in Arabic report
  if (LATIN_REGEX.test(translated)) {
    const directKey = String(text)
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[_/-]+/g, "_")
      .replace(/\s+/g, "_")
      .replace(/^_+|_+$/g, "");

    if (ARABIC_HEADING_OVERRIDES[directKey]) {
      return ARABIC_HEADING_OVERRIDES[directKey];
    }

    if (HEADING_DICTIONARY[directKey]?.ar) {
      return HEADING_DICTIONARY[directKey].ar;
    }

    // لا تحول أي عنوان مجهول إلى "المؤشر"
    return translated;
  }

  return translated;
};

const arabicizeCaptionTitle = (title, lang) =>
  arabicizeLabel(title, lang, "metric");

// Keys that are purely structural/meta and must never be rendered
// as content, anywhere in the document.
const META_KEYS = new Set([
  "title",
  "section_title",
  "stage_reference",

  // References are collected once and rendered only
  // on the final References page.
  "references",
  "reference",
  "sources",
  "source",
  "citations",
  "citation",
  "source_registry",
  "sources_registry",
  "references_registry",
]);
const FULL_REPORT_SPECIAL_MODULES = new Set([
  "executive_summary",
  "investment_recommendation",
  "recommendations",
  "references",
  "appendix",
]);

// =========================================================
// LOW-LEVEL DOCX HELPERS
// =========================================================
const align = (lang) =>
  lang === "ar" ? AlignmentType.LEFT : AlignmentType.LEFT;

const RTL_MARK = "\u200F";
const normalizeArabicInlineLabels = (text, lang) => {
  if (lang !== "ar") return String(text ?? "");

  let value = String(text ?? "");

  const replacements = [
    ["Dependency:", "الاعتمادية:"],
    ["Operational Relevance:", "الأهمية التشغيلية:"],
    ["Operational Dependency:", "الاعتمادية التشغيلية:"],
    ["Upstream Effect:", "الأثر السابق:"],
    ["Downstream Effect:", "الأثر اللاحق:"],
    ["Control Required:", "الضبط المطلوب:"],
    ["Recommended Control:", "إجراء الضبط الموصى به:"],

    ["Stage Or Factor:", "المرحلة أو العامل:"],
    ["Stage Or Activity:", "المرحلة أو النشاط:"],
    ["Capacity Impact:", "الأثر على الطاقة:"],
    ["Operational Effect:", "الأثر التشغيلي:"],
    ["Operational Impact:", "الأثر التشغيلي:"],
    ["Process Impact:", "الأثر على العملية:"],
    ["Potential Impact:", "الأثر المحتمل:"],
    ["Capacity Decision Impact:", "الأثر على قرار الطاقة:"],

    ["Control Point:", "نقطة الضبط:"],
    ["Purpose:", "الغرض:"],
    ["Required Evidence Or Check:", "الدليل أو الفحص المطلوب:"],

    ["Measurement Stage:", "مرحلة القياس:"],
    ["Validation Required:", "التحقق المطلوب:"],
    ["Recommended Validation:", "التحقق الموصى به:"],
    ["Validation Method:", "طريقة التحقق:"],

    ["Role:", "الدور:"],
    ["Operational Role:", "الدور التشغيلي:"],
    ["Continuity Risks:", "مخاطر الاستمرارية:"],
    ["Continuity Risk:", "مخاطر الاستمرارية:"],

    ["Level:", "المستوى:"],
    ["Status:", "الحالة:"],
    ["Rationale:", "المبررات:"],
    ["Assessment:", "التقييم:"],
    ["Clarity:", "الوضوح:"],
    ["Clarity Level:", "مستوى الوضوح:"],
    ["Readiness Level:", "مستوى الجاهزية:"],

    ["Unit:", "الوحدة:"],
    ["Value:", "القيمة:"],

    ["Trial Output:", "مخرجات التشغيل التجريبي:"],
    ["Required Area:", "المساحة المطلوبة:"],

    ["Permit Required:", "الحاجة إلى تصريح:"],
    ["Stated Permits:", "التصاريح المقدمة:"],

    ["Maintenance Required:", "الحاجة إلى الصيانة:"],
    ["Stated Cycle:", "دورة الصيانة المقدمة:"],

    ["Support Equipment Required:", "الحاجة إلى معدات مساندة:"],
    ["Stated Support Equipment:", "المعدات المساندة المقدمة:"],

    ["Employees Count:", "عدد الموظفين:"],
    ["Stated Stage Count:", "عدد المراحل المقدم:"],
    ["Stage Consistency:", "اتساق المراحل:"],

    ["Stated Procedures:", "الإجراءات المقدمة:"],

    ["Infrastructure Constraints", "قيود البنية التحتية"],
    ["Operational Risks:", "المخاطر التشغيلية:"],
    ["Technical Risks:", "المخاطر الفنية:"],

    ["Missing Information:", "المعلومات الناقصة:"],
    ["Why It Matters:", "أهمية المعلومة:"],
    ["Recommended Action:", "الإجراء الموصى به:"],

    ["Scalability Status:", "حالة قابلية التوسع:"],

    ["Technology:", "التقنية:"],
    ["Priority:", "الأولوية:"],

    ["Process Area:", "مجال العملية:"],
    ["Potential Automation Role:", "دور الأتمتة المحتمل:"],
    ["Expected Operational Benefit:", "الفائدة التشغيلية المتوقعة:"],

    ["Role Or Function:", "الدور أو الوظيفة:"],
    ["Core Responsibilities:", "المسؤوليات الأساسية:"],
    ["Process Dependency:", "الارتباط بالعملية:"],

    ["Role Or Skill:", "الدور أو المهارة:"],
    ["Why Critical:", "سبب الأهمية:"],
    ["Operational Impact If Unavailable:", "الأثر التشغيلي عند عدم التوفر:"],
    ["Continuity Requirement:", "متطلب الاستمرارية:"],

    ["Training Area:", "مجال التدريب:"],
    ["Target Role Or Group:", "الدور أو الفئة المستهدفة:"],
    ["Operational Reason:", "المبرر التشغيلي:"],

    ["Skill Area:", "مجال المهارة:"],
    ["Related Equipment Or System:", "المعدة أو النظام المرتبط:"],
    ["Required Capability:", "القدرة المطلوبة:"],

    ["Area:", "المجال:"],
    ["Potential Workforce Effect:", "الأثر المحتمل على القوى العاملة:"],
    [
      "Reskilling Or Role Change Required:",
      "متطلبات إعادة التأهيل أو تعديل الدور:",
    ],

    ["Process Stage:", "مرحلة العملية:"],
    ["Quality Control Requirement:", "متطلب ضبط الجودة:"],
    ["Reason:", "السبب:"],
    ["Evidence Or Record Required:", "الدليل أو السجل المطلوب:"],

    ["Quality Metric Or Check:", "مؤشر أو فحص الجودة:"],
    ["Measurement Method To Define:", "طريقة القياس المطلوب تحديدها:"],

    ["Equipment Or Function:", "المعدة أو الوظيفة:"],
    ["Failure Impact:", "أثر التعطل:"],

    ["Integration Area:", "مجال التكامل:"],

    ["Factor:", "العامل:"],
    ["Site Impact:", "الأثر على الموقع:"],
    ["Constraint:", "القيد:"],

    ["Stated Equipment:", "المعدات المقدمة:"],
    ["Stated Technologies:", "التقنيات المقدمة:"],
    ["Stated Standards:", "المعايير المقدمة:"],
    ["Stated Requirements:", "المتطلبات المقدمة:"],
    ["Stated Quantity:", "الكمية المقدمة:"],
    ["Requirement Status:", "حالة المتطلب:"],
    ["Stated Need:", "الاحتياج المقدم:"],
    ["Stated Useful Life:", "العمر التشغيلي المقدم:"],

    ["Operational Considerations:", "الاعتبارات التشغيلية:"],
    ["Operational Implications:", "الآثار التشغيلية:"],
    ["Operational Use Cases:", "حالات الاستخدام التشغيلية:"],

    ["Critical Dependencies:", "الاعتماديات الحرجة:"],
    ["Critical Equipment Dependencies:", "اعتماديات المعدات الحرجة:"],
    ["External Party:", "الطرف الخارجي:"],
    ["Dependency Level:", "مستوى الاعتماد:"],

    ["Handoff:", "نقطة التسليم:"],
    ["Parties Involved:", "الأطراف المعنية:"],

    ["Bottleneck:", "نقطة الاختناق:"],
    ["Cause:", "السبب:"],

    ["Storage Risks:", "مخاطر التخزين:"],

    ["Operational Safety:", "السلامة التشغيلية:"],
  ];

  for (const [english, arabic] of replacements) {
    value = value.split(english).join(arabic);
  }

  return value;
};
const withDirectionMark = (text, lang) => {
  let value = normalizeArabicInlineLabels(
    normalizeStrategicBusinessModelRiskText(text, lang),
    lang,
  );
  if (lang === "ar") {
    value = value.replace(/الطزاجة/g, "الطازجة");
  }
  return lang === "ar" && value ? `${RTL_MARK}${value}` : value;
};

const HEADING_LEVELS = [
  HeadingLevel.HEADING_1,
  HeadingLevel.HEADING_2,
  HeadingLevel.HEADING_3,
  HeadingLevel.HEADING_4,
  HeadingLevel.HEADING_5,
  HeadingLevel.HEADING_6,
];
const headingForLevel = (level) =>
  HEADING_LEVELS[Math.min(level, HEADING_LEVELS.length - 1)];

const emptyLine = (size = 120) =>
  new Paragraph({ text: "", spacing: { after: size } });

const rtlRunProps = (lang) =>
  lang === "ar"
    ? {
        rightToLeft: true,
        font: { ascii: THEME.fontArabic, cs: THEME.fontArabic },
      }
    : {};
const professionalTextRun = (props, lang) => {
  const originalText =
    props?.text !== undefined && props?.text !== null ? String(props.text) : "";

  let rawText = normalizeStrategicBusinessModelRiskText(originalText, lang);

  // أي تقرير عربي: نظّف الكلمات والعناوين الإنجليزية المولدة من النظام
  if (lang === "ar") {
    rawText = normalizeArabicInlineLabels(rawText, lang);
  }

  const useArabicDirection = lang === "ar" || containsArabic(rawText);

  return new TextRun({
    ...(useArabicDirection ? rtlRunProps("ar") : {}),
    ...props,

    text:
      props.text !== undefined
        ? useArabicDirection
          ? withDirectionMark(rawText, "ar")
          : rawText
        : props.text,
  });
};
const mixedDirectionRuns = (text, lang, baseProps = {}) => {
  let str = String(text ?? "");

  if (lang === "ar") {
    str = normalizeArabicInlineLabels(str, lang);

    ARABIC_TERM_REPLACEMENTS.forEach(([pattern, replacement]) => {
      str = str.replace(pattern, replacement);
    });
  }

  if (lang !== "ar") {
    return [professionalTextRun({ text: str, ...baseProps }, lang)];
  }

  // مهم: نظّف النص كاملًا قبل تقسيم العربي والإنجليزي
  str = normalizeArabicInlineLabels(str, lang);

  ARABIC_TERM_REPLACEMENTS.forEach(([pattern, replacement]) => {
    str = str.replace(pattern, replacement);
  });

  const parts = str.split(/([A-Za-z][A-Za-z0-9._&'’/-]*)/g);

  return parts
    .filter((part) => part !== "")
    .map((part) => {
      const isLatin = LATIN_REGEX.test(part) && !containsArabic(part);

      return new TextRun({
        ...(isLatin
          ? {
              font: {
                ascii: THEME.fontBody,
                cs: THEME.fontBody,
              },
            }
          : rtlRunProps("ar")),
        ...baseProps,
        text: isLatin ? part : withDirectionMark(part, "ar"),
      });
    });
};
const forceArabicTitle = (candidate, fallbackKey, lang) => {
  if (lang !== "ar") return candidate || translateKey(fallbackKey, lang);
  const text = String(candidate || "").trim();
  if (!text) return translateKey(fallbackKey, lang);
  if (containsArabic(text) && !LATIN_REGEX.test(text)) return text;
  return arabicizeLabel(text, lang, fallbackKey);
};

const sectionHeading = (text, level, lang, opts = {}) => {
  if (lang === "ar") {
    text = arabicizeLabel(text, lang, "metric");
  }
  if (
    lang === "ar" &&
    /Strategic\s+(?:Risks?|المخاطر|مخاطر)\s+Affecting\s+(?:the\s+)?Business\s+Model/i.test(
      String(text),
    )
  ) {
    text = "المخاطر الاستراتيجية المؤثرة على نموذج العمل";
  }
  const headingStyle =
    level === 0
      ? { size: 30, color: THEME.primary }
      : level === 1
        ? { size: 26, color: THEME.primary }
        : { size: 22, color: THEME.secondary };

  return new Paragraph({
    ...opts,

    heading: opts.includeInToc === false ? undefined : headingForLevel(level),
    bidirectional: lang === "ar",
    alignment: align(lang),

    keepNext: true,
    keepLines: true,
    spacing: {
      before: level === 0 ? 460 : level === 1 ? 340 : 260,
      after: level === 0 ? 200 : level === 1 ? 170 : 120,
      ...(opts.spacing || {}),
    },

    border:
      level === 0
        ? {
            bottom: {
              style: BorderStyle.SINGLE,
              size: 10,
              color: THEME.accent,
              space: 8,
            },
          }
        : level === 1
          ? {
              bottom: {
                style: BorderStyle.SINGLE,
                size: 2,
                color: THEME.boxBorder,
                space: 5,
              },
            }
          : undefined,

    children: [
      professionalTextRun(
        {
          text,
          bold: true,
          size: headingStyle.size,
          color: headingStyle.color,
        },
        lang,
      ),
    ],
  });
};
const majorSectionDivider = (title, lang) =>
  new Table({
    width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: [CONTENT_WIDTH_DXA],
    visuallyRightToLeft: lang === "ar",
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: "D7DEE8" },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: "D7DEE8" },
      left: { style: BorderStyle.SINGLE, size: 4, color: "D7DEE8" },
      right: { style: BorderStyle.SINGLE, size: 4, color: "D7DEE8" },
      insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    },
    rows: [
      new TableRow({
        cantSplit: true,
        children: [
          new TableCell({
            width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
            shading: {
              type: ShadingType.CLEAR,
              fill: "EEF2F8",
            },
            margins: {
              top: 420,
              bottom: 420,
              left: 320,
              right: 320,
            },
            children: [
              new Paragraph({
                heading: HeadingLevel.HEADING_1,
                bidirectional: lang === "ar",
                alignment: align(lang),
                keepNext: true,
                spacing: { before: 40, after: 40 },
                children: [
                  professionalTextRun(
                    {
                      text: title,
                      bold: true,
                      size: 32,
                      color: THEME.primary,
                    },
                    lang,
                  ),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
// Emphasises figures (currency, %, years, counts) inline so financial
// narrative reads like a consulting document rather than flat prose.
// Matches: 1,234.56 | 26–35 | 44 | 12% | -66.24% | USD 100,000 | 3–5
const NUMBER_RUN_REGEX =
  /(-?\d[\d,]*\.?\d*\s*%|USD\s?\d[\d,]*\.?\d*|\d[\d,]*\.?\d*\s?(?:USD|SAR|EUR)|\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+\.\d+|\d+–\d+|\d+-\d+|\b\d+\b)/g;

const runsWithHighlightedNumbers = (text, lang, baseProps = {}) => {
  const str = String(text);
  const parts = str.split(NUMBER_RUN_REGEX);
  const runs = [];

  parts.forEach((part) => {
    if (part === undefined || part === "") return;

    const isNumber = NUMBER_RUN_REGEX.test(part) && /\d/.test(part);
    NUMBER_RUN_REGEX.lastIndex = 0;

    if (isNumber) {
      runs.push(
        new TextRun({
          text: lang === "ar" ? `\u200E${part.trim()}\u200E` : part,
          bold: true,
          color: THEME.secondary,
          font: THEME.fontBody,
          ...baseProps,
        }),
      );
    } else {
      runs.push(...mixedDirectionRuns(part, lang, baseProps));
    }
  });

  return runs.length
    ? runs
    : [professionalTextRun({ text: str, ...baseProps }, lang)];
};

const bodyParagraph = (text, lang, opts = {}) =>
  new Paragraph({
    ...opts,
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: {
      after: 160,
      line: 300,
      ...(opts.spacing || {}),
    },
    children: runsWithHighlightedNumbers(text, lang),
  });

const keyValueParagraph = (label, value, lang) =>
  new Paragraph({
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: { after: 120, line: 300 },
    children: [
      professionalTextRun(
        { text: `${label}: `, bold: true, color: THEME.primary },
        lang,
      ),
      ...runsWithHighlightedNumbers(value, lang),
    ],
  });

const bulletParagraph = (value, lang, level = 0) => {
  const text =
    typeof value === "object" ? JSON.stringify(value) : String(value);

  return new Paragraph({
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: { after: 100, line: 290 },
    numbering: { reference: "premium-bullets", level },
    children: runsWithHighlightedNumbers(text, lang),
  });
};

const numberedParagraph = (
  value,
  lang,
  instance = 0,
  reference = "premium-numbers",
) =>
  new Paragraph({
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: { after: 100, line: 290 },
    numbering: {
      reference,
      level: 0,
      instance,
    },
    children: runsWithHighlightedNumbers(value, lang),
  });

const pageBreak = () => new Paragraph({ children: [new PageBreak()] });

const appendPageBreakIfNeeded = (nodes) => {
  if (nodes.length) nodes.push(pageBreak());
};

const appendNodesWithBreak = (target, nodes) => {
  if (!nodes.length) return;
  target.push(...nodes);
  target.push(pageBreak());
};

// Thin decorative divider between major blocks
const divider = () =>
  new Paragraph({
    spacing: { before: 80, after: 200 },
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 4, color: THEME.accent },
    },
    children: [],
  });

const captionParagraph = (kind, title, lang) =>
  new Paragraph({
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: { before: 80, after: 80 },
    shading: { type: ShadingType.CLEAR, fill: THEME.tableCaptionBg },
    children: [
      professionalTextRun(
        {
          text: `${t(kind === "figure" ? "figureLabel" : "tableLabel", lang)}: ${arabicizeCaptionTitle(title, lang)}`,
          bold: true,
          color: THEME.primary,
          size: 19,
        },
        lang,
      ),
    ],
  });

// =========================================================
// TEXT INTELLIGENCE — splitting narrative prose into
// professional structure (numbered points, sentence groups)
// =========================================================

// Many AI-generated narrative fields pack several distinct points into
// one long string using inline markers like "1) ... 2) ... 3) ...".
// Detect and split them into real list items instead of one wall of text.
const INLINE_NUMBERING_REGEX = /(?:^|\s)(\d{1,2})\)\s/g;

const splitInlineNumberedText = (text) => {
  const str = String(text);
  const matches = [...str.matchAll(INLINE_NUMBERING_REGEX)];
  if (matches.length < 2) return null; // not a numbered-list pattern

  const items = [];
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].index + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : str.length;
    const chunk = str.slice(start, end).trim();
    if (chunk) items.push(chunk);
  }
  // Anything before the first marker is a lead-in sentence worth keeping
  const lead = str.slice(0, matches[0].index).trim();
  return { lead: lead || null, items };
};

// Splits a long narrative into paragraph-sized chunks on sentence
// boundaries so a 1,500-character block doesn't render as one
// unbroken paragraph. Groups ~2-3 sentences per paragraph.
const splitIntoParagraphs = (text, sentencesPerPara = 3) => {
  const str = String(text).trim();
  if (!str) return [];
  // Split on Arabic/Latin sentence terminators, keep delimiter.
  const sentences = str.match(/[^.!؟?]+[.!؟?]+|[^.!؟?]+$/g) || [str];
  const paras = [];
  for (let i = 0; i < sentences.length; i += sentencesPerPara) {
    const group = sentences
      .slice(i, i + sentencesPerPara)
      .join(" ")
      .trim();
    if (group) paras.push(group);
  }
  return paras.length ? paras : [str];
};

const uniqueTextItems = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item).replace(/\s+/g, " ").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const isPngBytes = (bytes) =>
  bytes?.[0] === 0x89 &&
  bytes?.[1] === 0x50 &&
  bytes?.[2] === 0x4e &&
  bytes?.[3] === 0x47;

const WATERMARK_OPACITY = 0.14;

const applyPngOpacity = async (bytes, opacity) => {
  if (typeof document === "undefined") return bytes;

  try {
    const bitmap = await createImageBitmap(
      new Blob([bytes], { type: "image/png" }),
    );
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = opacity;
    ctx.drawImage(bitmap, 0, 0);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    if (!blob) return bytes;

    return new Uint8Array(await blob.arrayBuffer());
  } catch (error) {
    console.warn("Failed applying watermark opacity:", error);
    return bytes;
  }
};

const loadImageBytes = async (description) => {
  const publicBase = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const candidates = [
    `${publicBase}/img/jr-watermark.png`,
    `${publicBase}/jr-watermark.png`,
    "/img/jr-watermark.png",
    "/jr-watermark.png",
  ].filter((path, index, arr) => path && arr.indexOf(path) === index);

  for (const path of candidates) {
    try {
      const response = await fetch(path, { cache: "no-store" });
      if (!response.ok) continue;

      const bytes = new Uint8Array(await response.arrayBuffer());
      if (isPngBytes(bytes)) {
        console.log(`Loaded ${description} image:`, path, bytes.byteLength);
        return bytes;
      }

      console.warn(`Skipped non-PNG ${description} response:`, path);
    } catch (error) {
      console.warn(`Failed loading ${description} image:`, path, error);
    }
  }

  throw new Error(`Unable to load ${description} PNG image`);
};

const loadLogoBytes = async () => {
  return loadImageBytes("company logo");

  /* const response = await fetch("/jr-watermark.png");

  if (!response.ok) {
    throw new Error("Unable to load company logo");
  }

  const blob = await response.blob();

  return new Uint8Array(await blob.arrayBuffer()); */
};
const loadWatermarkBytes = async () => {
  return applyPngOpacity(await loadImageBytes("watermark"), WATERMARK_OPACITY);

  /* const response = await fetch("/jr-watermark.png");

  if (!response.ok) {
    throw new Error("Unable to load watermark image");
  }

  const arrayBuffer = await response.arrayBuffer();

  console.log("🟢 WATERMARK BYTES:", arrayBuffer.byteLength);

  return new Uint8Array(arrayBuffer); */
};
// Renders a long narrative field intelligently: if it contains an
// inline numbered list, render lead-in + numbered items; otherwise
// break it into readable multi-sentence paragraphs.
let numberingInstanceCounter = 1;
const renderNarrative = (text, lang, nodes) => {
  const split = splitInlineNumberedText(text);

  if (split) {
    if (split.lead) {
      nodes.push(bodyParagraph(split.lead, lang));
    }

    const currentInstance = numberingInstanceCounter++;

    uniqueTextItems(split.items).forEach((item) =>
      nodes.push(numberedParagraph(item, lang, currentInstance)),
    );

    return;
  }

  uniqueTextItems(splitIntoParagraphs(text)).forEach((para) =>
    nodes.push(bodyParagraph(para, lang)),
  );
};

// =========================================================
// CALLOUT BOXES / KPI BANDS
// =========================================================
const comparisonCards = (items, lang) => {
  const cleanItems = (items || [])
    .filter(
      (item) =>
        item &&
        item.title &&
        item.value !== null &&
        item.value !== undefined &&
        String(item.value).trim(),
    )
    .slice(0, 4);

  if (cleanItems.length < 2) return null;

  const cells = cleanItems.map(
    (item) =>
      new TableCell({
        shading: {
          type: ShadingType.CLEAR,
          fill: "F8FAFD",
        },

        borders: {
          top: {
            style: BorderStyle.SINGLE,
            size: 8,
            color: THEME.boxBorder,
          },
          bottom: {
            style: BorderStyle.SINGLE,
            size: 8,
            color: THEME.boxBorder,
          },
          left: {
            style: BorderStyle.SINGLE,
            size: 8,
            color: THEME.boxBorder,
          },
          right: {
            style: BorderStyle.SINGLE,
            size: 8,
            color: THEME.boxBorder,
          },
        },

        margins: {
          top: 170,
          bottom: 170,
          left: 150,
          right: 150,
        },

        children: [
          new Paragraph({
            bidirectional: lang === "ar",
            alignment: AlignmentType.CENTER,
            spacing: { after: 80 },

            children: [
              new TextRun({
                text: String(item.title),
                bold: true,
                size: 19,
                color: THEME.secondary,
                font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                rightToLeft: lang === "ar",
              }),
            ],
          }),

          new Paragraph({
            bidirectional: lang === "ar",
            alignment: AlignmentType.CENTER,

            children: [
              new TextRun({
                text: String(item.value),
                bold: true,
                size: 22,
                color: THEME.primary,
                font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                rightToLeft: lang === "ar",
              }),
            ],
          }),
        ],
      }),
  );

  return new Table({
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },

    rows: [
      new TableRow({
        children: cells,
      }),
    ],
  });
};
const summaryCards = (items, lang) => {
  const cleanItems = (items || [])
    .filter((item) => item && item.title && item.value)
    .slice(0, 3);

  if (cleanItems.length < 2) {
    return null;
  }

  const cells = cleanItems.map(
    (item) =>
      new TableCell({
        shading: {
          type: ShadingType.CLEAR,
          fill: "F4F7FB",
        },
        margins: {
          top: 180,
          bottom: 180,
          left: 160,
          right: 160,
        },
        children: [
          new Paragraph({
            bidirectional: lang === "ar",
            alignment: AlignmentType.CENTER,
            spacing: { after: 80 },
            children: [
              new TextRun({
                text: String(item.title),
                bold: true,
                size: 20,
                color: THEME.secondary,
                font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                rightToLeft: lang === "ar",
              }),
            ],
          }),

          new Paragraph({
            bidirectional: lang === "ar",
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: String(item.value),
                bold: true,
                size: 24,
                color: THEME.primary,
                font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                rightToLeft: lang === "ar",
              }),
            ],
          }),
        ],
      }),
  );

  return new Table({
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    rows: [
      new TableRow({
        children: cells,
      }),
    ],
  });
};
const decisionSummaryBox = (title, decision, reasons, lang) => {
  if (!decision) return null;

  const cleanReasons = Array.isArray(reasons)
    ? reasons.filter(Boolean).slice(0, 3)
    : [];

  const children = [
    new Paragraph({
      bidirectional: lang === "ar",
      alignment: align(lang),
      spacing: { after: 100 },
      children: [
        new TextRun({
          text: title,
          bold: true,
          size: 24,
          color: THEME.primary,
          font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
          rightToLeft: lang === "ar",
        }),
      ],
    }),

    new Paragraph({
      bidirectional: lang === "ar",
      alignment: align(lang),
      spacing: { after: 120 },
      children: [
        new TextRun({
          text: String(decision),
          bold: true,
          size: 28,
          color: THEME.secondary,
          font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
          rightToLeft: lang === "ar",
        }),
      ],
    }),
  ];

  cleanReasons.forEach((reason) => {
    children.push(bulletParagraph(String(reason), lang));
  });

  return new Table({
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    borders: {
      top: {
        style: BorderStyle.SINGLE,
        size: 12,
        color: THEME.primary,
      },
      bottom: {
        style: BorderStyle.SINGLE,
        size: 12,
        color: THEME.primary,
      },
      left: {
        style: BorderStyle.SINGLE,
        size: 12,
        color: THEME.primary,
      },
      right: {
        style: BorderStyle.SINGLE,
        size: 12,
        color: THEME.primary,
      },
      insideHorizontal: {
        style: BorderStyle.NONE,
        size: 0,
        color: "FFFFFF",
      },
      insideVertical: {
        style: BorderStyle.NONE,
        size: 0,
        color: "FFFFFF",
      },
    },

    rows: [
      new TableRow({
        children: [
          new TableCell({
            shading: {
              type: ShadingType.CLEAR,
              fill: "F4F7FB",
            },
            margins: {
              top: 220,
              bottom: 220,
              left: 240,
              right: 240,
            },
            children,
          }),
        ],
      }),
    ],
  });
};
// A titled, tinted callout box (used for Key Findings / KPI / Recs)
const highlightBox = (
  title,
  lines,
  lang,
  fill = THEME.boxBg,
  border = THEME.boxBorder,
) =>
  new Table({
    width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: [CONTENT_WIDTH_DXA],

    visuallyRightToLeft: lang === "ar",

    borders: {
      top: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: border,
      },

      bottom: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: border,
      },

      left: {
        style: BorderStyle.SINGLE,
        size: lang === "ar" ? 2 : 14,
        color: border,
      },

      right: {
        style: BorderStyle.SINGLE,
        size: lang === "ar" ? 14 : 2,
        color: border,
      },

      insideHorizontal: {
        style: BorderStyle.NONE,
      },

      insideVertical: {
        style: BorderStyle.NONE,
      },
    },

    rows: [
      new TableRow({
        cantSplit: true,
        children: [
          new TableCell({
            width: {
              size: CONTENT_WIDTH_DXA,
              type: WidthType.DXA,
            },

            shading: {
              type: ShadingType.CLEAR,
              fill,
            },

            margins: {
              top: 240,
              bottom: 230,
              left: 300,
              right: 300,
            },

            children: [
              // ---------- Box title ----------
              new Paragraph({
                bidirectional: lang === "ar",
                alignment: align(lang),

                spacing: {
                  after: 160,
                },

                children: [
                  professionalTextRun(
                    {
                      text: title,
                      bold: true,
                      color: border,
                      size: 25,
                    },
                    lang,
                  ),
                ],
              }),

              // ---------- Box content ----------
              ...lines.map(
                (line) =>
                  new Paragraph({
                    bidirectional: lang === "ar",
                    alignment: align(lang),

                    numbering: {
                      reference: "premium-bullets",
                      level: 0,
                    },

                    spacing: {
                      after: 110,
                      line: 290,
                    },

                    children: runsWithHighlightedNumbers(line, lang),
                  }),
              ),
            ],
          }),
        ],
      }),
    ],
  });
const decisionBox = (title, decision, justification, lang) =>
  new Table({
    width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: [CONTENT_WIDTH_DXA],
    visuallyRightToLeft: lang === "ar",

    borders: {
      top: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.primary,
      },
      bottom: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.primary,
      },
      left: {
        style: BorderStyle.SINGLE,
        size: lang === "ar" ? 2 : 16,
        color: THEME.primary,
      },
      right: {
        style: BorderStyle.SINGLE,
        size: lang === "ar" ? 16 : 2,
        color: THEME.primary,
      },
      insideHorizontal: {
        style: BorderStyle.NONE,
      },
      insideVertical: {
        style: BorderStyle.NONE,
      },
    },

    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: {
              size: CONTENT_WIDTH_DXA,
              type: WidthType.DXA,
            },

            shading: {
              type: ShadingType.CLEAR,
              fill: "EAF0F8",
            },

            margins: {
              top: 260,
              bottom: 260,
              left: 320,
              right: 320,
            },

            children: [
              new Paragraph({
                bidirectional: lang === "ar",
                alignment: align(lang),
                spacing: { after: 100 },
                children: [
                  professionalTextRun(
                    {
                      text: title,
                      bold: true,
                      color: THEME.primary,
                      size: 23,
                    },
                    lang,
                  ),
                ],
              }),

              new Paragraph({
                bidirectional: lang === "ar",
                alignment: align(lang),
                spacing: { after: 160 },
                children: [
                  professionalTextRun(
                    {
                      text: decision,
                      bold: true,
                      color: THEME.primary,
                      size: 30,
                    },
                    lang,
                  ),
                ],
              }),

              ...(justification
                ? [
                    new Paragraph({
                      bidirectional: lang === "ar",
                      alignment: align(lang),
                      spacing: {
                        after: 60,
                        line: 290,
                      },
                      children: runsWithHighlightedNumbers(justification, lang),
                    }),
                  ]
                : []),
            ],
          }),
        ],
      }),
    ],
  });

// Dark "consulting deck" style KPI band — one row of stat cells
const kpiBand = (stats, lang) => {
  const colWidth = Math.floor(CONTENT_WIDTH_DXA / stats.length);
  return new Table({
    width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: stats.map(() => colWidth),
    visuallyRightToLeft: lang === "ar",
    borders: {
      top: { style: BorderStyle.NONE },
      bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE },
      right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: { style: BorderStyle.SINGLE, size: 4, color: "FFFFFF" },
    },
    rows: [
      new TableRow({
        children: stats.map(
          ({ label, value }) =>
            new TableCell({
              width: { size: colWidth, type: WidthType.DXA },
              shading: { type: ShadingType.CLEAR, fill: THEME.kpiBg },
              verticalAlign: VerticalAlign.CENTER,
              margins: { top: 220, bottom: 220, left: 120, right: 120 },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 60 },
                  children: [
                    professionalTextRun(
                      {
                        text: String(value),
                        bold: true,
                        size: 30,
                        color: "FFFFFF",
                      },
                      lang,
                    ),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  bidirectional: containsArabic(label),
                  children: [
                    professionalTextRun(
                      {
                        text: arabicizeLabel(label, lang),
                        size: 18,
                        color: "D9E2F3",
                      },
                      lang,
                    ),
                  ],
                }),
              ],
            }),
        ),
      }),
    ],
  });
};

// =========================================================
// TABLES
// =========================================================

// Professional data table with shaded header row and zebra striping.
// Column widths are explicit DXA (docx-js requirement — percentage
// widths render unreliably in Google Docs / some Word versions).
let WORD_RENDER_IS_TECHNICAL = false;
let WORD_RENDER_TABLE_COUNT = 0;

const MAX_TECHNICAL_TABLES = 0;

const technicalTableAsText = (headerCells, rows, lang) => {
  console.log("🔥 TECHNICAL TABLE AS TEXT NEW VERSION ACTIVE");
  const cleanLabel = (value) => {
    let text = String(value ?? "");

    if (lang === "ar") {
      text = normalizeArabicInlineLabels(text, lang);
      text = arabicizeLabel(text, lang, "metric");
    }

    return text;
  };

  const cellToText = (cell) => {
    if (cell === null || cell === undefined) return "";

    if (Array.isArray(cell)) {
      return cell
        .map(cellToText)
        .filter(Boolean)
        .join(lang === "ar" ? "، " : ", ");
    }

    if (typeof cell === "object") {
      return Object.entries(cell)
        .map(([key, value]) => {
          const safeKey =
            lang === "ar"
              ? arabicizeLabel(prettifyKey(key), lang, "metric")
              : translateKey(key, lang);

          return `${safeKey}: ${cellToText(value)}`;
        })
        .join("؛ ");
    }

    let text = String(cell);

    if (lang === "ar") {
      text = normalizeArabicInlineLabels(text, lang);
    }

    return text;
  };

  const cleanedHeaders = (headerCells || []).map(cleanLabel);

  const lines = (rows || []).map((row) => {
    const cells = Array.isArray(row) ? row : [row];

    return cells
      .map((cell, index) => {
        const header = cleanedHeaders?.[index]
          ? `${cleanedHeaders[index]}: `
          : "";

        return `${header}${cellToText(cell)}`;
      })
      .filter(Boolean)
      .join(" — ");
  });

  return new Paragraph({
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: {
      after: 140,
      line: 280,
    },
    children: lines.map((line, index) =>
      professionalTextRun(
        {
          text: lang === "ar" ? normalizeArabicInlineLabels(line, lang) : line,
          size: 20,
          break: index === 0 ? undefined : 1,
        },
        lang,
      ),
    ),
  });
};
const professionalTable = (
  headerCells,
  rows,
  lang,
  colRatios = null,
  cellFillResolver = null,
  tableWidthDxa = CONTENT_WIDTH_DXA,
) => {
  const cleanTableText = (value) => {
    let text = String(value ?? "");

    text = normalizeStrategicBusinessModelRiskText(text, lang);

    if (lang === "ar") {
      text = normalizeArabicInlineLabels(text, lang);

      // للـ labels المفردة داخل الجداول
      text = arabicizeLabel(text, lang, "metric");
    }

    return text;
  };

  const cleanedHeaders = headerCells.map(cleanTableText);

  const cleanedRows = rows.map((cells) => cells.map(cleanTableText));

  const n = cleanedHeaders.length;

  const ratios =
    colRatios && colRatios.length === n
      ? colRatios
      : cleanedHeaders.map(() => 1 / n);

  const totalRatio = ratios.reduce((a, b) => a + b, 0);

  const widths = ratios.map((r) =>
    Math.floor((r / totalRatio) * tableWidthDxa),
  );

  return new Table({
    width: {
      size: tableWidthDxa,
      type: WidthType.DXA,
    },

    columnWidths: widths,

    visuallyRightToLeft: lang === "ar",

    borders: {
      top: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.accent,
      },

      bottom: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.accent,
      },

      left: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.accent,
      },

      right: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.accent,
      },

      insideHorizontal: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.accent,
      },

      insideVertical: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.accent,
      },
    },

    rows: [
      new TableRow({
        cantSplit: true,
        tableHeader: true,

        children: cleanedHeaders.map(
          (text, i) =>
            new TableCell({
              width: {
                size: widths[i],
                type: WidthType.DXA,
              },

              shading: {
                type: ShadingType.CLEAR,
                fill: THEME.tableHeaderBg,
              },

              verticalAlign: VerticalAlign.CENTER,

              margins: {
                top: 110,
                bottom: 110,
                left: 130,
                right: 130,
              },

              children: [
                new Paragraph({
                  bidirectional: lang === "ar" || containsArabic(text),

                  alignment:
                    lang === "ar" || containsArabic(text)
                      ? AlignmentType.LEFT
                      : AlignmentType.CENTER,

                  children: [
                    professionalTextRun(
                      {
                        text,
                        size: 20,
                      },

                      // مهم جدًا:
                      // لا نرجع "en" لمجرد أن النص الأصلي إنجليزي
                      lang,
                    ),
                  ],
                }),
              ],
            }),
        ),
      }),

      ...cleanedRows.map(
        (cells, i) =>
          new TableRow({
            cantSplit: true,

            children: cells.map(
              (text, ci) =>
                new TableCell({
                  width: {
                    size: widths[ci],
                    type: WidthType.DXA,
                  },

                  shading: {
                    type: ShadingType.CLEAR,

                    fill:
                      typeof cellFillResolver === "function"
                        ? cellFillResolver(rows[i]?.[ci], i, ci) ||
                          (i % 2 === 1 ? THEME.tableAltRowBg : "FFFFFF")
                        : i % 2 === 1
                          ? THEME.tableAltRowBg
                          : "FFFFFF",
                  },

                  margins: {
                    top: 90,
                    bottom: 90,
                    left: 130,
                    right: 130,
                  },

                  children: [
                    new Paragraph({
                      bidirectional: lang === "ar" || containsArabic(text),

                      alignment:
                        lang === "ar" || containsArabic(text)
                          ? AlignmentType.LEFT
                          : align(lang),

                      children: [
                        professionalTextRun(
                          {
                            text,
                            size: 20,
                          },
                          lang,
                        ),
                      ],
                    }),
                  ],
                }),
            ),
          }),
      ),
    ],
  });
};

// Array of plain objects -> professional table, columns derived
// automatically (keeps authority_matrix / governance_committees /
// financial_forecast-style array data working without hardcoding
// column names).
const tableFromArrayOfObjects = (
  arr,
  lang,
  tableWidthDxa = CONTENT_WIDTH_DXA,
) => {
  const columns = [];
  arr.forEach((obj) => {
    if (obj && typeof obj === "object") {
      Object.keys(obj).forEach((k) => {
        if (!columns.includes(k) && !META_KEYS.has(k.toLowerCase()))
          columns.push(k);
      });
    }
  });
  const headerCells = columns.map((c) => translateKey(c, lang));
  const rows = arr.map((obj) =>
    columns.map((col) => {
      const raw = obj ? obj[col] : "";
      return raw === undefined || raw === null
        ? ""
        : typeof raw === "object"
          ? JSON.stringify(raw)
          : String(raw);
    }),
  );
  return professionalTable(headerCells, rows, lang, null, null, tableWidthDxa);
};

// =========================================================
// WORD-NATIVE CHARTS
// =========================================================

const parseNumericValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value === null || value === undefined) return null;
  const str = String(value)
    .replace(/[,%]/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  if (!str || str === "-" || str === ".") return null;
  const number = Number(str);
  return Number.isFinite(number) ? number : null;
};

const compactValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "number") return String(value);
  return Math.abs(value) >= 1000
    ? value.toLocaleString(undefined, { maximumFractionDigits: 1 })
    : String(Math.round(value * 10) / 10);
};

const chartFromPoints = (title, points, lang) => {
  const usable = points
    .filter((p) => p && p.label && Number.isFinite(p.value))
    .slice(0, 12);
  if (usable.length < 2) return null;

  const max = Math.max(...usable.map((p) => Math.abs(p.value))) || 1;
  const labelWidth = Math.floor(CONTENT_WIDTH_DXA * 0.28);
  const barWidth = Math.floor(CONTENT_WIDTH_DXA * 0.54);
  const valueWidth = CONTENT_WIDTH_DXA - labelWidth - barWidth;

  return new Table({
    width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: [labelWidth, barWidth, valueWidth],
    visuallyRightToLeft: lang === "ar",
    borders: {
      top: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      bottom: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      left: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      right: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      insideHorizontal: {
        style: BorderStyle.SINGLE,
        size: 1,
        color: THEME.figureGrid,
      },
      insideVertical: { style: BorderStyle.NONE },
    },
    rows: usable.map(({ label, value }, idx) => {
      const blocks = Math.max(1, Math.round((Math.abs(value) / max) * 24));
      const bar = "█".repeat(blocks);
      return new TableRow({
        children: [
          new TableCell({
            width: { size: labelWidth, type: WidthType.DXA },
            shading: {
              type: ShadingType.CLEAR,
              fill: idx % 2 ? "FFFFFF" : THEME.figureBg,
            },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [
              new Paragraph({
                bidirectional: containsArabic(label) || lang === "ar",
                alignment: align(lang),
                children: [
                  professionalTextRun(
                    {
                      text: arabicizeLabel(label, lang).slice(0, 42),
                      size: 18,
                    },
                    lang,
                  ),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: barWidth, type: WidthType.DXA },
            shading: {
              type: ShadingType.CLEAR,
              fill: idx % 2 ? "FFFFFF" : THEME.figureBg,
            },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [
              new Paragraph({
                alignment:
                  lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,
                children: [
                  professionalTextRun(
                    {
                      text: bar,
                      color: value < 0 ? "C0504D" : THEME.secondary,
                      size: 18,
                    },
                    lang,
                  ),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: valueWidth, type: WidthType.DXA },
            shading: {
              type: ShadingType.CLEAR,
              fill: idx % 2 ? "FFFFFF" : THEME.figureBg,
            },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  professionalTextRun(
                    {
                      text: compactValue(value),
                      bold: true,
                      color: THEME.primary,
                      size: 18,
                    },
                    lang,
                  ),
                ],
              }),
            ],
          }),
        ],
      });
    }),
  });
};

const kpiIndicatorGrid = (title, points, lang) => {
  const usable = points
    .filter((p) => p && p.label && Number.isFinite(p.value))
    .slice(0, 6);
  if (usable.length < 2) return null;
  const max = Math.max(...usable.map((p) => Math.abs(p.value))) || 1;
  const colWidth = Math.floor(CONTENT_WIDTH_DXA / usable.length);

  return new Table({
    width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: usable.map(() => colWidth),
    visuallyRightToLeft: lang === "ar",
    borders: {
      top: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      bottom: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      left: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      right: { style: BorderStyle.SINGLE, size: 2, color: THEME.figureGrid },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: {
        style: BorderStyle.SINGLE,
        size: 2,
        color: THEME.figureGrid,
      },
    },
    rows: [
      new TableRow({
        children: usable.map((point) => {
          const score = Math.max(
            0,
            Math.min(10, Math.round((Math.abs(point.value) / max) * 10)),
          );
          const filled = "●".repeat(score);
          const empty = "○".repeat(10 - score);
          return new TableCell({
            width: { size: colWidth, type: WidthType.DXA },
            shading: { type: ShadingType.CLEAR, fill: THEME.figureBg },
            verticalAlign: VerticalAlign.CENTER,
            margins: { top: 180, bottom: 180, left: 100, right: 100 },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  professionalTextRun(
                    { text: filled, color: THEME.secondary, size: 20 },
                    lang,
                  ),
                  professionalTextRun(
                    { text: empty, color: THEME.figureGrid, size: 20 },
                    lang,
                  ),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 60, after: 40 },
                children: [
                  professionalTextRun(
                    {
                      text: compactValue(point.value),
                      bold: true,
                      color: THEME.primary,
                      size: 25,
                    },
                    lang,
                  ),
                ],
              }),
              new Paragraph({
                bidirectional: lang === "ar",
                alignment: AlignmentType.CENTER,
                children: [
                  professionalTextRun(
                    {
                      text: arabicizeLabel(point.label, lang).slice(0, 34),
                      color: THEME.accent,
                      size: 16,
                    },
                    lang,
                  ),
                ],
              }),
            ],
          });
        }),
      }),
    ],
  });
};
const CHART_COLORS = [
  "2F5597",
  "5B9BD5",
  "70AD47",
  "FFC000",
  "ED7D31",
  "A5A5A5",
  "9E480E",
  "4472C4",
];

const escapeXml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
};

const donutSegmentPath = (cx, cy, outerR, innerR, startAngle, endAngle) => {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, startAngle);

  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
    "Z",
  ].join(" ");
};
const pieDistributionChart = (title, points, lang) => {
  const usable = (points || [])
    .filter(
      (p) =>
        p && p.label && Number.isFinite(Number(p.value)) && Number(p.value) > 0,
    )
    .slice(0, 6);

  if (usable.length < 2) return null;

  const total = usable.reduce((sum, point) => sum + Number(point.value), 0);

  if (!total) return null;

  const canvas = document.createElement("canvas");

  const canvasWidth = 1000;
  const canvasHeight = 500;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext("2d");

  if (!ctx) return null;

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const colors = [
    "#1F3864",
    "#2E5395",
    "#4472A8",
    "#8496B0",
    "#B08A32",
    "#70AD47",
  ];

  // ---------- DONUT ----------
  const cx = 250;
  const cy = 250;
  const outerRadius = 150;
  const innerRadius = 88;

  let startAngle = -Math.PI / 2;

  usable.forEach((point, index) => {
    const value = Number(point.value);

    const portion = value / total;
    const endAngle = startAngle + portion * Math.PI * 2;

    ctx.beginPath();

    ctx.arc(cx, cy, outerRadius, startAngle, endAngle);

    ctx.arc(cx, cy, innerRadius, endAngle, startAngle, true);

    ctx.closePath();

    ctx.fillStyle = colors[index % colors.length];

    ctx.fill();

    startAngle = endAngle;
  });

  // ---------- CENTER ----------
  ctx.textAlign = "center";

  ctx.fillStyle = "#6B7280";
  ctx.font =
    lang === "ar" ? `26px ${THEME.fontArabic}` : `26px ${THEME.fontBody}`;

  ctx.fillText(lang === "ar" ? "الإجمالي" : "Total", cx, cy - 8);

  ctx.fillStyle = "#1F3864";

  ctx.font =
    lang === "ar"
      ? `bold 42px ${THEME.fontArabic}`
      : `bold 42px ${THEME.fontBody}`;

  ctx.fillText(compactValue(total), cx, cy + 45);

  // ---------- LEGEND ----------
  ctx.textAlign = lang === "ar" ? "right" : "left";

  usable.forEach((point, index) => {
    const y = 115 + index * 58;

    const pct = Math.round((Number(point.value) / total) * 100);

    ctx.beginPath();

    ctx.arc(590, y, 10, 0, Math.PI * 2);

    ctx.fillStyle = colors[index % colors.length];

    ctx.fill();

    ctx.fillStyle = "#333333";

    ctx.font =
      lang === "ar" ? `24px ${THEME.fontArabic}` : `24px ${THEME.fontBody}`;

    const label = arabicizeLabel(point.label, lang);

    if (lang === "ar") {
      ctx.textAlign = "right";

      ctx.fillText(label, 940, y + 8);

      ctx.fillStyle = "#6B7280";

      ctx.fillText(`${pct}%`, 560, y + 8);
    } else {
      ctx.textAlign = "left";

      ctx.fillText(label, 620, y + 8);

      ctx.textAlign = "right";
      ctx.fillStyle = "#6B7280";

      ctx.fillText(`${pct}%`, 940, y + 8);
    }
  });

  return canvasChartParagraph(canvas, 520, 260);
};

const gaugeIndicatorChart = (title, points, lang) => {
  const usable = (points || [])
    .filter((p) => p && p.label && Number.isFinite(p.value))
    .slice(0, 6);

  if (usable.length < 2) return null;

  const max = Math.max(...usable.map((p) => Math.abs(Number(p.value)))) || 1;

  const width = 720;
  const rowHeight = 40;
  const topPad = 28;
  const bottomPad = 24;
  const height = topPad + bottomPad + usable.length * rowHeight;

  const bars = usable
    .map((point, idx) => {
      const label = escapeXml(arabicizeLabel(point.label, lang));
      const value = Number(point.value);
      const pct = Math.max(0, Math.min(100, (Math.abs(value) / max) * 100));
      const y = topPad + idx * rowHeight;
      const color = CHART_COLORS[idx % CHART_COLORS.length];
      const barWidth = Math.max(8, Math.round((pct / 100) * 290));

      return `
        <text
          x="24"
          y="${y + 15}"
          font-size="13"
          font-family="${lang === "ar" ? THEME.fontArabic : THEME.fontBody}"
          fill="#333333"
        >${label}</text>

        <rect
          x="250"
          y="${y}"
          width="290"
          height="16"
          rx="8"
          ry="8"
          fill="#E9EDF4"
        />

        <rect
          x="250"
          y="${y}"
          width="${barWidth}"
          height="16"
          rx="8"
          ry="8"
          fill="#${color}"
        />

        <text
          x="565"
          y="${y + 13}"
          text-anchor="end"
          font-size="12"
          font-family="${lang === "ar" ? THEME.fontArabic : THEME.fontBody}"
          fill="#666666"
        >${Math.round(pct)}%</text>

        <text
          x="690"
          y="${y + 13}"
          text-anchor="end"
          font-size="13"
          font-family="${lang === "ar" ? THEME.fontArabic : THEME.fontBody}"
          fill="#222222"
          font-weight="700"
        >${escapeXml(compactValue(value))}</text>
      `;
    })
    .join("");

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <rect x="0" y="0" width="${width}" height="${height}" rx="18" ry="18" fill="#FFFFFF"/>
      ${bars}
    </svg>
  `;
};

const dataUrlToBytes = (dataUrl) => {
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);

  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const canvasChartParagraph = (canvas, width = 560, height = 280) => {
  const pngData = dataUrlToBytes(canvas.toDataURL("image/png"));

  return new Table({
    width: {
      size: CONTENT_WIDTH_DXA,
      type: WidthType.DXA,
    },
    alignment: AlignmentType.CENTER,
    borders: {
      top: { style: BorderStyle.SINGLE, size: 6, color: THEME.accent },
      bottom: { style: BorderStyle.SINGLE, size: 6, color: THEME.accent },
      left: { style: BorderStyle.SINGLE, size: 6, color: THEME.accent },
      right: { style: BorderStyle.SINGLE, size: 6, color: THEME.accent },
      insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            shading: {
              type: ShadingType.CLEAR,
              fill: "FFFFFF",
            },
            margins: {
              top: 140,
              bottom: 140,
              left: 140,
              right: 140,
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 80, after: 80 },
                children: [
                  new ImageRun({
                    type: "png",
                    data: pngData,
                    transformation: {
                      width,
                      height,
                    },
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
};
const normalizeVisualTitle = (title) =>
  String(title || "")
    .toLowerCase()
    .trim();

const cleanChartPoints = (points) => {
  if (!Array.isArray(points)) return null;

  const cleaned = points
    .filter(
      (p) => p && p.label !== undefined && Number.isFinite(Number(p.value)),
    )
    .map((p) => ({
      label: String(p.label),
      value: Number(p.value),
    }))
    .filter((p) => p.value >= 0);

  return cleaned.length >= 2 ? cleaned : null;
};

const isScoreLikeChart = (title) =>
  /(score|readiness|feasibility|index|rating|kpi|درجة|مؤشر|جاهزية|جدوى|تقييم)/i.test(
    normalizeVisualTitle(title),
  );

const isDistributionLikeChart = (title) =>
  /(distribution|share|mix|allocation|segment|segments|risk|risks|customers|competitors|distribution|توزيع|حصة|مزيج|شرائح|تقسيم|مخاطر|عملاء|منافسين)/i.test(
    normalizeVisualTitle(title),
  );

const isComparisonLikeChart = (title) =>
  /(comparison|benchmark|market|competitor|tam|sam|som|forces|analysis|مقارنة|سوق|منافس|تحليل|قوى|حجم)/i.test(
    normalizeVisualTitle(title),
  );
const chartVisualsFromPoints = (title, points, lang) => {
  const clean = cleanChartPoints(points);
  if (!clean) return [];

  const visuals = [];

  // ---------- 1) SCORE / INDEX / READINESS ----------
  if (isScoreLikeChart(title)) {
    if (clean.length <= 4) {
      visuals.push({
        title:
          lang === "ar"
            ? `${title} - مؤشرات رئيسية`
            : `${title} - KPI Overview`,
        node: kpiIndicatorGrid(title, clean, lang),
      });
    }

    // visuals.push({
    //   title: lang === "ar" ? `${title} - مؤشر قياس` : `${title} - Gauge`,
    //   node: gaugeIndicatorChart(title, clean, lang),
    // });

    return visuals.filter((item) => item.node);
  }

  // ---------- 2) DISTRIBUTION / MIX / RISK SPLIT ----------
  if (isDistributionLikeChart(title) && clean.length <= 6) {
    visuals.push({
      title:
        lang === "ar" ? `${title} - توزيع نسبي` : `${title} - Distribution`,
      node: pieDistributionChart(title, clean, lang),
    });

    if (clean.length <= 4) {
      visuals.push({
        title:
          lang === "ar"
            ? `${title} - مؤشرات مختصرة`
            : `${title} - KPI Snapshot`,
        node: kpiIndicatorGrid(title, clean, lang),
      });
    }

    return visuals.filter((item) => item.node);
  }

  // ---------- 3) COMPARISON / MARKET / COMPETITOR ----------
  if (isComparisonLikeChart(title) || clean.length <= 8) {
    visuals.push({
      title,
      node: chartFromPoints(title, clean, lang),
    });

    if (clean.length <= 4) {
      visuals.push({
        title:
          lang === "ar"
            ? `${title} - مؤشرات مختصرة`
            : `${title} - KPI Snapshot`,
        node: kpiIndicatorGrid(title, clean, lang),
      });
    }

    return visuals.filter((item) => item.node);
  }

  // ---------- 4) DEFAULT ----------
  visuals.push({
    title,
    node: chartFromPoints(title, clean.slice(0, 8), lang),
  });

  return visuals.filter((item) => item.node);
};
const watermarkHeader = (watermarkBytes) =>
  new Header({
    children: [
      new Paragraph({
        children: [
          new ImageRun({
            type: "png",
            data: watermarkBytes,

            transformation: {
              width: 430,
              height: 590,
            },

            floating: {
              behindDocument: true,
              allowOverlap: true,

              horizontalPosition: {
                relative: HorizontalPositionRelativeFrom.PAGE,
                align: HorizontalPositionAlign.CENTER,
              },

              verticalPosition: {
                relative: VerticalPositionRelativeFrom.PAGE,
                align: VerticalPositionAlign.CENTER,
              },

              wrap: {
                type: TextWrappingType.NONE,
              },
            },
          }),
        ],
      }),
    ],
  });
const chartPointsFromArrayOfObjects = (arr, lang) => {
  if (!Array.isArray(arr) || arr.length < 2) return null;
  const columns = Object.keys(
    arr.find((item) => item && typeof item === "object") || {},
  );
  const labelKey =
    columns.find((k) =>
      /(year|month|period|scenario|category|item|name|السنة|الفترة|البند)/i.test(
        k,
      ),
    ) || columns[0];
  const numericKey = columns.find(
    (k) =>
      arr.filter((row) => Number.isFinite(parseNumericValue(row?.[k])))
        .length >= 2,
  );
  if (!labelKey || !numericKey || labelKey === numericKey) return null;
  return arr.map((row, idx) => ({
    label: arabicizeLabel(
      row?.[labelKey] || `${t("metric", lang)} ${idx + 1}`,
      lang,
    ),
    value: parseNumericValue(row?.[numericKey]),
  }));
};

const chartPointsFromFlatObject = (obj, lang) => {
  const points = Object.entries(obj || {})
    .filter(([k]) => !META_KEYS.has(k.toLowerCase()))
    .map(([k, v]) => ({
      label: arabicizeLabel(k, lang),
      value: parseNumericValue(v),
    }))
    .filter((p) => Number.isFinite(p.value));
  return points.length >= 2 ? points : null;
};

const extractNumericPointsFromText = (text, lang) => {
  const str = String(text || "");
  const matches = [
    ...str.matchAll(
      /([^\n.!؟?،,;؛:]{3,60}?)\s*(-?\d[\d,]*(?:\.\d+)?\s*(?:%|٪|SAR|USD|ر\.س|ريال|دولار)?)/g,
    ),
  ];
  const points = matches
    .map((match, idx) => ({
      label:
        match[1]?.replace(/\s+/g, " ").trim().slice(-42) ||
        `${t("metric", lang)} ${idx + 1}`,
      value: parseNumericValue(match[2]),
    }))
    .filter((p) => Number.isFinite(p.value));

  const unique = [];
  points.forEach((point) => {
    if (
      !unique.some((p) => p.label === point.label && p.value === point.value)
    ) {
      unique.push(point);
    }
  });
  return unique.length >= 2 ? unique.slice(0, 8) : null;
};

const inferredMetricsTable = (title, text, lang) => {
  const points = extractNumericPointsFromText(text, lang);
  if (!points) return null;
  return {
    table: professionalTable(
      [
        WORD_RENDER_IS_TECHNICAL && lang === "ar"
          ? "مقياس المتابعة"
          : t("metric", lang),
        t("value", lang),
      ],
      points.map((point) => [point.label, compactValue(point.value)]),
      lang,
      [2, 1],
    ),
    charts: chartVisualsFromPoints(title, points, lang),
  };
};

// =========================================================
// SWOT MATRIX
// =========================================================

const isSwotLike = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value).map((k) => k.toLowerCase());
  return ["strengths", "weaknesses", "opportunities", "threats"].every((k) =>
    keys.includes(k),
  );
};

const swotLabels = {
  strengths: { en: "Strengths", ar: "نقاط القوة" },
  weaknesses: { en: "Weaknesses", ar: "نقاط الضعف" },
  opportunities: { en: "Opportunities", ar: "الفرص" },
  threats: { en: "Threats", ar: "التهديدات" },
};

const swotLabel = (key, lang) => {
  if (lang !== "ar") return swotLabels[key].en;
  return {
    strengths: "نقاط القوة",
    weaknesses: "نقاط الضعف",
    opportunities: "الفرص",
    threats: "التهديدات",
  }[key];
};

// A SWOT quadrant's value may be an array of points, or one long
// narrative string with an inline "1) ... 2) ..." pattern — handle both.
const toPointList = (value) => {
  const dedupe = (items) => {
    const seen = new Set();
    return items
      .map((item) => String(item).replace(/\s+/g, " ").trim())
      .filter((item) => {
        if (!item) return false;
        const key = item.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };
  if (Array.isArray(value)) return dedupe(value);
  if (typeof value !== "string") return dedupe([value]);
  const split = splitInlineNumberedText(value);
  if (split) return dedupe(split.items);
  return dedupe(splitIntoParagraphs(value, 2));
};

const swotCellContent = (title, items, lang) => [
  new Paragraph({
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: { after: 100 },
    children: [
      professionalTextRun(
        { text: title, bold: true, color: THEME.primary, size: 22 },
        lang,
      ),
    ],
  }),
  ...toPointList(items).map(
    (item) =>
      new Paragraph({
        bidirectional: lang === "ar",
        alignment: align(lang),
        numbering: { reference: "premium-bullets", level: 0 },
        spacing: { after: 60, line: 270 },
        children: runsWithHighlightedNumbers(item, lang, { size: 20 }),
      }),
  ),
];

// SWOT rendered as a 2x2 quadrant matrix with distinct tints per
// quadrant — the classic consulting-deck presentation.
const swotMatrix = (value, lang) => {
  const find = (name) => {
    const k = Object.keys(value).find((key) => key.toLowerCase() === name);
    return k ? value[k] : [];
  };
  const half = Math.floor(CONTENT_WIDTH_DXA / 2);
  const quadrant = (title, items, fill) =>
    new TableCell({
      width: { size: half, type: WidthType.DXA },
      shading: { type: ShadingType.CLEAR, fill },
      margins: { top: 160, bottom: 160, left: 180, right: 180 },
      children: swotCellContent(title, items, lang),
    });

  return new Table({
    width: { size: CONTENT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: [half, half],
    visuallyRightToLeft: lang === "ar",
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: THEME.accent },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: THEME.accent },
      left: { style: BorderStyle.SINGLE, size: 4, color: THEME.accent },
      right: { style: BorderStyle.SINGLE, size: 4, color: THEME.accent },
      insideHorizontal: {
        style: BorderStyle.SINGLE,
        size: 4,
        color: THEME.accent,
      },
      insideVertical: {
        style: BorderStyle.SINGLE,
        size: 4,
        color: THEME.accent,
      },
    },
    rows: [
      new TableRow({
        children: [
          quadrant(
            swotLabel("strengths", lang),
            find("strengths"),
            THEME.swotStrengthBg,
          ),
          quadrant(
            swotLabel("weaknesses", lang),
            find("weaknesses"),
            THEME.swotWeaknessBg,
          ),
        ],
      }),
      new TableRow({
        children: [
          quadrant(
            swotLabel("opportunities", lang),
            find("opportunities"),
            THEME.swotOpportunityBg,
          ),
          quadrant(
            swotLabel("threats", lang),
            find("threats"),
            THEME.swotThreatBg,
          ),
        ],
      }),
    ],
  });
};

// =========================================================
// SCENARIO COMPARISON (best/expected/worst case objects)
// =========================================================

const isScenarioSetLike = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value).map((k) => k.toLowerCase());
  return (
    ["best_case", "expected_case", "worst_case"].every((k) =>
      keys.includes(k),
    ) ||
    (keys.includes("best_case") && keys.includes("worst_case"))
  );
};

const scenarioComparisonTable = (value, lang) => {
  const order = ["worst_case", "expected_case", "best_case"];
  const present = order.filter((k) =>
    Object.keys(value).some((key) => key.toLowerCase() === k),
  );
  const getScenario = (name) => {
    const k = Object.keys(value).find((key) => key.toLowerCase() === name);
    return k ? value[k] : {};
  };
  // union of dimension keys across scenarios, excluding meta
  const dims = [];
  present.forEach((p) => {
    const sc = getScenario(p);
    if (sc && typeof sc === "object") {
      Object.keys(sc).forEach((k) => {
        if (!META_KEYS.has(k.toLowerCase()) && !dims.includes(k)) dims.push(k);
      });
    }
  });

  const headerCells = [
    t("dimension", lang),
    ...present.map((p) => translateKey(p, lang)),
  ];
  const rows = dims.map((dim) => [
    translateKey(dim, lang),
    ...present.map((p) => {
      const sc = getScenario(p);
      const raw = sc ? sc[dim] : "";
      return raw === undefined || raw === null ? "" : String(raw);
    }),
  ]);
  return professionalTable(headerCells, rows, lang, [
    1.4,
    ...present.map(() => 1),
  ]);
};

// =========================================================
// SPECIAL-CASE KEY RENDERERS
// =========================================================

// Keys whose value is a simple flat object of short label:value pairs
// (e.g. roi_metrics, readiness_score, go_conditional_go_no_go_assessment)
// render best as a compact two-column table rather than nested headings.
const isFlatKeyValueObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const entries = Object.entries(value).filter(
    ([k]) => !META_KEYS.has(k.toLowerCase()),
  );
  if (entries.length === 0) return false;
  return entries.every(([, v]) => typeof v !== "object" || v === null);
};

const flatKeyValueTable = (value, lang) => {
  const rows = Object.entries(value)
    .filter(([k]) => !META_KEYS.has(k.toLowerCase()))
    .map(([k, v]) => [
      translateKey(k, lang),
      v === null || v === undefined ? "" : String(v),
    ]);

  const firstColumnLabel =
    WORD_RENDER_IS_TECHNICAL && lang === "ar"
      ? "عنصر التقييم"
      : t("metric", lang);

  return professionalTable(
    [firstColumnLabel, t("value", lang)],
    rows,
    lang,
    [1, 2.2],
  );
};

// key_assumptions / key_takeaways arrays get a consistent callout
// treatment wherever they appear, instead of a generic bullet list
// buried under a subheading.
const KEY_ASSUMPTIONS_ALIASES = new Set(["key_assumptions"]);
const KEY_TAKEAWAYS_ALIASES = new Set([
  "key_takeaways",
  "strategic_implications_and_key_takeaways",
  "key_findings",
  "main_findings",
  "key_insights",
  "main_insights",
  "key_conclusions",
  "main_conclusions",
]);

// Long narrative strings get sentence-grouped / numbered-list treatment
// instead of being dropped in as one dense paragraph.
const NARRATIVE_MIN_LENGTH = 220;

const technicalObjectToText = (value, lang) => {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => technicalObjectToText(item, lang))
      .filter(Boolean)
      .join(lang === "ar" ? "، " : ", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([key]) => !META_KEYS.has(key.toLowerCase()))
      .map(([key, entryValue]) => {
        const rendered = technicalObjectToText(entryValue, lang);

        const safeKey =
          lang === "ar"
            ? arabicizeLabel(prettifyKey(key), lang, "metric")
            : translateKey(key, lang);

        return rendered ? `${safeKey}: ${rendered}` : "";
      })
      .filter(Boolean)
      .join("؛ ");
  }

  let text = String(value);

  if (lang === "ar") {
    text = normalizeArabicInlineLabels(text, lang);
  }

  return text;
};

// =========================================================
// CORE RECURSIVE RENDERER
// =========================================================
// Walks any value from a stage/module and emits professionally
// styled docx nodes. Keys are resolved through the heading
// dictionary so raw JSON names never reach the page. Different
// shapes (SWOT-like, scenario sets, flat metric objects, array of
// objects, plain narrative strings) get distinct, purpose-built
// renderers instead of one generic JSON walk.
const isGenericAnalyticalHeading = (label, lang) => {
  if (lang !== "ar") return false;

  return String(label || "").trim() === "محور تحليلي";
};
const renderNode = (rawKey, label, value, level, lang, nodes) => {
  const safeLabel =
    lang === "ar"
      ? arabicizeLabel(prettifyKey(rawKey || label), lang, rawKey || "metric")
      : label;
  console.log("🟢 RENDER NODE ACTIVE:", rawKey, label);
  if (
    lang === "ar" &&
    /strategic|affecting|business.model/i.test(`${rawKey} ${label}`)
  ) {
    console.log("🔴 NESTED HEADING DEBUG:", {
      rawKey,
      label,
      translated: translateKey(rawKey, lang),
    });
  }
  if (value === null || value === undefined || value === "") return;
  const keyNorm = String(rawKey || "").toLowerCase();

  if (META_KEYS.has(keyNorm)) return;

  // ---- key_assumptions / key_takeaways -> callout box ----
  if (KEY_ASSUMPTIONS_ALIASES.has(keyNorm)) {
    const assumptionLines = Array.isArray(value)
      ? value.map(String)
      : typeof value === "string"
        ? splitIntoParagraphs(value)
        : toPointList(value);

    if (assumptionLines.length) {
      nodes.push(
        highlightBox(
          t("keyAssumptions", lang),
          assumptionLines,
          lang,
          THEME.boxBg,
          THEME.boxBorder,
        ),
      );

      nodes.push(emptyLine());
      return;
    }
  }
  if (KEY_TAKEAWAYS_ALIASES.has(keyNorm)) {
    const takeawayLines = Array.isArray(value)
      ? value.map(String)
      : typeof value === "string"
        ? splitIntoParagraphs(value)
        : toPointList(value);

    if (takeawayLines.length) {
      nodes.push(
        highlightBox(
          t("keyTakeaways", lang),
          takeawayLines,
          lang,
          "FBF3E3",
          THEME.gold,
        ),
      );

      nodes.push(emptyLine());
      return;
    }
  }

  // ---- plain scalar ----
  if (typeof value !== "object") {
    if (typeof value === "string" && value.length >= NARRATIVE_MIN_LENGTH) {
      if (!isGenericAnalyticalHeading(safeLabel, lang)) {
        nodes.push(
          new Paragraph({
            bidirectional: lang === "ar",
            alignment: align(lang),
            spacing: { after: 80 },
            children: [
              professionalTextRun(
                {
                  text: safeLabel,
                  bold: true,
                  color: THEME.primary,
                  size: 22,
                },
                lang,
              ),
            ],
          }),
        );
      }

      renderNarrative(value, lang, nodes);
    } else {
      nodes.push(keyValueParagraph(safeLabel, value, lang));
    }
    return;
  }

  // ---- arrays ----
  if (Array.isArray(value)) {
    if (value.length === 0) return;
    const allPrimitive = value.every(
      (v) => v === null || typeof v !== "object",
    );
    nodes.push(sectionHeading(safeLabel, level, lang));
    if (allPrimitive) {
      uniqueTextItems(value).forEach((item) =>
        nodes.push(bulletParagraph(item, lang)),
      );
    } else {
      nodes.push(captionParagraph("table", safeLabel, lang));
      nodes.push(tableFromArrayOfObjects(value, lang));
      nodes.push(emptyLine());
      const chartPoints = chartPointsFromArrayOfObjects(value, lang);
      chartVisualsFromPoints(safeLabel, chartPoints || [], lang).forEach(
        ({ title, node }) => {
          nodes.push(captionParagraph("figure", title, lang));
          nodes.push(node);
          nodes.push(emptyLine());
        },
      );
    }
    return;
  }

  // ---- SWOT-shaped object ----
  if (isSwotLike(value)) {
    nodes.push(sectionHeading(safeLabel, level, lang));
    nodes.push(captionParagraph("table", safeLabel, lang));
    nodes.push(swotMatrix(value, lang));
    nodes.push(emptyLine());
    return;
  }

  // ---- scenario set (best/expected/worst case) ----
  if (isScenarioSetLike(value)) {
    nodes.push(sectionHeading(safeLabel, level, lang));
    nodes.push(captionParagraph("table", safeLabel, lang));
    nodes.push(scenarioComparisonTable(value, lang));
    nodes.push(emptyLine());
    return;
  }

  // ---- flat key:value metrics object ----
  if (isFlatKeyValueObject(value)) {
    nodes.push(sectionHeading(safeLabel, level, lang));

    nodes.push(captionParagraph("table", safeLabel, lang));
    nodes.push(flatKeyValueTable(value, lang));
    nodes.push(emptyLine());
    const chartPoints = chartPointsFromFlatObject(value, lang);
    chartVisualsFromPoints(safeLabel, chartPoints || [], lang).forEach(
      ({ title, node }) => {
        nodes.push(captionParagraph("figure", title, lang));
        nodes.push(node);
        nodes.push(emptyLine());
      },
    );
    return;
  }

  // ---- generic nested object: recurse ----
  const entries = Object.entries(value).filter(
    ([k]) => !META_KEYS.has(k.toLowerCase()),
  );
  if (entries.length === 0) return;

  if (!isGenericAnalyticalHeading(safeLabel, lang)) {
    nodes.push(sectionHeading(safeLabel, level, lang));
  }
  entries.forEach(([k, v]) => {
    const childLabel =
      lang === "ar"
        ? arabicizeLabel(prettifyKey(k), lang, k || "metric")
        : translateKey(k, lang);

    renderNode(k, childLabel, v, level + 1, lang, nodes);
  });
};

// This overrides the generic keyValueParagraph path for values
// longer than a couple of sentences.
const marketSizingTable = (marketModule, lang) => {
  if (!marketModule || typeof marketModule !== "object") return null;

  const tam = marketModule.total_addressable_market_tam;
  const sam = marketModule.serviceable_addressable_market_sam;
  const som = marketModule.serviceable_obtainable_market_som;

  if (!tam && !sam && !som) return null;

  const notAvailable =
    lang === "ar" ? "غير متاح في بيانات الدراسة" : "Not available";

  return professionalTable(
    [
      lang === "ar" ? "مستوى السوق" : "Market Level",
      lang === "ar" ? "التحليل" : "Analysis",
    ],
    [
      [
        lang === "ar"
          ? "TAM — إجمالي السوق القابل للاستهداف"
          : "TAM — Total Addressable Market",
        tam || notAvailable,
      ],
      [
        lang === "ar"
          ? "SAM — السوق المتاح للخدمة"
          : "SAM — Serviceable Addressable Market",
        sam || notAvailable,
      ],
      [
        lang === "ar"
          ? "SOM — السوق الممكن الاستحواذ عليه"
          : "SOM — Serviceable Obtainable Market",
        som || notAvailable,
      ],
    ],
    lang,
    [1.35, 2.65],
  );
};
const qualitativeRiskMatrix = (riskModule, lang) => {
  if (!riskModule || typeof riskModule !== "object") return null;

  const matrixText = riskModule.overall_risk_matrix;

  if (!matrixText || typeof matrixText !== "string") return null;

  const highSevere =
    lang === "ar"
      ? matrixText
          .match(
            /احتمالية عالية\s*[×x]\s*تأثير شديد[^:]*:\s*([\s\S]*?)(?=في خانة|$)/i,
          )?.[1]
          ?.trim()
      : matrixText
          .match(
            /high probability\s*[×x]\s*severe impact[^:]*:\s*([\s\S]*?)(?=high probability|medium probability|$)/i,
          )?.[1]
          ?.trim();

  const highMedium =
    lang === "ar"
      ? matrixText
          .match(
            /احتمالية عالية\s*[×x]\s*تأثير متوسط[^:]*:\s*([\s\S]*?)(?=في خانة|$)/i,
          )?.[1]
          ?.trim()
      : matrixText
          .match(
            /high probability\s*[×x]\s*medium impact[^:]*:\s*([\s\S]*?)(?=high probability|medium probability|$)/i,
          )?.[1]
          ?.trim();

  const mediumSevere =
    lang === "ar"
      ? matrixText
          .match(
            /احتمالية متوسطة\s*[×x]\s*تأثير شديد[^:]*:\s*([\s\S]*?)(?=هذا التوزيع يعني|في خانة|$)/i,
          )?.[1]
          ?.trim()
      : matrixText
          .match(
            /medium probability\s*[×x]\s*severe impact[^:]*:\s*([\s\S]*?)(?=this distribution means|high probability|medium probability|$)/i,
          )?.[1]
          ?.trim();
  if (!highSevere && !highMedium && !mediumSevere) {
    return null;
  }
  const na = lang === "ar" ? "—" : "—";

  return professionalTable(
    [
      lang === "ar" ? "الاحتمالية \\ التأثير" : "Probability \\ Impact",
      lang === "ar" ? "محدود" : "Limited",
      lang === "ar" ? "متوسط" : "Medium",
      lang === "ar" ? "شديد" : "Severe",
    ],
    [
      [
        lang === "ar" ? "عالية" : "High",
        na,
        highMedium || na,
        highSevere || na,
      ],
      [lang === "ar" ? "متوسطة" : "Medium", na, na, mediumSevere || na],
      [lang === "ar" ? "منخفضة" : "Low", na, na, na],
    ],
    lang,
    [0.9, 1.1, 1.4, 2.0],
  );
};
const customerJourneyFlowChart = (customerModule, lang) => {
  if (!customerModule || typeof customerModule !== "object") return null;

  const journey = customerModule.customer_journey;
  if (!journey) return null;

  let stages = [];

  // =====================================================
  // 1) BEST CASE: journey is already an ARRAY
  // =====================================================
  if (Array.isArray(journey)) {
    stages = journey
      .map((item, index) => {
        if (typeof item === "string") {
          return {
            title:
              lang === "ar" ? `المرحلة ${index + 1}` : `Stage ${index + 1}`,
            description: item,
          };
        }

        if (item && typeof item === "object") {
          const title =
            item.title ||
            item.stage ||
            item.name ||
            item.phase ||
            item.step ||
            item.label;

          const description =
            item.description ||
            item.details ||
            item.summary ||
            item.action ||
            item.activity ||
            item.value;

          if (!title && !description) return null;

          return {
            title: String(
              title ||
                (lang === "ar" ? `المرحلة ${index + 1}` : `Stage ${index + 1}`),
            ),
            description: String(description || ""),
          };
        }

        return null;
      })
      .filter(Boolean);
  }

  // =====================================================
  // 2) journey is a STRUCTURED OBJECT
  // =====================================================
  else if (typeof journey === "object") {
    stages = Object.entries(journey)
      .filter(
        ([key]) =>
          ![
            "title",
            "section_title",
            "summary",
            "overview",
            "key_takeaways",
            "key_assumptions",
          ].includes(String(key).toLowerCase()),
      )
      .map(([key, value]) => {
        if (value === null || value === undefined) return null;

        if (typeof value === "object") {
          const title =
            value.title || value.stage || value.name || translateKey(key, lang);

          const description =
            value.description ||
            value.details ||
            value.summary ||
            value.action ||
            value.activity ||
            "";

          return {
            title: String(title),
            description: String(description),
          };
        }

        return {
          title: translateKey(key, lang),
          description: String(value),
        };
      })
      .filter(Boolean);
  }

  // =====================================================
  // 3) LEGACY / CURRENT STUDIES: narrative STRING
  //    Extract stages from "Stage (description)" patterns
  // =====================================================
  else if (typeof journey === "string") {
    let source = journey.trim();

    // Only use the opening journey definition.
    // We intentionally ignore later analytical explanation.
    source =
      source.split(
        lang === "ar"
          ? /لماذا[؟?]|كيف يحدث|ماذا يعني/i
          : /why\?|why\b|how does|what does this mean/i,
      )[0] || source;

    const matches = [...source.matchAll(/([^،,;؛:]+?)\s*\(([^()]+)\)/g)];

    stages = matches
      .map((match) => {
        let title = String(match[1] || "").trim();
        const description = String(match[2] || "").trim();

        // Remove generic introductory wording,
        // without assuming any specific industry or customer type.
        title = title
          .replace(/^ماذا[؟?]?\s*/i, "")
          .replace(/^رحلة\s+العميل\s+تبدأ\s+ب?/i, "")
          .replace(/^ثم\s+/i, "")
          .replace(
            /^the\s+customer\s+journey\s+(starts|begins)\s+(with|at)\s*/i,
            "",
          )
          .replace(/^then\s+/i, "")
          .trim();

        if (!title || !description) return null;

        return {
          title,
          description,
        };
      })
      .filter(Boolean);
  }

  // Need at least two reliable stages to justify a flow chart
  if (stages.length < 2) return null;

  // Avoid an unreadable chart if a future study contains many stages
  stages = stages.slice(0, 7);

  // =====================================================
  // CANVAS
  // =====================================================
  const canvas = document.createElement("canvas");

  const canvasWidth = 1200;
  const canvasHeight = 300;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  ctx.direction = lang === "ar" ? "rtl" : "ltr";

  const marginX = 35;
  const gap = 24;

  const availableWidth = canvasWidth - marginX * 2 - gap * (stages.length - 1);

  const boxWidth = Math.max(125, Math.min(205, availableWidth / stages.length));

  const actualGap =
    stages.length > 1
      ? (canvasWidth - marginX * 2 - boxWidth * stages.length) /
        (stages.length - 1)
      : 0;

  const boxHeight = 140;
  const y = 80;

  const colors = [
    "#1F3864",
    "#2E5395",
    "#4472A8",
    "#5F78A7",
    "#748BB5",
    "#8496B0",
    "#A28E5D",
  ];

  const wrapCanvasText = (text, maxWidth, maxLines = 3) => {
    const words = String(text || "").split(/\s+/);
    const lines = [];

    let line = "";

    words.forEach((word) => {
      const test = line ? `${line} ${word}` : word;

      if (ctx.measureText(test).width <= maxWidth) {
        line = test;
      } else {
        if (line) lines.push(line);
        line = word;
      }
    });

    if (line) lines.push(line);

    if (lines.length > maxLines) {
      const trimmed = lines.slice(0, maxLines);
      trimmed[maxLines - 1] = `${trimmed[maxLines - 1].replace(/[.…]+$/, "")}…`;

      return trimmed;
    }

    return lines;
  };

  stages.forEach((stage, index) => {
    const visualIndex = lang === "ar" ? stages.length - 1 - index : index;

    const x = marginX + visualIndex * (boxWidth + actualGap);
    // ---------- BOX ----------
    ctx.fillStyle = "#F7F9FC";
    ctx.strokeStyle = colors[index % colors.length];
    ctx.lineWidth = 3;

    ctx.beginPath();

    if (typeof ctx.roundRect === "function") {
      ctx.roundRect(x, y, boxWidth, boxHeight, 15);
    } else {
      ctx.rect(x, y, boxWidth, boxHeight);
    }

    ctx.fill();
    ctx.stroke();

    // ---------- NUMBER ----------
    ctx.beginPath();
    ctx.arc(x + 21, y + 21, 14, 0, Math.PI * 2);

    ctx.fillStyle = colors[index % colors.length];
    ctx.fill();

    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";

    ctx.font =
      lang === "ar"
        ? `bold 16px ${THEME.fontArabic}`
        : `bold 16px ${THEME.fontBody}`;

    ctx.fillText(String(index + 1), x + 21, y + 27);

    // ---------- TITLE ----------
    ctx.fillStyle = THEME.primary;
    ctx.textAlign = "center";

    ctx.font =
      lang === "ar"
        ? `bold 20px ${THEME.fontArabic}`
        : `bold 20px ${THEME.fontBody}`;

    const titleLines = wrapCanvasText(stage.title, boxWidth - 24, 2);

    titleLines.forEach((line, i) => {
      ctx.fillText(line, x + boxWidth / 2, y + 45 + i * 21);
    });

    // ---------- DESCRIPTION ----------
    ctx.fillStyle = "#4B5563";

    ctx.font =
      lang === "ar" ? `15px ${THEME.fontArabic}` : `15px ${THEME.fontBody}`;

    const descStart = y + 78 + Math.max(0, titleLines.length - 1) * 12;

    const descriptionLines = wrapCanvasText(
      stage.description,
      boxWidth - 20,
      3,
    );

    descriptionLines.forEach((line, i) => {
      ctx.fillText(line, x + boxWidth / 2, descStart + i * 18);
    });

    // ---------- ARROW ----------
    if (index < stages.length - 1) {
      const arrowY = y + boxHeight / 2;

      const arrowX1 = x + boxWidth + 5;
      const arrowX2 = x + boxWidth + actualGap - 7;

      ctx.strokeStyle = "#9CA3AF";
      ctx.lineWidth = 3;

      ctx.beginPath();
      ctx.moveTo(arrowX1, arrowY);
      ctx.lineTo(arrowX2, arrowY);
      ctx.stroke();

      ctx.beginPath();

      if (lang === "ar") {
        // visual RTL arrow
        ctx.moveTo(arrowX1 + 9, arrowY - 7);
        ctx.lineTo(arrowX1, arrowY);
        ctx.lineTo(arrowX1 + 9, arrowY + 7);
      } else {
        ctx.moveTo(arrowX2 - 9, arrowY - 7);
        ctx.lineTo(arrowX2, arrowY);
        ctx.lineTo(arrowX2 - 9, arrowY + 7);
      }

      ctx.stroke();
    }
  });

  return canvasChartParagraph(canvas, 560, 140);
};
const customerPersonasTable = (customerModule, lang) => {
  if (!customerModule || typeof customerModule !== "object") return null;

  const personas = customerModule.customer_personas;
  if (!personas) return null;

  // =====================================================
  // 1) BEST CASE: structured ARRAY
  // =====================================================
  if (Array.isArray(personas)) {
    const structured = personas.filter(
      (item) => item && typeof item === "object",
    );

    if (structured.length) {
      return tableFromArrayOfObjects(structured, lang);
    }

    return null;
  }

  // =====================================================
  // 2) STRUCTURED OBJECT
  // =====================================================
  if (typeof personas === "object") {
    return flatKeyValueTable(personas, lang);
  }

  // =====================================================
  // 3) NARRATIVE STRING
  // =====================================================
  if (typeof personas !== "string") return null;

  const text = personas.trim();

  // Extract persona names from quoted values.
  // Supports Arabic/English quotation marks.
  const quotedNames = [...text.matchAll(/["“”«»]([^"“”«»]{3,120})["“”«»]/g)]
    .map((match) => String(match[1] || "").trim())
    .filter(Boolean);

  const uniqueNames = [...new Set(quotedNames)].slice(0, 8);

  if (uniqueNames.length < 2) return null;

  // Extract the analytical section after "How does it affect?"
  // so introductory text does not enter persona descriptions.
  let analysisText = text;

  const effectSplit =
    lang === "ar"
      ? text.split(/كيف يحدث الأثر[؟?]?\s*/i)
      : text.split(/how does (?:the )?(?:effect|impact) occur[?]?\s*/i);

  if (effectSplit.length > 1) {
    analysisText = effectSplit.slice(1).join(" ");
  }

  // Stop before final project-wide implications
  analysisText =
    analysisText.split(
      lang === "ar"
        ? /وماذا يعني هذا للمشروع[؟?]?/i
        : /what does this mean for the project[?]?/i,
    )[0] || analysisText;

  const markers =
    lang === "ar" ? ["أ", "ب", "ج", "د", "هـ", "ه", "و", "ز", "ح"] : null;

  const rows = uniqueNames.map((name, index) => {
    let description = "";

    // Arabic labeled narrative: (أ) ... (ب) ... (ج)
    if (lang === "ar" && markers[index]) {
      const current = markers[index];
      const next = markers[index + 1];

      const pattern = next
        ? new RegExp(`\\(${current}\\)\\s*([\\s\\S]*?)(?=\\(${next}\\)|$)`, "i")
        : new RegExp(`\\(${current}\\)\\s*([\\s\\S]*?)$`, "i");

      description = analysisText.match(pattern)?.[1]?.trim() || "";
    }

    // Generic fallback:
    // find the persona name inside the analytical section
    if (!description) {
      const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      const namePattern = new RegExp(
        `${escapedName}\\s*[:：-]?\\s*([\\s\\S]*?)(?=${uniqueNames
          .filter((n) => n !== name)
          .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
          .join("|")}|$)`,
        "i",
      );

      description = analysisText.match(namePattern)?.[1]?.trim() || "";
    }

    // Keep table readable
    if (description.length > 320) {
      description = `${description.slice(0, 317).trim()}...`;
    }

    return [
      name,
      description ||
        (lang === "ar"
          ? "التفاصيل متاحة في التحليل النصي"
          : "Details available in the narrative analysis"),
    ];
  });

  if (!rows.length) return null;

  return professionalTable(
    [
      lang === "ar" ? "شخصية العميل" : "Customer Persona",
      lang === "ar"
        ? "الدوافع ومعايير القرار"
        : "Motivations & Decision Criteria",
    ],
    rows,
    lang,
    [1.35, 3.1],
  );
};

const buyingProcessTable = (customerModule, lang) => {
  if (!customerModule || typeof customerModule !== "object") return null;

  const buyingProcess = customerModule.buying_process_and_decision_criteria;

  if (!buyingProcess) return null;

  // Structured array
  if (Array.isArray(buyingProcess)) {
    const structured = buyingProcess.filter(
      (item) => item && typeof item === "object",
    );

    if (!structured.length) return null;

    return tableFromArrayOfObjects(structured, lang);
  }

  // Structured object
  if (typeof buyingProcess === "object" && !Array.isArray(buyingProcess)) {
    return flatKeyValueTable(buyingProcess, lang);
  }

  // Narrative string → keep narrative, do not fabricate structure
  return null;
};
const looksLikeTimeSeries = (labels) => {
  if (!Array.isArray(labels) || labels.length < 2) return false;

  const normalized = labels.map((label) =>
    String(label || "")
      .trim()
      .toLowerCase(),
  );

  const timePatterns = [
    // Years
    /^\d{4}$/,

    // Year ranges / periods
    /^\d{4}\s*[-–]\s*\d{4}$/,

    // Quarter
    /^q[1-4]\s*\d{2,4}$/,
    /^\d{2,4}\s*q[1-4]$/,

    // Arabic quarter
    /^الربع\s+(الأول|الثاني|الثالث|الرابع|\d+)/,

    // English months
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)(uary|ruary|ch|il|e|y|ust|tember|ober|ember)?(\s+\d{4})?$/,

    // Arabic months
    /^(يناير|فبراير|مارس|أبريل|ابريل|مايو|يونيو|يوليو|أغسطس|اغسطس|سبتمبر|أكتوبر|اكتوبر|نوفمبر|ديسمبر)(\s+\d{4})?$/,
    // Month + year / dates
    /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/,
    /^\d{4}[\/\-]\d{1,2}$/,
    /^\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}$/,

    // Generic period labels
    /^year\s*\d+$/,
    /^month\s*\d+$/,
    /^period\s*\d+$/,
    /^السنة\s*\d+$/,
    /^الشهر\s*\d+$/,
    /^الفترة\s*\d+$/,
  ];

  const matches = normalized.filter((label) =>
    timePatterns.some((pattern) => pattern.test(label)),
  ).length;

  // Most labels should look chronological
  return matches >= Math.ceil(labels.length * 0.6);
};
const genericNumericChart = (value, label, lang, options = null) => {
  if (!value) return null;

  let labels = [];
  let values = [];

  // =====================================================
  // 1) ARRAY OF NUMBERS
  // =====================================================
  if (
    Array.isArray(value) &&
    value.length >= 2 &&
    value.every((item) => typeof item === "number")
  ) {
    labels = value.map((_, index) =>
      lang === "ar" ? `عنصر ${index + 1}` : `Item ${index + 1}`,
    );

    values = value;
  }

  // =====================================================
  // 2) ARRAY OF OBJECTS WITH LABEL + NUMERIC VALUE
  // =====================================================
  else if (
    Array.isArray(value) &&
    value.length >= 2 &&
    value.every(
      (item) => item && typeof item === "object" && !Array.isArray(item),
    )
  ) {
    const candidates = value
      .map((item) => {
        const labelValue =
          item.label ??
          item.name ??
          item.title ??
          item.category ??
          item.force ??
          item.level ??
          item.period ??
          item.year ??
          item.month ??
          item.stage ??
          item.item;

        const numericValue =
          item.value ??
          item.amount ??
          item.score ??
          item.total ??
          item.revenue ??
          item.cost ??
          item.price ??
          item.percentage ??
          item.percent ??
          item.rate ??
          item.count;

        const parsedNumericValue =
          typeof numericValue === "number"
            ? numericValue
            : Number(
                String(numericValue ?? "")
                  .replace(/,/g, "")
                  .trim(),
              );

        if (
          labelValue === undefined ||
          labelValue === null ||
          !Number.isFinite(parsedNumericValue)
        ) {
          return null;
        }

        return {
          label: String(labelValue),
          value: parsedNumericValue,
        };
      })
      .filter(Boolean);

    if (candidates.length < 2) return null;

    labels = candidates.map((item) => item.label);
    values = candidates.map((item) => item.value);
  }

  // =====================================================
  // 3) FLAT OBJECT WITH NUMERIC VALUES
  // =====================================================
  else if (typeof value === "object" && !Array.isArray(value)) {
    const numericEntries = Object.entries(value)
      .map(([key, itemValue]) => {
        const parsedValue =
          typeof itemValue === "number"
            ? itemValue
            : Number(
                String(itemValue ?? "")
                  .replace(/,/g, "")
                  .trim(),
              );

        if (!Number.isFinite(parsedValue)) {
          return null;
        }

        return [key, parsedValue];
      })
      .filter(Boolean);

    if (numericEntries.length < 2) return null;

    labels = numericEntries.map(([key]) => translateKey(key, lang));

    values = numericEntries.map(([, itemValue]) => itemValue);
  }

  if (values.length < 2) return null;
  if (lang === "ar") {
    labels = labels.map((label) => {
      const normalized = String(label || "")
        .trim()
        .toUpperCase();

      if (normalized === "TAM") {
        return "TAM — إجمالي السوق القابل للاستهداف";
      }

      if (normalized === "SAM") {
        return "SAM — السوق المتاح للخدمة";
      }

      if (normalized === "SOM") {
        return "SOM — السوق الممكن الاستحواذ عليه";
      }

      return label;
    });
  }
  // Do not chart invalid numeric values
  if (values.some((item) => !Number.isFinite(item))) {
    return null;
  }

  // Limit visual clutter
  labels = labels.slice(0, 12);
  values = values.slice(0, 12);
  const useLineChart = looksLikeTimeSeries(labels);
  const canvas = document.createElement("canvas");

  const canvasWidth = 1100;
  const canvasHeight = 430;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const chartLeft = 110;
  const chartRight = 45;
  const chartTop = 55;
  const chartBottom = 120;

  const chartWidth = canvasWidth - chartLeft - chartRight;

  const chartHeight = canvasHeight - chartTop - chartBottom;

  const maxValue =
    options?.maxScale && Number.isFinite(Number(options.maxScale))
      ? Number(options.maxScale)
      : Math.max(...values, 0);
  if (maxValue <= 0) return null;

  // =====================================================
  // GRID
  // =====================================================
  ctx.strokeStyle = "#E5E7EB";
  ctx.lineWidth = 1;

  const gridLines = 5;

  for (let i = 0; i <= gridLines; i++) {
    const y = chartTop + (chartHeight / gridLines) * i;

    ctx.beginPath();
    ctx.moveTo(chartLeft, y);
    ctx.lineTo(chartLeft + chartWidth, y);
    ctx.stroke();

    const gridValue = maxValue - (maxValue / gridLines) * i;

    ctx.fillStyle = "#6B7280";
    ctx.textAlign = "right";

    ctx.font =
      lang === "ar" ? `14px ${THEME.fontArabic}` : `14px ${THEME.fontBody}`;

    ctx.fillText(formatNumber(gridValue), chartLeft - 12, y + 5);
  }

  // =====================================================
  // BARS
  // =====================================================
  // =====================================================
  // SERIES
  // =====================================================
  const slotWidth = chartWidth / values.length;

  if (useLineChart) {
    // ===================================================
    // LINE CHART — chronological / sequential data
    // ===================================================
    const points = values.map((itemValue, index) => {
      const x =
        values.length === 1
          ? chartLeft + chartWidth / 2
          : chartLeft + (chartWidth / (values.length - 1)) * index;

      const y = chartTop + chartHeight - (itemValue / maxValue) * chartHeight;

      return {
        x,
        y,
        value: itemValue,
        label: labels[index],
      };
    });

    // Line
    ctx.strokeStyle = "#4472A8";
    ctx.lineWidth = 4;

    ctx.beginPath();

    points.forEach((point, index) => {
      if (index === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });

    ctx.stroke();

    // Points + labels
    points.forEach((point) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 6, 0, Math.PI * 2);

      ctx.fillStyle = "#1F3864";
      ctx.fill();

      // Value
      ctx.textAlign = "center";

      ctx.font =
        lang === "ar"
          ? `bold 14px ${THEME.fontArabic}`
          : `bold 14px ${THEME.fontBody}`;

      ctx.fillText(formatNumber(point.value), point.x, point.y - 13);

      // X label
      ctx.fillStyle = "#4B5563";

      ctx.font =
        lang === "ar" ? `14px ${THEME.fontArabic}` : `14px ${THEME.fontBody}`;

      const shortLabel =
        point.label.length > 18 ? `${point.label.slice(0, 16)}…` : point.label;

      ctx.fillText(shortLabel, point.x, chartTop + chartHeight + 28);
    });
  } else {
    // ===================================================
    // BAR CHART — categorical comparison
    // ===================================================
    const barWidth = Math.min(70, slotWidth * 0.58);

    values.forEach((itemValue, index) => {
      const barHeight = (itemValue / maxValue) * chartHeight;

      const x = chartLeft + slotWidth * index + (slotWidth - barWidth) / 2;

      const y = chartTop + chartHeight - barHeight;

      ctx.fillStyle = "#4472A8";

      ctx.fillRect(x, y, barWidth, barHeight);

      // Value
      ctx.fillStyle = "#1F3864";
      ctx.textAlign = "center";

      ctx.font =
        lang === "ar"
          ? `bold 14px ${THEME.fontArabic}`
          : `bold 14px ${THEME.fontBody}`;

      const displayedValue =
        options?.showAsScale && options?.maxScale
          ? `${formatNumber(itemValue)}/${formatNumber(options.maxScale)}`
          : formatNumber(itemValue);

      ctx.fillText(displayedValue, x + barWidth / 2, y - 8);
      // Category label
      ctx.save();

      ctx.translate(x + barWidth / 2, chartTop + chartHeight + 15);

      ctx.rotate(-Math.PI / 5);

      ctx.fillStyle = "#4B5563";
      ctx.textAlign = "right";

      ctx.font =
        lang === "ar" ? `14px ${THEME.fontArabic}` : `14px ${THEME.fontBody}`;

      const shortLabel =
        labels[index].length > 24
          ? `${labels[index].slice(0, 22)}…`
          : labels[index];

      ctx.fillText(shortLabel, 0, 0);

      ctx.restore();
    });
  }

  return canvasChartParagraph(canvas, 560, 220);
};
const formatNumber = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return String(value ?? "");
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(numericValue);
};
const genericKpiSnapshot = (value, lang) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const entries = Object.entries(value)
    .map(([key, itemValue]) => {
      let displayValue = null;

      if (typeof itemValue === "number" && Number.isFinite(itemValue)) {
        displayValue = formatNumber(itemValue);
      } else if (typeof itemValue === "string") {
        const trimmed = itemValue.trim();

        const numericLike = /^[-+]?\d[\d,]*(?:\.\d+)?\s*[%٪]?$/.test(trimmed);

        if (numericLike) {
          displayValue = trimmed;
        }
      }

      if (displayValue === null) {
        return null;
      }

      return {
        label: translateKey(key, lang),
        value: displayValue,
      };
    })
    .filter(Boolean)
    .slice(0, 6);

  if (entries.length < 2) {
    return null;
  }

  const cells = entries.map(
    (item) =>
      new TableCell({
        shading: {
          type: ShadingType.CLEAR,
          fill: THEME.kpiBg,
        },
        margins: {
          top: 160,
          bottom: 160,
          left: 140,
          right: 140,
        },
        children: [
          new Paragraph({
            bidirectional: lang === "ar",
            alignment: AlignmentType.CENTER,
            spacing: { after: 70 },
            children: [
              new TextRun({
                text: String(item.value),
                bold: true,
                color: "FFFFFF",
                size: 30,
                font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                rightToLeft: lang === "ar",
              }),
            ],
          }),

          new Paragraph({
            bidirectional: lang === "ar",
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: item.label,
                color: "FFFFFF",
                size: 18,
                font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                rightToLeft: lang === "ar",
              }),
            ],
          }),
        ],
      }),
  );

  const rows = [];

  for (let i = 0; i < cells.length; i += 3) {
    const rowCells = cells.slice(i, i + 3);

    while (rowCells.length < 3) {
      rowCells.push(
        new TableCell({
          children: [new Paragraph("")],
        }),
      );
    }

    rows.push(
      new TableRow({
        children: rowCells,
      }),
    );
  }

  return new Table({
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    rows,
  });
};
const genericSingleMetricCard = (rawKey, label, value, lang) => {
  let displayValue = null;

  // =====================================================
  // 1) REAL NUMBER
  // =====================================================
  if (typeof value === "number" && Number.isFinite(value)) {
    displayValue = formatNumber(value);
  }

  // =====================================================
  // 2) SIMPLE NUMERIC STRING
  //    examples:
  //    "85%"
  //    "4.5"
  //    "1,250"
  //    "SAR 250,000"
  // =====================================================
  else if (typeof value === "string") {
    const trimmed = value.trim();

    const looksNumeric =
      /^[^\p{L}]{0,12}[\d٠-٩][\d٠-٩,.\s]*[%٪]?[^\p{L}]{0,12}$/u.test(trimmed);

    if (!looksNumeric) return null;

    displayValue = trimmed;
  }

  if (displayValue === null) return null;

  const title = String(label || translateKey(rawKey, lang)).trim();

  if (!title) return null;

  return new Table({
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },

    rows: [
      new TableRow({
        children: [
          new TableCell({
            shading: {
              type: ShadingType.CLEAR,
              fill: THEME.kpiBg,
            },

            margins: {
              top: 180,
              bottom: 180,
              left: 180,
              right: 180,
            },

            children: [
              new Paragraph({
                bidirectional: lang === "ar",
                alignment: AlignmentType.CENTER,

                spacing: {
                  after: 80,
                },

                children: [
                  new TextRun({
                    text: title,
                    bold: true,
                    color: "FFFFFF",
                    size: 22,
                    font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                    rightToLeft: lang === "ar",
                  }),
                ],
              }),

              new Paragraph({
                bidirectional: lang === "ar",
                alignment: AlignmentType.CENTER,

                children: [
                  new TextRun({
                    text: String(displayValue),
                    bold: true,
                    color: "FFFFFF",
                    size: 34,
                    font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
                    rightToLeft: lang === "ar",
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
};
const genericBoundedMetricVisual = (rawKey, label, value, lang) => {
  const key = String(rawKey || "").toLowerCase();

  let numericValue = null;
  let maxValue = null;
  let suffix = "";

  // =====================================================
  // 1) STRING PERCENTAGE: "82%" / "82 %"
  // =====================================================
  if (typeof value === "string") {
    const match = value.trim().match(/^(-?\d+(?:\.\d+)?)\s*[%٪]$/);

    if (!match) return null;

    numericValue = Number(match[1]);
    maxValue = 100;
    suffix = "%";
  }

  // =====================================================
  // 2) OBJECT WITH EXPLICIT VALUE + MAX
  // examples:
  // { value: 4.2, max: 5 }
  // { score: 78, maximum: 100 }
  // { rating: 7, scale_max: 10 }
  // =====================================================
  else if (value && typeof value === "object" && !Array.isArray(value)) {
    const candidateValue =
      value.value ??
      value.score ??
      value.rating ??
      value.percentage ??
      value.percent;

    const candidateMax =
      value.max ??
      value.maximum ??
      value.max_value ??
      value.scale_max ??
      value.out_of;

    if (
      typeof candidateValue !== "number" ||
      typeof candidateMax !== "number" ||
      !Number.isFinite(candidateValue) ||
      !Number.isFinite(candidateMax) ||
      candidateMax <= 0
    ) {
      return null;
    }

    numericValue = candidateValue;
    maxValue = candidateMax;

    if (key.includes("percent") || key.includes("percentage")) {
      suffix = "%";
    }
  }

  // =====================================================
  // 3) PLAIN NUMBER
  // Only accept when key explicitly says percentage.
  // Never assume a raw score scale.
  // =====================================================
  else if (
    typeof value === "number" &&
    Number.isFinite(value) &&
    (key.includes("percentage") || key.includes("percent"))
  ) {
    numericValue = value;
    maxValue = 100;
    suffix = "%";
  }

  if (numericValue === null || maxValue === null) {
    return null;
  }

  const ratio = numericValue / maxValue;

  if (!Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
    return null;
  }

  const canvas = document.createElement("canvas");

  const canvasWidth = 1000;
  const canvasHeight = 190;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Title
  ctx.fillStyle = "#1F3864";
  ctx.textAlign = lang === "ar" ? "right" : "left";

  ctx.font =
    lang === "ar"
      ? `bold 23px ${THEME.fontArabic}`
      : `bold 23px ${THEME.fontBody}`;

  ctx.fillText(
    String(label || translateKey(rawKey, lang)),
    lang === "ar" ? 950 : 50,
    42,
  );

  // Background bar
  const barX = 50;
  const barY = 85;
  const barWidth = 900;
  const barHeight = 38;

  ctx.fillStyle = "#E5E7EB";
  ctx.fillRect(barX, barY, barWidth, barHeight);

  // Filled portion
  ctx.fillStyle = "#4472A8";
  ctx.fillRect(barX, barY, barWidth * ratio, barHeight);

  // Value
  ctx.fillStyle = "#1F3864";
  ctx.textAlign = "center";

  ctx.font =
    lang === "ar"
      ? `bold 21px ${THEME.fontArabic}`
      : `bold 21px ${THEME.fontBody}`;

  const displayedValue = suffix
    ? `${formatNumber(numericValue)}${suffix}`
    : `${formatNumber(numericValue)} / ${formatNumber(maxValue)}`;

  ctx.fillText(displayedValue, canvasWidth / 2, 160);

  return canvasChartParagraph(canvas, 560, 106);
};
const genericProcessFlowChart = (value, lang) => {
  let source = value;

  // =====================================================
  // EXPLICIT NUMBERED PROCESS INSIDE NARRATIVE TEXT
  // =====================================================
  if (typeof value === "string") {
    const normalizedValue = value.replace(
      /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g,
      "",
    );
    const firstPart =
      normalizedValue.split(
        lang === "ar"
          ? /لماذا[؟?]|كيف يحدث|ما الذي يعنيه|ماذا يعني/i
          : /why[?]?|how does|what does this mean/i,
      )[0] || value;

    const matches = [
      ...firstPart.matchAll(/\(\s*(\d+)\s*\)\s*([\s\S]*?)(?=\(\s*\d+\s*\)|$)/g),
    ];

    if (matches.length < 2) {
      return null;
    }

    source = matches.map((match) => ({
      step: Number(match[1]),

      title: String(match[2] || "")
        .trim()
        .replace(/^[،,:؛\-\s]+/, "")
        .replace(/[،؛,.]+$/, ""),
    }));
  }

  if (!Array.isArray(source) || source.length < 2) {
    return null;
  }

  const steps = source
    .map((item, index) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return null;
      }

      // There must be explicit process-like structure.
      const explicitStep =
        item.step ?? item.stage ?? item.phase ?? item.order ?? item.sequence;

      if (explicitStep === undefined || explicitStep === null) {
        return null;
      }

      const title =
        item.title ??
        item.name ??
        item.label ??
        item.activity ??
        item.action ??
        item.task;

      if (!title) return null;

      const description =
        item.description ?? item.details ?? item.summary ?? item.note ?? "";

      return {
        order: typeof explicitStep === "number" ? explicitStep : index + 1,

        title: String(title),
        description: String(description || ""),
      };
    })
    .filter(Boolean);

  // Most of the array must genuinely look like a process.
  if (steps.length < 2 || steps.length < Math.ceil(source.length * 0.7)) {
    return null;
  }
  steps.sort((a, b) => a.order - b.order);

  const visibleSteps = steps.slice(0, 8);

  const canvas = document.createElement("canvas");

  const canvasWidth = 1200;
  const canvasHeight = 300;

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  ctx.direction = lang === "ar" ? "rtl" : "ltr";

  const marginX = 35;
  const gap = 22;

  const availableWidth =
    canvasWidth - marginX * 2 - gap * (visibleSteps.length - 1);

  const boxWidth = Math.max(
    120,
    Math.min(190, availableWidth / visibleSteps.length),
  );

  const actualGap =
    visibleSteps.length > 1
      ? (canvasWidth - marginX * 2 - boxWidth * visibleSteps.length) /
        (visibleSteps.length - 1)
      : 0;

  const boxHeight = 145;
  const y = 72;

  const wrapText = (text, maxWidth, maxLines = 3) => {
    const words = String(text || "")
      .trim()
      .split(/\s+/);

    const lines = [];
    let current = "";

    words.forEach((word) => {
      const test = current ? `${current} ${word}` : word;

      if (ctx.measureText(test).width <= maxWidth) {
        current = test;
      } else {
        if (current) lines.push(current);
        current = word;
      }
    });

    if (current) lines.push(current);

    if (lines.length > maxLines) {
      const trimmed = lines.slice(0, maxLines);

      trimmed[maxLines - 1] = `${trimmed[maxLines - 1].replace(/[.…]+$/, "")}…`;

      return trimmed;
    }

    return lines;
  };

  visibleSteps.forEach((step, index) => {
    const visualIndex = lang === "ar" ? visibleSteps.length - 1 - index : index;

    const x = marginX + visualIndex * (boxWidth + actualGap);

    // Box
    ctx.fillStyle = "#F7F9FC";
    ctx.strokeStyle = "#4472A8";
    ctx.lineWidth = 3;

    ctx.beginPath();

    if (typeof ctx.roundRect === "function") {
      ctx.roundRect(x, y, boxWidth, boxHeight, 14);
    } else {
      ctx.rect(x, y, boxWidth, boxHeight);
    }

    ctx.fill();
    ctx.stroke();

    // Number
    ctx.beginPath();

    ctx.arc(x + 22, y + 22, 15, 0, Math.PI * 2);

    ctx.fillStyle = "#1F3864";
    ctx.fill();

    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";

    ctx.font =
      lang === "ar"
        ? `bold 16px ${THEME.fontArabic}`
        : `bold 16px ${THEME.fontBody}`;

    ctx.fillText(String(index + 1), x + 22, y + 28);

    // Title
    ctx.fillStyle = "#1F3864";

    ctx.font =
      lang === "ar"
        ? `bold 19px ${THEME.fontArabic}`
        : `bold 19px ${THEME.fontBody}`;

    const titleLines = wrapText(step.title, boxWidth - 22, 2);

    titleLines.forEach((line, lineIndex) => {
      ctx.fillText(line, x + boxWidth / 2, y + 55 + lineIndex * 21);
    });

    // Description
    if (step.description) {
      ctx.fillStyle = "#4B5563";

      ctx.font =
        lang === "ar" ? `14px ${THEME.fontArabic}` : `14px ${THEME.fontBody}`;

      const descLines = wrapText(step.description, boxWidth - 18, 3);

      const descY = y + 94 + Math.max(0, titleLines.length - 1) * 10;

      descLines.forEach((line, lineIndex) => {
        ctx.fillText(line, x + boxWidth / 2, descY + lineIndex * 17);
      });
    }

    // Arrow
    if (index < visibleSteps.length - 1) {
      const arrowY = y + boxHeight / 2;

      const nextVisualIndex =
        lang === "ar" ? visibleSteps.length - 1 - (index + 1) : index + 1;

      const nextX = marginX + nextVisualIndex * (boxWidth + actualGap);

      const fromX = lang === "ar" ? x - 5 : x + boxWidth + 5;

      const toX = lang === "ar" ? nextX + boxWidth + 5 : nextX - 5;

      ctx.strokeStyle = "#9CA3AF";
      ctx.lineWidth = 3;

      ctx.beginPath();
      ctx.moveTo(fromX, arrowY);
      ctx.lineTo(toX, arrowY);
      ctx.stroke();

      ctx.beginPath();

      if (lang === "ar") {
        ctx.moveTo(toX + 9, arrowY - 7);
        ctx.lineTo(toX, arrowY);
        ctx.lineTo(toX + 9, arrowY + 7);
      } else {
        ctx.moveTo(toX - 9, arrowY - 7);
        ctx.lineTo(toX, arrowY);
        ctx.lineTo(toX - 9, arrowY + 7);
      }

      ctx.stroke();
    }
  });

  return canvasChartParagraph(canvas, 560, 140);
};
const renderVisualsForValue = (rawKey, label, value, level, lang, nodes) => {
  if (value === null || value === undefined) return false;
  const keyNorm = String(rawKey || "").toLowerCase();
  if (keyNorm === "overall_risk_matrix") {
    return true;
  }
  if (
    keyNorm === "market_growth_trend" ||
    keyNorm === "market_supply_comparison" ||
    keyNorm === "market_sizing_chart" ||
    keyNorm === "porters_five_forces_chart" ||
    keyNorm === "risk_level_distribution_chart"
  ) {
    const numericChart = genericNumericChart(
      value,
      label,
      lang,
      keyNorm === "porters_five_forces_chart"
        ? { maxScale: 5, showAsScale: true }
        : null,
    );
    if (numericChart) {
      nodes.push(sectionHeading(label, level, lang));
      nodes.push(numericChart);
      nodes.push(emptyLine());
      return true;
    }
  }
  // =====================================================
  // SINGLE NUMERIC METRIC
  // =====================================================
  // =====================================================
  // EXPLICIT PERCENTAGE / BOUNDED SCORE
  // =====================================================
  const boundedMetric = genericBoundedMetricVisual(rawKey, label, value, lang);

  if (boundedMetric) {
    nodes.push(boundedMetric);
    nodes.push(emptyLine());
    return true;
  }
  const metricCard = genericSingleMetricCard(rawKey, label, value, lang);

  if (metricCard) {
    nodes.push(metricCard);
    nodes.push(emptyLine());
    return true;
  }
  // =====================================================
  // GENERIC PROCESS / WORKFLOW
  // =====================================================

  let processFlow = null;

  // Generic router only converts explicitly structured arrays.
  // Narrative strings stay narrative unless a dedicated block
  // intentionally sends them to the process parser.
  if (Array.isArray(value)) {
    processFlow = genericProcessFlowChart(value, lang);
  }

  if (processFlow) {
    nodes.push(processFlow);
    nodes.push(emptyLine());
    return true;
  }
  // =====================================================
  // 1) ARRAY OF OBJECTS → GENERIC TABLE
  // =====================================================
  if (
    Array.isArray(value) &&
    value.length >= 2 &&
    value.every(
      (item) => item && typeof item === "object" && !Array.isArray(item),
    )
  ) {
    const table = tableFromArrayOfObjects(value, lang);

    if (table) {
      nodes.push(sectionHeading(label, level, lang));
      nodes.push(table);
      nodes.push(emptyLine());
      return true;
    }
  }
  // =====================================================
  // COMPACT COMPARISON CARDS
  // =====================================================
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value)
      .filter(
        ([key, itemValue]) =>
          !META_KEYS.has(String(key).toLowerCase()) &&
          (typeof itemValue === "string" || typeof itemValue === "number"),
      )
      .map(([key, itemValue]) => ({
        title: translateKey(key, lang),
        value: String(itemValue).trim(),
      }))
      .filter((item) => item.value && item.value.length <= 80);

    if (entries.length >= 2 && entries.length <= 4) {
      const cards = comparisonCards(entries, lang);

      if (cards) {
        nodes.push(sectionHeading(label, level, lang));

        nodes.push(cards);
        nodes.push(emptyLine());

        return true;
      }
    }
  }
  // =====================================================
  // 2) FLAT OBJECT → GENERIC KEY / VALUE TABLE
  // =====================================================
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value).filter(
      ([, itemValue]) =>
        itemValue !== null &&
        itemValue !== undefined &&
        typeof itemValue !== "object",
    );

    const objectKeys = Object.keys(value);

    const isFlat =
      objectKeys.length >= 2 && entries.length === objectKeys.length;

    if (isFlat) {
      const table = flatKeyValueTable(value, lang);

      if (table) {
        nodes.push(sectionHeading(label, level, lang));
        nodes.push(table);
        nodes.push(emptyLine());
        return true;
      }
    }
  }

  // =====================================================
  // 3) NUMERIC ARRAY → RESERVED FOR GENERIC CHART ENGINE
  // =====================================================
  // =====================================================
  // 3) GENERIC NUMERIC VISUAL
  // =====================================================
  const numericChart = genericNumericChart(value, label, lang);

  if (numericChart) {
    nodes.push(sectionHeading(label, level, lang));

    nodes.push(numericChart);
    nodes.push(emptyLine());

    return true;
  }

  // =====================================================
  // 4) NARRATIVE STRING
  // Do not infer tables/charts from unrestricted prose.
  // =====================================================
  if (typeof value === "string") {
    return false;
  }

  return false;
};
const competitorComparisonTableFromText = (value, lang) => {
  if (typeof value !== "string" || !value.trim()) return null;

  const text = value.trim();

  const parts = text
    .split(/\n+|(?=\d+\.\s)|(?=•\s)/)
    .map((item) =>
      item
        .replace(/^\d+\.\s*|^•\s*/, "")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter(Boolean);

  const competitors = parts
    .map((item) => {
      const nameMatch = item.match(/^(?:شركة\s+)?([^\-–—:]{2,80})\s*[\-–—:]/);

      if (!nameMatch) return null;

      return {
        name: nameMatch[1].trim(),
        details: item.slice(nameMatch[0].length).trim(),
      };
    })
    .filter(Boolean)
    .slice(0, 10);

  if (competitors.length < 2) return null;

  return professionalTable(
    [
      lang === "ar" ? "المنافس" : "Competitor",
      lang === "ar"
        ? "الخدمات، نقاط القوة، نقاط الضعف والتموضع"
        : "Services, Strengths, Weaknesses & Positioning",
    ],
    competitors.map((item) => [item.name, item.details]),
    lang,
    [1, 3],
  );
};
const technicalProcessTimeFlow = (value, lang) => {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }

  const nodes = [];

  const getText = (item, keys) => {
    for (const key of keys) {
      if (item && item[key] !== undefined && item[key] !== null) {
        const text = String(item[key]).trim();

        if (text) {
          return text;
        }
      }
    }

    return "";
  };

  const cleanItems = value
    .filter((item) => item && typeof item === "object" && !Array.isArray(item))
    .map((item) => ({
      stage: getText(item, [
        "stage_or_factor",
        "stage_or_activity",
        "stage",
        "factor",
        "activity",
      ]),

      impact: getText(item, [
        "capacity_impact",
        "effect_on_capacity",
        "capacity_effect",
        "operational_impact",
        "effect",
      ]),

      verification: getText(item, [
        "verification_required",
        "validation_required",
        "recommended_validation",
        "validation_method",
        "required_verification",
      ]),
    }))
    .filter((item) => item.stage || item.impact || item.verification);

  if (!cleanItems.length) {
    return [];
  }

  cleanItems.forEach((item, index) => {
    const boxLines = [];

    if (item.impact) {
      boxLines.push(
        `${
          lang === "ar"
            ? "الأثر على الطاقة الإنتاجية"
            : "Impact on Production Capacity"
        }: ${item.impact}`,
      );
    }

    if (item.verification) {
      boxLines.push(
        `${
          lang === "ar" ? "التحقق المطلوب" : "Required Verification"
        }: ${item.verification}`,
      );
    }

    nodes.push(
      highlightBox(
        item.stage ||
          (lang === "ar" ? `المرحلة ${index + 1}` : `Stage ${index + 1}`),
        boxLines,
        lang,
        index % 2 === 0 ? "F4F7FB" : "EEF2F8",
        THEME.secondary,
      ),
    );

    if (index < cleanItems.length - 1) {
      nodes.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: {
            before: 70,
            after: 70,
          },
          children: [
            new TextRun({
              text: "↓",
              bold: true,
              size: 30,
              color: THEME.secondary,
              font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
            }),
          ],
        }),
      );
    }
  });

  return nodes;
};
// =========================================================
// FACILITY LAYOUT — DYNAMIC CONCEPTUAL DIAGRAM
// =========================================================
// =========================================================
// FACILITY LAYOUT — PREMIUM DYNAMIC INFOGRAPHIC
// =========================================================
const facilityLayoutConceptDiagram = (value, lang) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const functionalZones = Array.isArray(value.functional_zones)
    ? value.functional_zones.filter((item) => item && typeof item === "object")
    : [];

  const processFlow = Array.isArray(value.process_flow)
    ? [...value.process_flow]
        .filter((item) => item && typeof item === "object")
        .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0))
    : [];

  const utilityDistribution = Array.isArray(value.utility_distribution)
    ? value.utility_distribution.filter(
        (item) => item && typeof item === "object",
      )
    : [];

  if (functionalZones.length < 2 && processFlow.length < 2) {
    return null;
  }

  // =========================================================
  // CANVAS
  // =========================================================
  const canvas = document.createElement("canvas");

  const W = 1600;
  const H = 1080;

  canvas.width = W;
  canvas.height = H;

  const ctx = canvas.getContext("2d");

  if (!ctx) return null;

  ctx.direction = lang === "ar" ? "rtl" : "ltr";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // =========================================================
  // COLORS
  // =========================================================
  const C = {
    navy: "#15375A",
    blue: "#1F6FAE",
    cyan: "#2CA6C7",
    teal: "#168A93",
    aqua: "#DDF5F7",
    paleBlue: "#EAF4FB",
    paleTeal: "#E7F7F5",
    line: "#BFD7E8",
    darkText: "#15324A",
    muted: "#6A7F91",
    white: "#FFFFFF",
    lightBg: "#F8FBFD",
    green: "#4A9A74",
    amber: "#C89A42",
    red: "#C55C5C",
  };

  // =========================================================
  // BACKGROUND
  // =========================================================
  const bg = ctx.createLinearGradient(0, 0, W, H);

  bg.addColorStop(0, "#FFFFFF");
  bg.addColorStop(0.55, "#F8FCFE");
  bg.addColorStop(1, "#EEF8FA");

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // =========================================================
  // HELPERS
  // =========================================================
  const roundedPath = (x, y, w, h, r = 20) => {
    const radius = Math.min(r, w / 2, h / 2);

    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const drawCard = (
    x,
    y,
    w,
    h,
    fill = C.white,
    border = C.line,
    radius = 24,
    shadow = true,
  ) => {
    ctx.save();

    if (shadow) {
      ctx.shadowColor = "rgba(21,55,90,0.14)";
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 8;
    }

    roundedPath(x, y, w, h, radius);

    ctx.fillStyle = fill;
    ctx.fill();

    ctx.shadowColor = "transparent";

    ctx.strokeStyle = border;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();
  };

  const wrapText = (text, maxWidth, maxLines = 2) => {
    const words = String(text || "")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .filter(Boolean);

    const lines = [];
    let line = "";

    words.forEach((word) => {
      const test = line ? `${line} ${word}` : word;

      if (ctx.measureText(test).width <= maxWidth) {
        line = test;
      } else {
        if (line) lines.push(line);
        line = word;
      }
    });

    if (line) lines.push(line);

    if (lines.length > maxLines) {
      const trimmed = lines.slice(0, maxLines);
      trimmed[maxLines - 1] = `${trimmed[maxLines - 1].replace(/…$/, "")}…`;

      return trimmed;
    }

    return lines;
  };

  const drawWrappedCentered = (
    text,
    centerX,
    startY,
    maxWidth,
    lineHeight,
    maxLines = 2,
  ) => {
    const lines = wrapText(text, maxWidth, maxLines);

    lines.forEach((line, index) => {
      ctx.fillText(line, centerX, startY + index * lineHeight);
    });
  };

  const drawArrowHead = (x, y, angle, color = C.blue, size = 12) => {
    ctx.save();

    ctx.translate(x, y);
    ctx.rotate(angle);

    ctx.fillStyle = color;

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-size, -size * 0.58);
    ctx.lineTo(-size, size * 0.58);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  };

  const drawConnector = (from, to, color = C.blue) => {
    if (!from || !to) return;

    const dx = to.x - from.x;
    const dy = to.y - from.y;

    ctx.save();

    ctx.strokeStyle = color;
    ctx.lineWidth = 5;

    ctx.beginPath();

    if (Math.abs(dy) < 20) {
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
    } else {
      const middleY = (from.y + to.y) / 2;

      ctx.moveTo(from.x, from.y);

      ctx.bezierCurveTo(from.x, middleY, to.x, middleY, to.x, to.y);
    }

    ctx.stroke();

    const angle = Math.atan2(to.y - from.y, to.x - from.x);

    drawArrowHead(to.x, to.y, angle, color, 14);

    ctx.restore();
  };

  // =========================================================
  // ICON CLASSIFIER
  // =========================================================
  const classify = (text) => {
    const s = String(text || "").toLowerCase();

    if (/حجر|استقبال|إصبعيات|quarantine|reception|intake/.test(s)) {
      return "intake";
    }

    if (/حوض|حضانة|تسمين|تربية|أسماك|fish|tank|grow|nursery/.test(s)) {
      return "tank";
    }

    if (/مياه|ماء|ترشيح|معالجة|مضخ|ras|water|filter|pump/.test(s)) {
      return "water";
    }

    if (/جودة|مختبر|فحص|تحليل|quality|lab|testing/.test(s)) {
      return "lab";
    }

    if (/تبريد|تعبئة|تغليف|cold|cool|pack/.test(s)) {
      return "cold";
    }

    if (/مستودع|تخزين|أعلاف|مخزون|warehouse|storage|feed/.test(s)) {
      return "storage";
    }

    if (/صيانة|maintenance|workshop/.test(s)) {
      return "maintenance";
    }

    if (/نقل|تحميل|توزيع|شحن|transport|loading|dispatch/.test(s)) {
      return "transport";
    }

    if (/إدارة|مكتب|تحكم|إداري|office|admin|control/.test(s)) {
      return "office";
    }

    if (/نفايات|مخلفات|حمأة|waste|sludge/.test(s)) {
      return "waste";
    }

    if (/سلامة|طوارئ|safety|emergency/.test(s)) {
      return "safety";
    }

    if (/حصاد|فرز|harvest|sorting/.test(s)) {
      return "harvest";
    }

    return "process";
  };

  // =========================================================
  // ICON DRAWINGS
  // =========================================================
  const drawTankIcon = (cx, cy, size) => {
    const w = size * 0.92;
    const h = size * 0.5;

    ctx.save();

    ctx.fillStyle = "#DDF5FB";
    ctx.strokeStyle = C.blue;
    ctx.lineWidth = 3;

    ctx.beginPath();
    ctx.ellipse(cx, cy - h * 0.2, w / 2, h / 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(cx - w / 2, cy - h * 0.2);
    ctx.lineTo(cx - w / 2, cy + h * 0.42);
    ctx.quadraticCurveTo(cx, cy + h * 0.65, cx + w / 2, cy + h * 0.42);
    ctx.lineTo(cx + w / 2, cy - h * 0.2);
    ctx.stroke();

    ctx.fillStyle = C.cyan;
    ctx.globalAlpha = 0.2;

    ctx.beginPath();
    ctx.ellipse(cx, cy + h * 0.16, w * 0.46, h * 0.18, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 1;

    // fish
    ctx.fillStyle = C.blue;

    [-0.22, 0.08, 0.28].forEach((offset, index) => {
      const fx = cx + offset * w;
      const fy = cy + (index % 2 ? 4 : 12);

      ctx.beginPath();
      ctx.ellipse(fx, fy, 9, 5, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(fx - 9, fy);
      ctx.lineTo(fx - 16, fy - 6);
      ctx.lineTo(fx - 16, fy + 6);
      ctx.closePath();
      ctx.fill();
    });

    ctx.restore();
  };

  const drawWaterIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.blue;
    ctx.lineWidth = 5;

    // pipe
    ctx.beginPath();
    ctx.moveTo(cx - size * 0.42, cy + size * 0.18);
    ctx.lineTo(cx - size * 0.1, cy + size * 0.18);
    ctx.lineTo(cx - size * 0.1, cy - size * 0.18);
    ctx.lineTo(cx + size * 0.4, cy - size * 0.18);
    ctx.stroke();

    // water drop
    ctx.fillStyle = C.cyan;

    ctx.beginPath();
    ctx.moveTo(cx + size * 0.22, cy - size * 0.42);
    ctx.bezierCurveTo(
      cx + size * 0.06,
      cy - size * 0.14,
      cx + size * 0.06,
      cy + size * 0.12,
      cx + size * 0.22,
      cy + size * 0.22,
    );
    ctx.bezierCurveTo(
      cx + size * 0.38,
      cy + size * 0.12,
      cx + size * 0.38,
      cy - size * 0.14,
      cx + size * 0.22,
      cy - size * 0.42,
    );
    ctx.fill();

    ctx.restore();
  };

  const drawStorageIcon = (cx, cy, size) => {
    ctx.save();

    const w = size * 0.9;
    const h = size * 0.65;

    ctx.fillStyle = "#EDF4F8";
    ctx.strokeStyle = C.navy;
    ctx.lineWidth = 3;

    ctx.fillRect(cx - w / 2, cy - h / 2, w, h);
    ctx.strokeRect(cx - w / 2, cy - h / 2, w, h);

    ctx.fillStyle = "#D9E6ED";

    ctx.fillRect(cx - w * 0.17, cy - h * 0.15, w * 0.34, h * 0.47);

    ctx.strokeRect(cx - w * 0.17, cy - h * 0.15, w * 0.34, h * 0.47);

    ctx.beginPath();
    ctx.moveTo(cx - w / 2, cy - h / 2);
    ctx.lineTo(cx, cy - h * 0.78);
    ctx.lineTo(cx + w / 2, cy - h / 2);
    ctx.stroke();

    ctx.restore();
  };

  const drawLabIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.teal;
    ctx.lineWidth = 4;

    ctx.beginPath();
    ctx.moveTo(cx - size * 0.12, cy - size * 0.4);
    ctx.lineTo(cx - size * 0.12, cy - size * 0.06);
    ctx.lineTo(cx - size * 0.34, cy + size * 0.35);
    ctx.quadraticCurveTo(
      cx,
      cy + size * 0.5,
      cx + size * 0.34,
      cy + size * 0.35,
    );
    ctx.lineTo(cx + size * 0.12, cy - size * 0.06);
    ctx.lineTo(cx + size * 0.12, cy - size * 0.4);
    ctx.stroke();

    ctx.fillStyle = "#BCECF0";

    ctx.beginPath();
    ctx.moveTo(cx - size * 0.27, cy + size * 0.22);
    ctx.lineTo(cx + size * 0.27, cy + size * 0.22);
    ctx.quadraticCurveTo(
      cx,
      cy + size * 0.44,
      cx - size * 0.27,
      cy + size * 0.22,
    );
    ctx.fill();

    ctx.restore();
  };

  const drawColdIcon = (cx, cy, size) => {
    ctx.save();

    ctx.fillStyle = "#EDF8FC";
    ctx.strokeStyle = C.blue;
    ctx.lineWidth = 3;

    ctx.fillRect(cx - size * 0.38, cy - size * 0.31, size * 0.76, size * 0.62);
    ctx.strokeRect(
      cx - size * 0.38,
      cy - size * 0.31,
      size * 0.76,
      size * 0.62,
    );

    ctx.strokeStyle = C.cyan;
    ctx.lineWidth = 4;

    for (let angle = 0; angle < Math.PI; angle += Math.PI / 3) {
      ctx.beginPath();
      ctx.moveTo(
        cx - Math.cos(angle) * size * 0.23,
        cy - Math.sin(angle) * size * 0.23,
      );
      ctx.lineTo(
        cx + Math.cos(angle) * size * 0.23,
        cy + Math.sin(angle) * size * 0.23,
      );
      ctx.stroke();
    }

    ctx.restore();
  };

  const drawMaintenanceIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.amber;
    ctx.lineWidth = 6;

    ctx.beginPath();
    ctx.arc(
      cx - size * 0.08,
      cy - size * 0.08,
      size * 0.2,
      0.55,
      Math.PI * 1.8,
    );
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(cx + size * 0.05, cy + size * 0.04);
    ctx.lineTo(cx + size * 0.35, cy + size * 0.34);
    ctx.stroke();

    ctx.fillStyle = C.amber;

    ctx.beginPath();
    ctx.arc(cx + size * 0.38, cy + size * 0.37, size * 0.09, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  };

  const drawTransportIcon = (cx, cy, size) => {
    ctx.save();

    ctx.fillStyle = C.paleBlue;
    ctx.strokeStyle = C.blue;
    ctx.lineWidth = 3;

    ctx.fillRect(cx - size * 0.43, cy - size * 0.23, size * 0.55, size * 0.4);
    ctx.strokeRect(cx - size * 0.43, cy - size * 0.23, size * 0.55, size * 0.4);

    ctx.beginPath();
    ctx.moveTo(cx + size * 0.12, cy - size * 0.14);
    ctx.lineTo(cx + size * 0.31, cy - size * 0.14);
    ctx.lineTo(cx + size * 0.43, cy + size * 0.03);
    ctx.lineTo(cx + size * 0.43, cy + size * 0.17);
    ctx.lineTo(cx + size * 0.12, cy + size * 0.17);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = C.navy;

    [-0.25, 0.28].forEach((offset) => {
      ctx.beginPath();
      ctx.arc(
        cx + size * offset,
        cy + size * 0.22,
        size * 0.09,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    });

    ctx.restore();
  };

  const drawOfficeIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.navy;
    ctx.lineWidth = 3;
    ctx.fillStyle = "#ECF3F8";

    ctx.fillRect(cx - size * 0.38, cy - size * 0.34, size * 0.76, size * 0.67);
    ctx.strokeRect(
      cx - size * 0.38,
      cy - size * 0.34,
      size * 0.76,
      size * 0.67,
    );

    ctx.fillStyle = C.blue;

    const windowSize = size * 0.12;

    [-0.2, 0, 0.2].forEach((ox) => {
      [-0.14, 0.08].forEach((oy) => {
        ctx.fillRect(
          cx + size * ox - windowSize / 2,
          cy + size * oy - windowSize / 2,
          windowSize,
          windowSize,
        );
      });
    });

    ctx.restore();
  };

  const drawWasteIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.green;
    ctx.lineWidth = 4;

    ctx.beginPath();
    ctx.moveTo(cx - size * 0.25, cy - size * 0.22);
    ctx.lineTo(cx + size * 0.25, cy - size * 0.22);
    ctx.lineTo(cx + size * 0.18, cy + size * 0.35);
    ctx.lineTo(cx - size * 0.18, cy + size * 0.35);
    ctx.closePath();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(cx - size * 0.3, cy - size * 0.3);
    ctx.lineTo(cx + size * 0.3, cy - size * 0.3);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(cx, cy + size * 0.04, size * 0.12, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  };

  const drawSafetyIcon = (cx, cy, size) => {
    ctx.save();

    ctx.fillStyle = "#F8E8E8";
    ctx.strokeStyle = C.red;
    ctx.lineWidth = 4;

    ctx.beginPath();
    ctx.moveTo(cx, cy - size * 0.42);
    ctx.lineTo(cx + size * 0.34, cy - size * 0.2);
    ctx.lineTo(cx + size * 0.26, cy + size * 0.28);
    ctx.lineTo(cx, cy + size * 0.43);
    ctx.lineTo(cx - size * 0.26, cy + size * 0.28);
    ctx.lineTo(cx - size * 0.34, cy - size * 0.2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(cx, cy - size * 0.2);
    ctx.lineTo(cx, cy + size * 0.1);
    ctx.stroke();

    ctx.fillStyle = C.red;

    ctx.beginPath();
    ctx.arc(cx, cy + size * 0.22, size * 0.035, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  };

  const drawHarvestIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.blue;
    ctx.lineWidth = 3;

    ctx.beginPath();
    ctx.arc(cx, cy + size * 0.08, size * 0.32, 0, Math.PI);
    ctx.stroke();

    for (let i = -2; i <= 2; i++) {
      ctx.beginPath();
      ctx.moveTo(cx + i * size * 0.1, cy + size * 0.08);
      ctx.lineTo(cx + i * size * 0.06, cy + size * 0.33);
      ctx.stroke();
    }

    ctx.fillStyle = C.teal;

    ctx.beginPath();
    ctx.ellipse(
      cx,
      cy - size * 0.08,
      size * 0.19,
      size * 0.1,
      0,
      0,
      Math.PI * 2,
    );
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(cx - size * 0.18, cy - size * 0.08);
    ctx.lineTo(cx - size * 0.32, cy - size * 0.18);
    ctx.lineTo(cx - size * 0.32, cy + size * 0.02);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  };

  const drawProcessIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.teal;
    ctx.lineWidth = 4;

    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.27, 0, Math.PI * 2);
    ctx.stroke();

    for (let i = 0; i < 8; i++) {
      const a = (Math.PI * 2 * i) / 8;

      ctx.beginPath();
      ctx.moveTo(
        cx + Math.cos(a) * size * 0.29,
        cy + Math.sin(a) * size * 0.29,
      );
      ctx.lineTo(
        cx + Math.cos(a) * size * 0.43,
        cy + Math.sin(a) * size * 0.43,
      );
      ctx.stroke();
    }

    ctx.fillStyle = C.cyan;

    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.09, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  };

  const drawIntakeIcon = (cx, cy, size) => {
    ctx.save();

    ctx.strokeStyle = C.blue;
    ctx.lineWidth = 3;

    ctx.fillStyle = "#E8F5FB";

    ctx.beginPath();
    ctx.ellipse(
      cx,
      cy + size * 0.04,
      size * 0.34,
      size * 0.25,
      0,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = C.teal;

    [-0.12, 0.1].forEach((offset) => {
      const fx = cx + size * offset;

      ctx.beginPath();
      ctx.ellipse(
        fx,
        cy + size * 0.05,
        size * 0.1,
        size * 0.055,
        0,
        0,
        Math.PI * 2,
      );
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(fx - size * 0.09, cy + size * 0.05);
      ctx.lineTo(fx - size * 0.17, cy - size * 0.01);
      ctx.lineTo(fx - size * 0.17, cy + size * 0.11);
      ctx.closePath();
      ctx.fill();
    });

    ctx.restore();
  };

  const drawIcon = (kind, cx, cy, size = 80) => {
    switch (kind) {
      case "tank":
        drawTankIcon(cx, cy, size);
        break;

      case "water":
        drawWaterIcon(cx, cy, size);
        break;

      case "lab":
        drawLabIcon(cx, cy, size);
        break;

      case "cold":
        drawColdIcon(cx, cy, size);
        break;

      case "storage":
        drawStorageIcon(cx, cy, size);
        break;

      case "maintenance":
        drawMaintenanceIcon(cx, cy, size);
        break;

      case "transport":
        drawTransportIcon(cx, cy, size);
        break;

      case "office":
        drawOfficeIcon(cx, cy, size);
        break;

      case "waste":
        drawWasteIcon(cx, cy, size);
        break;

      case "safety":
        drawSafetyIcon(cx, cy, size);
        break;

      case "harvest":
        drawHarvestIcon(cx, cy, size);
        break;

      case "intake":
        drawIntakeIcon(cx, cy, size);
        break;

      default:
        drawProcessIcon(cx, cy, size);
    }
  };

  // =========================================================
  // HEADER
  // =========================================================
  const headerGradient = ctx.createLinearGradient(180, 40, W - 180, 150);

  headerGradient.addColorStop(0, "#E8F7FA");
  headerGradient.addColorStop(0.5, "#F7FCFD");
  headerGradient.addColorStop(1, "#E8F3FA");

  drawCard(115, 42, W - 230, 132, headerGradient, "#C6E4EE", 44, false);

  // decorative water lines
  ctx.strokeStyle = "#74CAE0";
  ctx.lineWidth = 5;

  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(65, 88 + i * 14);

    ctx.bezierCurveTo(130, 52 + i * 10, 185, 128 + i * 4, 250, 88 + i * 12);

    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(W - 250, 88 + i * 12);

    ctx.bezierCurveTo(
      W - 185,
      128 + i * 4,
      W - 130,
      52 + i * 10,
      W - 65,
      88 + i * 14,
    );

    ctx.stroke();
  }

  ctx.fillStyle = C.navy;

  ctx.font =
    lang === "ar"
      ? `bold 42px ${THEME.fontArabic}`
      : `bold 42px ${THEME.fontHeading}`;

  ctx.textAlign = "center";

  ctx.fillText(
    lang === "ar"
      ? "المخطط التشغيلي المفاهيمي المقترح"
      : "Proposed Conceptual Operational Layout",
    W / 2,
    98,
  );

  ctx.fillStyle = C.teal;

  ctx.font =
    lang === "ar" ? `20px ${THEME.fontArabic}` : `20px ${THEME.fontBody}`;

  ctx.fillText(
    lang === "ar"
      ? "تدفق العمليات والمناطق التشغيلية والمرافق الداعمة"
      : "Operational flow, functional zones and supporting utilities",
    W / 2,
    137,
  );

  // =========================================================
  // SECTION TITLE
  // =========================================================
  drawCard(550, 194, 500, 54, C.navy, C.navy, 28, false);

  ctx.fillStyle = C.white;

  ctx.font =
    lang === "ar"
      ? `bold 23px ${THEME.fontArabic}`
      : `bold 23px ${THEME.fontBody}`;

  ctx.textAlign = "center";

  ctx.fillText(
    lang === "ar" ? "المسار التشغيلي الرئيسي" : "Primary Operational Flow",
    W / 2,
    229,
  );

  // =========================================================
  // PROCESS FLOW
  // =========================================================
  const flowItems =
    processFlow.length >= 2
      ? processFlow.slice(0, 10)
      : functionalZones.slice(0, 10).map((zone, index) => ({
          sequence: index + 1,
          stage: zone.zone_name || zone.zone_purpose || zone.zone_id,
          zone: zone.zone_name || zone.zone_id,
        }));

  const cardW = 262;
  const cardH = 205;
  const cols = 5;

  const gapX = 34;
  const gapY = 88;

  const totalRowWidth = cols * cardW + (cols - 1) * gapX;

  const startX = (W - totalRowWidth) / 2;
  const startY = 282;

  const flowPositions = [];

  flowItems.forEach((item, index) => {
    const row = Math.floor(index / cols);
    const positionInRow = index % cols;

    let col;

    // snake layout
    if (lang === "ar") {
      col = row % 2 === 0 ? cols - 1 - positionInRow : positionInRow;
    } else {
      col = row % 2 === 0 ? positionInRow : cols - 1 - positionInRow;
    }

    const x = startX + col * (cardW + gapX);
    const y = startY + row * (cardH + gapY);

    const label =
      item.stage ||
      item.zone ||
      `${lang === "ar" ? "مرحلة" : "Stage"} ${index + 1}`;

    const kind = classify(label);

    const fill =
      index % 3 === 0 ? "#F4FAFD" : index % 3 === 1 ? "#F1FBF9" : "#F8FBFD";

    drawCard(x, y, cardW, cardH, fill, "#C7DFEA", 26, true);

    // colored top accent
    const accent =
      kind === "water"
        ? C.cyan
        : kind === "cold"
          ? "#5AA9D6"
          : kind === "maintenance"
            ? C.amber
            : kind === "safety"
              ? C.red
              : C.teal;

    ctx.fillStyle = accent;

    roundedPath(x, y, cardW, 11, 8);
    ctx.fill();

    // stage circle
    ctx.fillStyle = C.navy;

    ctx.beginPath();
    ctx.arc(x + cardW - 32, y + 31, 20, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = C.white;
    ctx.font = `bold 18px ${THEME.fontBody}`;
    ctx.textAlign = "center";

    ctx.fillText(String(item.sequence || index + 1), x + cardW - 32, y + 38);

    // icon background
    ctx.fillStyle = "#FFFFFF";

    ctx.beginPath();
    ctx.arc(x + cardW / 2, y + 88, 55, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = "#D8EAF1";
    ctx.lineWidth = 2;
    ctx.stroke();

    drawIcon(kind, x + cardW / 2, y + 88, 88);

    ctx.fillStyle = C.darkText;

    ctx.font =
      lang === "ar"
        ? `bold 20px ${THEME.fontArabic}`
        : `bold 20px ${THEME.fontBody}`;

    ctx.textAlign = "center";

    drawWrappedCentered(label, x + cardW / 2, y + 162, cardW - 34, 23, 2);

    flowPositions.push({
      x,
      y,
      row,
      col,
      centerX: x + cardW / 2,
      centerY: y + cardH / 2,
      left: x,
      right: x + cardW,
      top: y,
      bottom: y + cardH,
    });
  });

  // =========================================================
  // FLOW CONNECTORS
  // =========================================================
  for (let i = 0; i < flowPositions.length - 1; i++) {
    const a = flowPositions[i];
    const b = flowPositions[i + 1];

    if (a.row === b.row) {
      const goingRight = b.centerX > a.centerX;

      drawConnector(
        {
          x: goingRight ? a.right + 5 : a.left - 5,
          y: a.centerY,
        },
        {
          x: goingRight ? b.left - 12 : b.right + 12,
          y: b.centerY,
        },
        C.blue,
      );
    } else {
      drawConnector(
        {
          x: a.centerX,
          y: a.bottom + 5,
        },
        {
          x: b.centerX,
          y: b.top - 12,
        },
        C.blue,
      );
    }
  }

  // =========================================================
  // UTILITIES / SUPPORT SYSTEMS
  // =========================================================
  const utilityY = 812;

  drawCard(100, utilityY, W - 200, 194, "#F4FAFC", "#C6E1E8", 28, false);

  ctx.fillStyle = C.navy;

  ctx.font =
    lang === "ar"
      ? `bold 23px ${THEME.fontArabic}`
      : `bold 23px ${THEME.fontBody}`;

  ctx.textAlign = lang === "ar" ? "right" : "left";

  ctx.fillText(
    lang === "ar"
      ? "المرافق والأنظمة الداعمة"
      : "Supporting Utilities & Systems",
    lang === "ar" ? W - 140 : 140,
    utilityY + 39,
  );

  const utilities =
    utilityDistribution.length > 0
      ? utilityDistribution.slice(0, 5)
      : functionalZones
          .filter((zone) => {
            const text = `${zone.zone_name || ""} ${zone.zone_purpose || ""}`;

            return [
              "water",
              "maintenance",
              "storage",
              "lab",
              "safety",
              "office",
              "waste",
            ].includes(classify(text));
          })
          .slice(0, 5)
          .map((zone) => ({
            utility: zone.zone_name || zone.zone_purpose || zone.zone_id,
          }));

  const fallbackUtilities =
    lang === "ar"
      ? [
          "الطاقة والمرافق",
          "المياه والمعالجة",
          "السلامة والطوارئ",
          "الصيانة والدعم",
        ]
      : [
          "Power & Utilities",
          "Water & Treatment",
          "Safety & Emergency",
          "Maintenance & Support",
        ];

  const displayedUtilities =
    utilities.length > 0
      ? utilities
      : fallbackUtilities.map((utility) => ({
          utility,
        }));

  const utilCount = Math.min(displayedUtilities.length, 5);

  const utilGap = 18;

  const utilW = (W - 280 - utilGap * (utilCount - 1)) / utilCount;

  displayedUtilities.slice(0, utilCount).forEach((item, index) => {
    const text = item.utility || item.name || item.system || item.zone || "";

    const visualIndex = lang === "ar" ? utilCount - 1 - index : index;

    const x = 140 + visualIndex * (utilW + utilGap);

    const y = utilityY + 65;

    drawCard(x, y, utilW, 102, C.white, "#D2E6EC", 20, false);

    const kind = classify(text);

    drawIcon(kind, x + 46, y + 50, 58);

    ctx.fillStyle = C.darkText;

    ctx.font =
      lang === "ar"
        ? `bold 17px ${THEME.fontArabic}`
        : `bold 17px ${THEME.fontBody}`;

    ctx.textAlign = "center";

    drawWrappedCentered(text, x + utilW / 2 + 20, y + 43, utilW - 95, 20, 2);
  });

  // =========================================================
  // FOOTER / CONCEPT STATUS
  // =========================================================
  ctx.fillStyle = C.muted;

  ctx.font =
    lang === "ar" ? `15px ${THEME.fontArabic}` : `15px ${THEME.fontBody}`;

  ctx.textAlign = "center";

  ctx.fillText(
    lang === "ar"
      ? "مخطط مفاهيمي أولي مبني على بيانات الدراسة — يخضع للتحقق والتصميم الهندسي قبل التنفيذ"
      : "Preliminary conceptual layout based on study data — subject to engineering validation before implementation",
    W / 2,
    1048,
  );

  return canvasChartParagraph(canvas, 560, 378);
};
const renderModuleField = (rawKey, label, value, level, lang, nodes) => {
  const keyNorm = String(rawKey || "").toLowerCase();

  const safeFieldLabel =
    lang === "ar"
      ? arabicizeLabel(prettifyKey(rawKey || label), lang, rawKey || "metric")
      : label;
  if (
    WORD_RENDER_IS_TECHNICAL &&
    keyNorm === "facility_layout_plan" &&
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    console.log("🟦 FACILITY LAYOUT SPECIAL RENDERER HIT:", value);

    nodes.push(
      sectionHeading(
        lang === "ar"
          ? "المخطط التشغيلي المفاهيمي المقترح"
          : "Proposed Conceptual Operational Layout",
        level,
        lang,
      ),
    );

    nodes.push(
      highlightBox(
        lang === "ar" ? "طبيعة المخطط" : "Layout Status",
        [
          lang === "ar"
            ? "هذا مخطط تشغيلي مفاهيمي أولي مبني على بيانات الدراسة، ولا يمثل مخططًا هندسيًا نهائيًا صالحًا للتنفيذ دون تحقق واعتماد هندسي."
            : "This is a preliminary conceptual operational layout based on the study inputs and does not represent a final construction-ready engineering design without further validation and engineering approval.",
        ],
        lang,
        THEME.boxBg,
        THEME.boxBorder,
      ),
    );

    nodes.push(emptyLine());
    const layoutDiagram = facilityLayoutConceptDiagram(value, lang);

    if (layoutDiagram) {
      nodes.push(layoutDiagram);
      nodes.push(emptyLine());
    }
    // =========================================================
    // FUNCTIONAL ZONES
    // =========================================================
    const functionalZones = Array.isArray(value.functional_zones)
      ? value.functional_zones.filter(
          (zone) => zone && typeof zone === "object",
        )
      : [];

    if (functionalZones.length) {
      nodes.push(
        sectionHeading(
          lang === "ar"
            ? "المناطق الوظيفية المقترحة"
            : "Proposed Functional Zones",
          level + 1,
          lang,
        ),
      );

      functionalZones.forEach((zone) => {
        const zoneTitle =
          zone.zone_name ||
          zone.zone_id ||
          (lang === "ar" ? "منطقة تشغيلية" : "Operational Zone");

        const zoneLines = [];

        if (zone.zone_purpose) {
          zoneLines.push(
            `${lang === "ar" ? "الغرض التشغيلي" : "Operational purpose"}: ${zone.zone_purpose}`,
          );
        }

        if (zone.recommended_area) {
          zoneLines.push(
            `${lang === "ar" ? "المساحة المقترحة" : "Recommended area"}: ${zone.recommended_area}`,
          );
        }

        if (zone.area_basis) {
          zoneLines.push(
            `${lang === "ar" ? "أساس تقدير المساحة" : "Area basis"}: ${zone.area_basis}`,
          );
        }

        if (zone.priority) {
          zoneLines.push(
            `${lang === "ar" ? "الأولوية" : "Priority"}: ${zone.priority}`,
          );
        }

        if (
          Array.isArray(zone.environmental_requirements) &&
          zone.environmental_requirements.length
        ) {
          zoneLines.push(
            `${lang === "ar" ? "المتطلبات البيئية" : "Environmental requirements"}: ${zone.environmental_requirements.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        if (
          Array.isArray(zone.safety_requirements) &&
          zone.safety_requirements.length
        ) {
          zoneLines.push(
            `${lang === "ar" ? "متطلبات السلامة" : "Safety requirements"}: ${zone.safety_requirements.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        if (zoneLines.length) {
          nodes.push(
            highlightBox(
              zone.zone_id ? `${zone.zone_id} — ${zoneTitle}` : zoneTitle,
              zoneLines,
              lang,
              "F8FAFD",
              THEME.secondary,
            ),
          );

          nodes.push(emptyLine(80));
        }
      });
    }
    // =========================================================
    // ADJACENCY / SEPARATION MAP
    // =========================================================
    const adjacencyRequirements = Array.isArray(value.adjacency_requirements)
      ? value.adjacency_requirements.filter(
          (item) => item && typeof item === "object",
        )
      : [];

    if (adjacencyRequirements.length) {
      nodes.push(
        sectionHeading(
          lang === "ar"
            ? "علاقات القرب والفصل بين المناطق"
            : "Zone Adjacency & Separation Relationships",
          level + 1,
          lang,
        ),
      );

      adjacencyRequirements.forEach((item) => {
        const fromZone = item.from_zone || "";
        const toZone = item.to_zone || "";
        const relationship = String(item.relationship || "").trim();

        let relationshipLabel = relationship;

        if (lang === "ar") {
          if (relationship === "must_be_adjacent") {
            relationshipLabel = "يجب أن تكون متجاورة";
          } else if (relationship === "should_be_near") {
            relationshipLabel = "يفضل أن تكون متقاربة";
          } else if (relationship === "should_be_separated") {
            relationshipLabel = "يجب الفصل بينها";
          }
        } else {
          if (relationship === "must_be_adjacent") {
            relationshipLabel = "Must be adjacent";
          } else if (relationship === "should_be_near") {
            relationshipLabel = "Should be near";
          } else if (relationship === "should_be_separated") {
            relationshipLabel = "Should be separated";
          }
        }

        const relationshipLines = [];

        if (relationshipLabel) {
          relationshipLines.push(
            `${lang === "ar" ? "نوع العلاقة" : "Relationship"}: ${relationshipLabel}`,
          );
        }

        if (item.reason) {
          relationshipLines.push(
            `${lang === "ar" ? "المبرر التشغيلي" : "Operational rationale"}: ${item.reason}`,
          );
        }

        if (fromZone || toZone) {
          nodes.push(
            highlightBox(
              `${fromZone || "—"}  →  ${toZone || "—"}`,
              relationshipLines,
              lang,
              relationship === "should_be_separated"
                ? THEME.riskHighBg
                : relationship === "must_be_adjacent"
                  ? THEME.riskLowBg
                  : THEME.boxBg,
              relationship === "should_be_separated"
                ? "C0504D"
                : relationship === "must_be_adjacent"
                  ? "70AD47"
                  : THEME.boxBorder,
            ),
          );

          nodes.push(emptyLine(80));
        }
      });
    }
    // =========================================================
    // PROCESS FLOW
    // =========================================================
    const processFlow = Array.isArray(value.process_flow)
      ? value.process_flow
          .filter((item) => item && typeof item === "object")
          .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0))
      : [];

    if (processFlow.length) {
      nodes.push(
        sectionHeading(
          lang === "ar"
            ? "تسلسل العملية التشغيلية"
            : "Operational Process Flow",
          level + 1,
          lang,
        ),
      );

      processFlow.forEach((item, index) => {
        const stageTitle =
          item.stage ||
          `${lang === "ar" ? "المرحلة" : "Stage"} ${item.sequence || index + 1}`;

        const stageLines = [];

        if (item.zone) {
          stageLines.push(
            `${lang === "ar" ? "المنطقة" : "Zone"}: ${item.zone}`,
          );
        }

        if (Array.isArray(item.inputs) && item.inputs.length) {
          stageLines.push(
            `${lang === "ar" ? "المدخلات" : "Inputs"}: ${item.inputs.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        if (Array.isArray(item.outputs) && item.outputs.length) {
          stageLines.push(
            `${lang === "ar" ? "المخرجات" : "Outputs"}: ${item.outputs.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        if (item.estimated_duration) {
          stageLines.push(
            `${lang === "ar" ? "المدة التقديرية" : "Estimated duration"}: ${item.estimated_duration}`,
          );
        }

        if (
          Array.isArray(item.critical_dependencies) &&
          item.critical_dependencies.length
        ) {
          stageLines.push(
            `${lang === "ar" ? "الاعتماديات الحرجة" : "Critical dependencies"}: ${item.critical_dependencies.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        nodes.push(
          highlightBox(
            `${item.sequence || index + 1}. ${stageTitle}`,
            stageLines,
            lang,
            "F8FAFD",
            THEME.secondary,
          ),
        );

        if (index < processFlow.length - 1) {
          nodes.push(
            new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: {
                before: 40,
                after: 40,
              },
              children: [
                professionalTextRun(
                  {
                    text: "↓",
                    bold: true,
                    size: 28,
                    color: THEME.secondary,
                  },
                  lang,
                ),
              ],
            }),
          );
        }
      });

      nodes.push(emptyLine());
    }
    // =========================================================
    // EQUIPMENT PLACEMENT MAP
    // =========================================================
    const equipmentPlacement = Array.isArray(value.equipment_placement)
      ? value.equipment_placement.filter(
          (item) => item && typeof item === "object",
        )
      : [];

    if (equipmentPlacement.length) {
      nodes.push(
        sectionHeading(
          lang === "ar"
            ? "توزيع المعدات والتقنيات داخل المناطق"
            : "Equipment & Technology Placement",
          level + 1,
          lang,
        ),
      );

      equipmentPlacement.forEach((item) => {
        const equipmentName =
          item.equipment ||
          (lang === "ar" ? "معدة أو نظام" : "Equipment or System");

        const placementLines = [];

        if (item.recommended_zone) {
          placementLines.push(
            `${lang === "ar" ? "المنطقة المقترحة" : "Recommended zone"}: ${item.recommended_zone}`,
          );
        }

        if (item.placement_reason) {
          placementLines.push(
            `${lang === "ar" ? "مبرر التوزيع" : "Placement rationale"}: ${item.placement_reason}`,
          );
        }

        if (
          Array.isArray(item.utility_requirements) &&
          item.utility_requirements.length
        ) {
          placementLines.push(
            `${lang === "ar" ? "متطلبات المرافق" : "Utility requirements"}: ${item.utility_requirements.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        if (item.maintenance_access) {
          placementLines.push(
            `${lang === "ar" ? "الوصول للصيانة" : "Maintenance access"}: ${item.maintenance_access}`,
          );
        }

        if (
          Array.isArray(item.safety_considerations) &&
          item.safety_considerations.length
        ) {
          placementLines.push(
            `${lang === "ar" ? "اعتبارات السلامة" : "Safety considerations"}: ${item.safety_considerations.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        if (placementLines.length) {
          nodes.push(
            highlightBox(
              equipmentName,
              placementLines,
              lang,
              "F8FAFD",
              THEME.tertiary,
            ),
          );

          nodes.push(emptyLine(80));
        }
      });

      nodes.push(emptyLine());
    }
    // =========================================================
    // UTILITY DISTRIBUTION MAP
    // =========================================================
    const utilityDistribution = Array.isArray(value.utility_distribution)
      ? value.utility_distribution.filter(
          (item) => item && typeof item === "object",
        )
      : [];

    if (utilityDistribution.length) {
      nodes.push(
        sectionHeading(
          lang === "ar"
            ? "توزيع المرافق والخدمات الفنية"
            : "Utility Distribution",
          level + 1,
          lang,
        ),
      );

      utilityDistribution.forEach((item) => {
        const utilityName =
          item.utility || (lang === "ar" ? "مرفق أو خدمة فنية" : "Utility");

        const utilityLines = [];

        if (Array.isArray(item.critical_zones) && item.critical_zones.length) {
          utilityLines.push(
            `${lang === "ar" ? "المناطق الحرجة" : "Critical zones"}: ${item.critical_zones.join(lang === "ar" ? "، " : ", ")}`,
          );
        }

        if (item.redundancy_requirement) {
          utilityLines.push(
            `${lang === "ar" ? "متطلب الاستمرارية أو البديل الاحتياطي" : "Redundancy requirement"}: ${item.redundancy_requirement}`,
          );
        }

        if (item.notes) {
          utilityLines.push(
            `${lang === "ar" ? "ملاحظات تشغيلية" : "Operational notes"}: ${item.notes}`,
          );
        }

        if (utilityLines.length) {
          nodes.push(
            highlightBox(
              utilityName,
              utilityLines,
              lang,
              THEME.boxBg,
              THEME.boxBorder,
            ),
          );

          nodes.push(emptyLine(80));
        }
      });

      nodes.push(emptyLine());
    }
    // =========================================================
    // PEOPLE / MATERIAL / WASTE FLOW
    // =========================================================
    const peopleFlow = Array.isArray(value.people_flow)
      ? value.people_flow.filter((item) => item && typeof item === "object")
      : [];

    const materialFlow = Array.isArray(value.material_flow)
      ? value.material_flow.filter((item) => item && typeof item === "object")
      : [];

    const wasteFlow = Array.isArray(value.waste_flow)
      ? value.waste_flow.filter((item) => item && typeof item === "object")
      : [];

    if (peopleFlow.length || materialFlow.length || wasteFlow.length) {
      nodes.push(
        sectionHeading(
          lang === "ar"
            ? "مسارات الحركة التشغيلية داخل الموقع"
            : "Operational Movement Flows",
          level + 1,
          lang,
        ),
      );
    }

    // =========================================================
    // PEOPLE FLOW
    // =========================================================
    if (peopleFlow.length) {
      nodes.push(
        sectionHeading(
          lang === "ar" ? "حركة الأشخاص" : "People Flow",
          level + 2,
          lang,
        ),
      );

      peopleFlow.forEach((item) => {
        const routeTitle =
          item.route || `${item.from || "—"} → ${item.to || "—"}`;

        const flowLines = [];

        if (item.from) {
          flowLines.push(`${lang === "ar" ? "من" : "From"}: ${item.from}`);
        }

        if (item.to) {
          flowLines.push(`${lang === "ar" ? "إلى" : "To"}: ${item.to}`);
        }

        if (item.control_requirement) {
          flowLines.push(
            `${lang === "ar" ? "متطلب التحكم" : "Control requirement"}: ${item.control_requirement}`,
          );
        }

        if (flowLines.length) {
          nodes.push(
            highlightBox(
              routeTitle,
              flowLines,
              lang,
              "F8FAFD",
              THEME.secondary,
            ),
          );

          nodes.push(emptyLine(80));
        }
      });
    }

    // =========================================================
    // MATERIAL FLOW
    // =========================================================
    if (materialFlow.length) {
      nodes.push(
        sectionHeading(
          lang === "ar" ? "تدفق المواد والمنتجات" : "Material & Product Flow",
          level + 2,
          lang,
        ),
      );

      materialFlow.forEach((item) => {
        const materialTitle =
          item.material ||
          (lang === "ar" ? "مادة أو منتج" : "Material or Product");

        const flowLines = [];

        if (item.from) {
          flowLines.push(`${lang === "ar" ? "من" : "From"}: ${item.from}`);
        }

        if (item.to) {
          flowLines.push(`${lang === "ar" ? "إلى" : "To"}: ${item.to}`);
        }

        if (item.handling_requirement) {
          flowLines.push(
            `${lang === "ar" ? "متطلب المناولة" : "Handling requirement"}: ${item.handling_requirement}`,
          );
        }

        if (flowLines.length) {
          nodes.push(
            highlightBox(
              materialTitle,
              flowLines,
              lang,
              "F8FAFD",
              THEME.tertiary,
            ),
          );

          nodes.push(emptyLine(80));
        }
      });
    }

    // =========================================================
    // WASTE FLOW
    // =========================================================
    if (wasteFlow.length) {
      nodes.push(
        sectionHeading(
          lang === "ar" ? "تدفق النفايات والمخلفات" : "Waste Flow",
          level + 2,
          lang,
        ),
      );

      wasteFlow.forEach((item) => {
        const wasteTitle =
          item.waste_type || (lang === "ar" ? "نوع النفايات" : "Waste Type");

        const flowLines = [];

        if (item.source_zone) {
          flowLines.push(
            `${lang === "ar" ? "منطقة المصدر" : "Source zone"}: ${item.source_zone}`,
          );
        }

        if (item.temporary_storage) {
          flowLines.push(
            `${lang === "ar" ? "التخزين المؤقت" : "Temporary storage"}: ${item.temporary_storage}`,
          );
        }

        if (item.disposal_or_treatment) {
          flowLines.push(
            `${lang === "ar" ? "المعالجة أو التخلص" : "Disposal or treatment"}: ${item.disposal_or_treatment}`,
          );
        }

        if (flowLines.length) {
          nodes.push(
            highlightBox(
              wasteTitle,
              flowLines,
              lang,
              THEME.riskMedBg,
              THEME.gold,
            ),
          );

          nodes.push(emptyLine(80));
        }
      });
    }

    if (peopleFlow.length || materialFlow.length || wasteFlow.length) {
      nodes.push(emptyLine());
    }
    // =========================================================
    // SPACE ALLOCATION
    // =========================================================
    const spaceAllocation = Array.isArray(value.space_allocation)
      ? value.space_allocation.filter(
          (item) => item && typeof item === "object",
        )
      : [];

    if (spaceAllocation.length) {
      nodes.push(
        sectionHeading(
          lang === "ar"
            ? "توزيع المساحات المقترح"
            : "Proposed Space Allocation",
          level + 1,
          lang,
        ),
      );

      spaceAllocation.forEach((item) => {
        const zoneName =
          item.zone || (lang === "ar" ? "منطقة تشغيلية" : "Operational Zone");

        const spaceLines = [];

        if (
          item.recommended_share_percent !== null &&
          item.recommended_share_percent !== undefined
        ) {
          spaceLines.push(
            `${lang === "ar" ? "النسبة المقترحة من المساحة" : "Recommended share"}: ${item.recommended_share_percent}%`,
          );
        }

        if (
          item.recommended_area_m2 !== null &&
          item.recommended_area_m2 !== undefined
        ) {
          spaceLines.push(
            `${lang === "ar" ? "المساحة المقترحة" : "Recommended area"}: ${item.recommended_area_m2} m²`,
          );
        }

        if (item.basis) {
          spaceLines.push(
            `${lang === "ar" ? "أساس التقدير" : "Basis"}: ${item.basis}`,
          );
        }

        if (spaceLines.length) {
          nodes.push(
            highlightBox(zoneName, spaceLines, lang, "F8FAFD", THEME.secondary),
          );

          nodes.push(emptyLine(80));
        }
      });

      nodes.push(emptyLine());
    }
    // =========================================================
    // LAYOUT CONSTRAINTS / VALIDATION
    // =========================================================
    const layoutConstraints = Array.isArray(value.layout_constraints)
      ? value.layout_constraints.filter(Boolean)
      : [];

    const layoutAssumptions = Array.isArray(value.layout_assumptions)
      ? value.layout_assumptions.filter(Boolean)
      : [];

    const layoutValidation = Array.isArray(value.validation_required)
      ? value.validation_required.filter(Boolean)
      : [];

    if (layoutConstraints.length) {
      nodes.push(
        highlightBox(
          lang === "ar" ? "قيود التخطيط" : "Layout Constraints",
          layoutConstraints.map(String),
          lang,
          THEME.riskMedBg,
          THEME.gold,
        ),
      );

      nodes.push(emptyLine());
    }

    if (layoutAssumptions.length) {
      nodes.push(
        highlightBox(
          lang === "ar" ? "افتراضات التخطيط" : "Layout Assumptions",
          layoutAssumptions.map(String),
          lang,
          THEME.boxBg,
          THEME.boxBorder,
        ),
      );

      nodes.push(emptyLine());
    }

    if (layoutValidation.length) {
      nodes.push(
        highlightBox(
          lang === "ar"
            ? "التحقق المطلوب قبل اعتماد المخطط"
            : "Validation Required Before Layout Approval",
          layoutValidation.map(String),
          lang,
          THEME.riskHighBg,
          "C0504D",
        ),
      );

      nodes.push(emptyLine());
    }

    if (value.final_layout_recommendation) {
      nodes.push(
        highlightBox(
          lang === "ar"
            ? "التوصية النهائية للتخطيط"
            : "Final Layout Recommendation",
          [String(value.final_layout_recommendation)],
          lang,
          "EAF0F8",
          THEME.primary,
        ),
      );

      nodes.push(emptyLine());
    }
    return;
  }

  if (WORD_RENDER_IS_TECHNICAL && keyNorm === "process_time_implications") {
    const flowNodes = technicalProcessTimeFlow(value, lang);

    if (flowNodes.length) {
      nodes.push(sectionHeading(safeFieldLabel, level, lang));

      nodes.push(...flowNodes);
      nodes.push(emptyLine());

      return;
    }
  }
  if (keyNorm === "direct_competitors") {
    console.log("🟣 DIRECT COMPETITORS SPECIAL RENDERER HIT 🟣", value);
  }
  if (keyNorm === "direct_competitors") {
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every(
        (item) => item && typeof item === "object" && !Array.isArray(item),
      )
    ) {
      const competitorTable = professionalTable(
        [
          lang === "ar" ? "المنافس" : "Competitor",
          lang === "ar" ? "العرض والتموضع" : "Offering & Positioning",
          lang === "ar" ? "التسعير" : "Pricing",
          lang === "ar" ? "نقاط القوة والضعف" : "Strengths & Weaknesses",
          lang === "ar" ? "مستوى التهديد" : "Threat Level",
        ],
        value.map((item) => [
          [item.name, item.competitor_type].filter(Boolean).join(" — "),
          [item.services_and_offering, item.positioning]
            .filter(Boolean)
            .join(" — "),
          item.pricing || "",
          [
            item.strengths
              ? `${lang === "ar" ? "القوة:" : "Strengths:"} ${item.strengths}`
              : "",
            item.weaknesses
              ? `${lang === "ar" ? "الضعف:" : "Weaknesses:"} ${item.weaknesses}`
              : "",
          ]
            .filter(Boolean)
            .join(" — "),
          item.threat_level || "",
        ]),
        lang,
        [1.2, 2.4, 1.25, 2.4, 0.85],
        (text, rowIndex, colIndex) => {
          if (colIndex !== 4) return null;

          const value = String(text || "")
            .trim()
            .toLowerCase();

          if (value === "high" || value === "مرتفع") {
            return THEME.riskHighBg;
          }

          if (value === "medium" || value === "متوسط") {
            return THEME.riskMedBg;
          }

          if (value === "low" || value === "منخفض") {
            return THEME.riskLowBg;
          }

          return null;
        },
      );
      if (competitorTable) {
        nodes.push(sectionHeading(safeFieldLabel, level, lang));
        nodes.push(competitorTable);
        nodes.push(emptyLine());
        return;
      }
    }

    const competitorTable = competitorComparisonTableFromText(value, lang);

    if (competitorTable) {
      nodes.push(sectionHeading(safeFieldLabel, level, lang));
      nodes.push(competitorTable);
      nodes.push(emptyLine());
      return;
    }

    // Fallback: competitor names only, separated by commas or lines.
    if (typeof value === "string" && value.trim()) {
      const text = value.trim();

      const hasNarrative =
        text.length > 300 ||
        /[.!؟؛]/.test(text) ||
        /ماذا|لماذا|كيف|يتمثل|استنادًا|because|however/i.test(text);

      if (!hasNarrative) {
        const names = text
          .split(/[,،\n]+/)
          .map((name) => name.trim())
          .filter(Boolean);

        if (names.length >= 1 && names.length <= 20) {
          const namesTable = professionalTable(
            [lang === "ar" ? "المنافس" : "Competitor"],
            names.map((name) => [name]),
            lang,
            [1],
          );

          if (namesTable) {
            nodes.push(sectionHeading(safeFieldLabel, level, lang));
            nodes.push(namesTable);
            nodes.push(emptyLine());
            return;
          }
        }
      }
    }
  }
  if (META_KEYS.has(keyNorm)) return;
  // =========================================================
  // TECHNICAL RISK REGISTER — LANDSCAPE ROUTER
  // =========================================================
  if (
    WORD_RENDER_IS_TECHNICAL &&
    [
      "risk_register",
      "technical_risk_register",
      "operational_risk_register",
    ].includes(keyNorm) &&
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (item) => item && typeof item === "object" && !Array.isArray(item),
    )
  ) {
    console.log("🟧 RISK REGISTER LANDSCAPE ROUTER HIT:", rawKey);

    nodes.push(RISK_LANDSCAPE_START);

    nodes.push(
      sectionHeading(
        lang === "ar" ? "سجل المخاطر" : "Risk Register",
        level,
        lang,
      ),
    );

    nodes.push(
      captionParagraph(
        "table",
        lang === "ar" ? "سجل المخاطر" : "Risk Register",
        lang,
      ),
    );

    nodes.push(
      tableFromArrayOfObjects(value, lang, LANDSCAPE_CONTENT_WIDTH_DXA),
    );

    nodes.push(emptyLine());

    nodes.push(RISK_LANDSCAPE_END);

    return;
  }
  const visualRendered = renderVisualsForValue(
    rawKey,
    safeFieldLabel,
    value,
    level,
    lang,
    nodes,
  );

  if (visualRendered) return;

  if (typeof value === "string" && value.length >= NARRATIVE_MIN_LENGTH) {
    if (!isGenericAnalyticalHeading(safeFieldLabel, lang)) {
      nodes.push(sectionHeading(safeFieldLabel, level, lang));
    }

    renderNarrative(value, lang, nodes);
    return;
  }

  renderNode(rawKey, label, value, level, lang, nodes);
};

// =========================================================
// REFERENCES ENGINE
// =========================================================
// Whenever the study text touches statistics-grade topics (GDP,
// population, inflation, market size, industry data, digital
// payments, consumer behavior, government statistics), we surface a
// standard set of the reliable public sources consulting reports
// cite for that class of claim — never fabricated, always the
// well-known primary institutions.

const REFERENCE_TRIGGERS = [
  {
    test: /(GDP|gross domestic product|الناتج المحلي)/i,
    ref: {
      en: "World Bank. World Development Indicators — GDP data.",
      ar: "البنك الدولي، مؤشرات التنمية العالمية — بيانات الناتج المحلي الإجمالي.",
    },
  },
  {
    test: /(population|demographic|السكان|الديموغراف)/i,
    ref: {
      en: "United Nations, Department of Economic and Social Affairs — World Population Prospects.",
      ar: "الأمم المتحدة، إدارة الشؤون الاقتصادية والاجتماعية — توقعات السكان في العالم.",
    },
  },
  {
    test: /(inflation|CPI|التضخم)/i,
    ref: {
      en: "International Monetary Fund (IMF) — World Economic Outlook Database.",
      ar: "صندوق النقد الدولي — قاعدة بيانات آفاق الاقتصاد العالمي.",
    },
  },
  {
    test: /(market size|industry size|حجم السوق|حجم القطاع)/i,
    ref: {
      en: "Statista — Market and Industry Reports (where publicly available).",
      ar: "Statista — تقارير السوق والقطاع (حيثما تتوفر بشكل عام).",
    },
  },
  {
    test: /(digital payment|e-?commerce|fintech|الدفع الرقمي|التجارة الإلكترونية)/i,
    ref: {
      en: "World Bank — Global Findex Database on digital financial services.",
      ar: "البنك الدولي — قاعدة بيانات Global Findex للخدمات المالية الرقمية.",
    },
  },
  {
    test: /(consumer behavior|consumer spending|سلوك المستهلك|الإنفاق الاستهلاكي)/i,
    ref: {
      en: "OECD — Consumer Trends and Household Spending Statistics.",
      ar: "منظمة التعاون الاقتصادي والتنمية (OECD) — إحصاءات اتجاهات المستهلك وإنفاق الأسر.",
    },
  },
  {
    test: /(central bank|البنك المركزي)/i,
    ref: {
      en: "Relevant national Central Bank — official statistical bulletins.",
      ar: "البنك المركزي المعني — النشرات الإحصائية الرسمية.",
    },
  },
  {
    test: /(regulatory|licensing|ministry|امتثال|ترخيص|وزارة)/i,
    ref: {
      en: "Relevant government ministries and official regulatory bodies.",
      ar: "الوزارات الحكومية المعنية والجهات التنظيمية الرسمية.",
    },
  },
];

const LINKED_REFERENCE_TRIGGERS = [
  {
    test: /(GDP|gross domestic product|الناتج المحلي|اقتصاد|economic|market|السوق|industry|القطاع)/i,
    title: {
      en: "World Bank - World Development Indicators",
      ar: "البنك الدولي - مؤشرات التنمية العالمية",
    },
    url: "https://databank.worldbank.org/source/world-development-indicators",
  },
  {
    test: /(population|demographic|السكان|الديموغراف|customer|العملاء)/i,
    title: {
      en: "United Nations - World Population Prospects",
      ar: "الأمم المتحدة - توقعات السكان في العالم",
    },
    url: "https://population.un.org/wpp/",
  },
  {
    test: /(inflation|CPI|تضخم|التضخم|macro|مالي|financial)/i,
    title: {
      en: "IMF - World Economic Outlook Database",
      ar: "صندوق النقد الدولي - قاعدة بيانات آفاق الاقتصاد العالمي",
    },
    url: "https://www.imf.org/en/Publications/WEO/weo-database",
  },
  {
    test: /(business|startup|enterprise|SME|مشروع|منشأة|شركة|investment|استثمار)/i,
    title: {
      en: "OECD - Entrepreneurship and Business Statistics",
      ar: "منظمة التعاون الاقتصادي والتنمية - إحصاءات الأعمال وريادة الأعمال",
    },
    url: "https://www.oecd.org/en/data.html",
  },
  {
    test: /(digital|technology|AI|e-commerce|fintech|رقمي|تقنية|ذكاء|التجارة الإلكترونية)/i,
    title: {
      en: "World Bank - Global Findex Database",
      ar: "البنك الدولي - قاعدة بيانات Global Findex",
    },
    url: "https://www.worldbank.org/en/publication/globalfindex",
  },
  {
    test: /(Saudi|KSA|السعودية|المملكة|منشآت|رخص|ترخيص|وزارة|regulatory|licensing)/i,
    title: {
      en: "Saudi General Authority for Statistics",
      ar: "الهيئة العامة للإحصاء في السعودية",
    },
    url: "https://www.stats.gov.sa/",
  },
  {
    test: /(Saudi|KSA|السعودية|SME|منشآت|startup|ريادة)/i,
    title: {
      en: "Monsha'at - SME General Authority",
      ar: "منشآت - الهيئة العامة للمنشآت الصغيرة والمتوسطة",
    },
    url: "https://www.monshaat.gov.sa/",
  },
];

const DEFAULT_REFERENCES = [
  {
    title: {
      en: "World Bank Open Data",
      ar: "البيانات المفتوحة للبنك الدولي",
    },
    url: "https://data.worldbank.org/",
  },
  {
    title: {
      en: "IMF Data",
      ar: "بيانات صندوق النقد الدولي",
    },
    url: "https://www.imf.org/en/Data",
  },
  {
    title: {
      en: "Saudi General Authority for Statistics",
      ar: "الهيئة العامة للإحصاء في السعودية",
    },
    url: "https://www.stats.gov.sa/",
  },
];

// Scans the full study text once and returns linked reference entries.
const collectReferences = (study, lang) => {
  const haystack = JSON.stringify(study || {});
  const found = [];

  const isTechnicalOperational = isTechnicalOperationalStudy(study);
  if (!isTechnicalOperational) {
    LINKED_REFERENCE_TRIGGERS.forEach(({ test, title, url }) => {
      if (test.test(haystack) && !found.some((f) => f.url === url)) {
        found.push({ title: title[lang], url });
      }
    });

    DEFAULT_REFERENCES.forEach(({ title, url }) => {
      if (!found.some((f) => f.url === url)) {
        found.push({ title: title[lang], url });
      }
    });
  }
  const explicitUrls = [...haystack.matchAll(/https?:\/\/[^\s"'<>\\)]+/g)]
    .map((match) => match[0].replace(/[.,;،؛]+$/, ""))
    .filter(Boolean);
  explicitUrls.forEach((url) => {
    if (!found.some((f) => f.url === url)) {
      let sourceName = "";

      try {
        const hostname = new URL(url).hostname.replace(/^www\./, "");

        const knownSources = {
          "stats.gov.sa": {
            ar: "الهيئة العامة للإحصاء",
            en: "General Authority for Statistics",
          },
          "mewa.gov.sa": {
            ar: "وزارة البيئة والمياه والزراعة",
            en: "Ministry of Environment, Water and Agriculture",
          },
          "sfda.gov.sa": {
            ar: "الهيئة العامة للغذاء والدواء",
            en: "Saudi Food and Drug Authority",
          },
          "worldbank.org": {
            ar: "البنك الدولي",
            en: "World Bank",
          },
          "imf.org": {
            ar: "صندوق النقد الدولي",
            en: "International Monetary Fund",
          },
          "fao.org": {
            ar: "منظمة الأغذية والزراعة للأمم المتحدة",
            en: "Food and Agriculture Organization",
          },
        };

        const matchedKey = Object.keys(knownSources).find(
          (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
        );

        sourceName = matchedKey ? knownSources[matchedKey][lang] : hostname;
      } catch {
        sourceName =
          lang === "ar" ? "مصدر إلكتروني موثق" : "Verified Online Source";
      }

      found.push({
        title: sourceName,
        url,
      });
    }
  });
  if (isTechnicalOperational && found.length === 0) {
    found.push(
      {
        title:
          lang === "ar"
            ? "وزارة البيئة والمياه والزراعة"
            : "Ministry of Environment, Water and Agriculture",
        url: "https://www.mewa.gov.sa",
      },
      {
        title:
          lang === "ar"
            ? "الهيئة العامة للغذاء والدواء"
            : "Saudi Food and Drug Authority",
        url: "https://www.sfda.gov.sa",
      },
    );
  }
  return found;
};

const referenceParagraph = (ref, index, lang) =>
  new Paragraph({
    bidirectional: lang === "ar",
    alignment: align(lang),
    spacing: { after: 180, line: 270 },
    children: [
      professionalTextRun(
        {
          text: `${index + 1}. ${ref.title}`,
          bold: true,
          color: THEME.primary,
          size: 20,
        },
        lang,
      ),

      new TextRun({
        break: 1,
      }),

      new ExternalHyperlink({
        link: ref.url,
        children: [
          professionalTextRun(
            {
              text: ref.url,
              color: "6B7280",
              underline: {},
              size: 17,
            },
            "en",
          ),
        ],
      }),
    ],
  });

// =========================================================
// STRUCTURAL PAGES
// =========================================================
const buildCoverTopBlock = (lang, clientName, logoBytes) =>
  new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    visuallyRightToLeft: lang === "ar",
    borders: {
      top: { style: BorderStyle.NONE },
      bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE },
      right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: { style: BorderStyle.NONE },
    },
    rows: [
      new TableRow({
        children: [
          // النص
          new TableCell({
            width: { size: 72, type: WidthType.PERCENTAGE },
            verticalAlign: VerticalAlign.CENTER,
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            margins: { top: 80, bottom: 80, left: 80, right: 80 },
            children: [
              new Paragraph({
                alignment:
                  lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,
                bidirectional: lang === "ar",
                spacing: { after: 60 },
                children: [
                  professionalTextRun(
                    {
                      text:
                        lang === "ar"
                          ? "مؤسسة الجبرية التجارية"
                          : "Al-Jabriyah Establishment Commercial",
                      bold: true,
                      size: 26,
                      color: THEME.primary,
                    },
                    lang,
                  ),
                ],
              }),

              new Paragraph({
                alignment:
                  lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,
                spacing: { after: 120 },
                children: [
                  professionalTextRun(
                    {
                      text:
                        lang === "ar"
                          ? "Al-Jabriyah Establishment Commercial"
                          : "Commercial Consulting & Feasibility Studies",
                      size: 18,
                      color: THEME.secondary,
                    },
                    "en",
                  ),
                ],
              }),

              new Paragraph({
                alignment:
                  lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,
                bidirectional: lang === "ar",
                spacing: { after: 40 },
                children: [
                  professionalTextRun(
                    {
                      text: lang === "ar" ? "موجهة إلى" : "Prepared For",
                      bold: true,
                      size: 16,
                      color: THEME.accent,
                    },
                    lang,
                  ),
                ],
              }),

              new Paragraph({
                alignment:
                  lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,
                bidirectional: lang === "ar",
                children: [
                  professionalTextRun(
                    {
                      text:
                        clientName ||
                        (lang === "ar" ? "اسم العميل" : "Client Name"),
                      bold: true,
                      size: 20,
                      color: THEME.primary,
                    },
                    lang,
                  ),
                ],
              }),
            ],
          }),

          // اللوغو
          new TableCell({
            width: { size: 28, type: WidthType.PERCENTAGE },
            verticalAlign: VerticalAlign.CENTER,
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            margins: { top: 60, bottom: 60, left: 60, right: 60 },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new ImageRun({
                    type: "png",
                    data: logoBytes,
                    transformation: {
                      width: 120,
                      height: 165,
                    },
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
const buildCoverPage = (study, lang, meta, logoBytes) => {
  console.log("🟣 COVER FUNCTION CURRENT VERSION");
  console.log("🟣 COVER STUDY TYPE:", study?.study_type);
  console.log("🟣 COVER IS TECHNICAL:", isTechnicalOperationalStudy(study));
  const clientName =
    meta?.clientName ||
    study?.recipient_name ||
    study?.client_name ||
    study?.customer_name ||
    study?.recipientName ||
    "";

  const projectName =
    meta?.projectName || study?.project_name || study?.projectName || "";

  const studyDate =
    meta?.studyDate || study?.study_date || new Date().toLocaleDateString();

  const nodes = [];
  const isTechnicalOperational = isTechnicalOperationalStudy(study);
  // =====================================================
  // TOP BRAND BLOCK
  // Logo right + company name left
  // =====================================================

  nodes.push(
    new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },

      visuallyRightToLeft: lang === "ar",

      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE },
      },

      rows: [
        new TableRow({
          children: [
            // LOGO — right side in Arabic
            new TableCell({
              width: {
                size: 30,
                type: WidthType.PERCENTAGE,
              },

              verticalAlign: VerticalAlign.CENTER,

              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
              },

              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,

                  children: [
                    new ImageRun({
                      type: "png",
                      data: logoBytes,

                      transformation: {
                        width: 125,
                        height: 170,
                      },
                    }),
                  ],
                }),
              ],
            }),

            // COMPANY NAME
            new TableCell({
              width: {
                size: 70,
                type: WidthType.PERCENTAGE,
              },

              verticalAlign: VerticalAlign.CENTER,

              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
              },

              children: [
                new Paragraph({
                  alignment:
                    lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,

                  bidirectional: lang === "ar",

                  spacing: {
                    after: 70,
                  },

                  children: [
                    professionalTextRun(
                      {
                        text:
                          lang === "ar"
                            ? "مؤسسة الجبرية التجارية"
                            : "Al-Jabriyah Establishment Commercial",

                        bold: true,
                        size: 27,
                        color: THEME.primary,
                      },
                      lang,
                    ),
                  ],
                }),

                new Paragraph({
                  alignment:
                    lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,

                  children: [
                    new TextRun({
                      text: "Al-Jabriyah Establishment Commercial",
                      size: 18,
                      color: THEME.secondary,
                      font: THEME.fontBody,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    }),
  );

  // =====================================================
  // DIVIDER
  // =====================================================

  nodes.push(
    new Paragraph({
      spacing: {
        before: 100,
        after: 700,
      },

      border: {
        bottom: {
          style: BorderStyle.SINGLE,
          size: 8,
          color: THEME.gold,
          space: 5,
        },
      },

      children: [
        new TextRun({
          text: " ",
          size: 2,
        }),
      ],
    }),
  );

  // =====================================================
  // STUDY TITLE
  // =====================================================

  nodes.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      bidirectional: lang === "ar",

      spacing: {
        after: 120,
      },

      children: [
        professionalTextRun(
          {
            text: isTechnicalOperational
              ? lang === "ar"
                ? "الدراسة الفنية والتشغيلية"
                : "TECHNICAL & OPERATIONAL STUDY"
              : lang === "ar"
                ? "دراسة الجدوى السوقية"
                : "MARKET FEASIBILITY STUDY",

            bold: true,
            size: 50,
            color: THEME.primary,
          },
          lang,
        ),
      ],
    }),
  );

  // =====================================================
  // PROJECT NAME
  // =====================================================

  if (projectName) {
    nodes.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        bidirectional: lang === "ar",

        spacing: {
          after: 130,
        },

        children: [
          professionalTextRun(
            {
              text: projectName,
              bold: true,
              size: 27,
              color: THEME.secondary,
            },
            lang,
          ),
        ],
      }),
    );
  }

  // =====================================================
  // SUBTITLE
  // =====================================================

  nodes.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      bidirectional: lang === "ar",

      spacing: {
        after: 650,
      },

      children: [
        professionalTextRun(
          {
            text: isTechnicalOperational
              ? lang === "ar"
                ? "تقرير استشاري لتقييم الجدوى الفنية والجاهزية التشغيلية"
                : "Technical Feasibility & Operational Readiness Advisory Report"
              : lang === "ar"
                ? "تقرير استشاري لدعم قرار دخول السوق"
                : "Strategic Market Entry Decision Report",

            size: 20,
            color: THEME.accent,
          },
          lang,
        ),
      ],
    }),
  );

  // =====================================================
  // CLIENT NAME
  // =====================================================

  if (clientName) {
    nodes.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        bidirectional: lang === "ar",

        spacing: {
          after: 70,
        },

        children: [
          professionalTextRun(
            {
              text: lang === "ar" ? "موجهة إلى" : "Prepared For",

              bold: true,
              size: 16,
              color: THEME.accent,
            },
            lang,
          ),
        ],
      }),
    );

    nodes.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        bidirectional: lang === "ar",

        spacing: {
          after: 420,
        },

        children: [
          professionalTextRun(
            {
              text: String(clientName),

              bold: true,
              size: 25,
              color: THEME.primary,
            },
            lang,
          ),
        ],
      }),
    );
  }

  // =====================================================
  // DATE
  // =====================================================

  nodes.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      bidirectional: lang === "ar",

      spacing: {
        after: 100,
      },

      children: [
        professionalTextRun(
          {
            text:
              lang === "ar"
                ? `تاريخ الدراسة  |  ${studyDate}`
                : `Study Date  |  ${studyDate}`,

            size: 17,
            color: THEME.secondary,
          },
          lang,
        ),
      ],
    }),
  );

  // =====================================================
  // BOTTOM BAR
  // =====================================================

  nodes.push(
    new Paragraph({
      spacing: {
        before: 400,
      },

      border: {
        bottom: {
          style: BorderStyle.SINGLE,
          size: 22,
          color: THEME.primary,
          space: 2,
        },
      },

      children: [
        new TextRun({
          text: " ",
          size: 2,
        }),
      ],
    }),
  );

  return nodes;
};

const buildCopyrightPage = (lang) => [
  sectionHeading(t("copyrightPageTitle", lang), 0, lang),
  bodyParagraph(t("copyrightBody", lang), lang),
  emptyLine(),
  bodyParagraph(`© ${new Date().getFullYear()}`, lang),
];

const buildDisclaimerPage = (lang) => [
  sectionHeading(t("disclaimerTitle", lang), 0, lang),
  bodyParagraph(t("disclaimerBody", lang), lang),
];

const buildTocPage = (lang) => {
  const tocTitle = lang === "ar" ? "الفهرس" : "Table of Contents";

  return [
    sectionHeading(tocTitle, 0, lang, { includeInToc: false }),

    new TableOfContents(tocTitle, {
      hyperlink: true,
      headingStyleRange: "1-2",
    }),
  ];
};

const buildListPage = (titleKey, lang) => [
  sectionHeading(t(titleKey, lang), 0, lang),

  titleKey === "listOfTables"
    ? new TableOfContents(t("listOfTables", lang), {
        captionLabel: t("tableLabel", lang),
      })
    : new TableOfContents(t("listOfFigures", lang), {
        captionLabel: t("figureLabel", lang),
      }),
];
const buildIntroductionPage = (study, lang) => {
  const isTechnicalOperational = isTechnicalOperationalStudy(study);

  return [
    sectionHeading(translateKey("introduction", lang), 0, lang),

    bodyParagraph(
      isTechnicalOperational
        ? lang === "ar"
          ? "أُعد هذا التقرير وفق منهجية متعددة المراحل لتقييم الجدوى الفنية والجاهزية التشغيلية للمشروع، وتشمل تحليل الطاقة الإنتاجية، والموقع والبنية التحتية، والمعدات والتقنيات، وآليات التشغيل، والجودة والتخزين، والقوى العاملة، والسلامة والبيئة، والأتمتة، والمخاطر الفنية ومتطلبات التنفيذ."
          : "This report was prepared using a structured multi-stage methodology to assess the project's technical feasibility and operational readiness, covering capacity, site and infrastructure, equipment and technology, operating processes, quality and storage, workforce, safety and environment, automation, technical risks, and implementation requirements."
        : t("introLine", lang),
      lang,
    ),

    emptyLine(),

    sectionHeading(t("methodologyTitle", lang), 1, lang),

    bodyParagraph(
      isTechnicalOperational
        ? lang === "ar"
          ? "يعتمد التحليل على بيانات الاستبيان الفني والتشغيلي، والبحث الفني والتنظيمي الموثق، وربط مخرجات المراحل ببعضها للتحقق من اتساق متطلبات الموقع والمرافق والمعدات والطاقة والعمليات والجودة والموارد البشرية والسلامة والأتمتة قبل إصدار الحكم على الجاهزية الفنية والتشغيلية."
          : "The analysis integrates technical and operational survey data with documented technical and regulatory research, while linking stage outputs to assess consistency across site, utilities, equipment, capacity, processes, quality, workforce, safety, and automation before forming an overall technical and operational readiness assessment."
        : t("methodologyBody", lang),
      lang,
    ),
  ];
};

// Locate a stage-6 module by its key across the whole study object,
// regardless of nesting depth quirks (some modules double-wrap their
// own key, e.g. stage6.executive_summary.executive_summary).
const findModule = (study, moduleKey) => {
  if (!study) return null;
  if (study.sections && typeof study.sections === "object") {
    const directKey = Object.keys(study.sections).find(
      (key) => key.toLowerCase() === moduleKey.toLowerCase(),
    );
    if (directKey) return study.sections[directKey];
  }
  for (const stageKey of Object.keys(study)) {
    const rawStage = study[stageKey];
    const stage =
      rawStage && typeof rawStage === "object" && rawStage.section_contents
        ? rawStage.section_contents
        : rawStage;
    if (stage && typeof stage === "object" && stage[moduleKey] !== undefined) {
      const val = stage[moduleKey];
      // unwrap self-nested module (module.module)
      if (val && typeof val === "object" && val[moduleKey] !== undefined) {
        return val[moduleKey];
      }
      return val;
    }
  }
  return null;
};

const buildExecutiveSummaryPage = (study, lang) => {
  const nodes = [
    sectionHeading(translateKey("executive_summary", lang), 0, lang),
  ];
  const summary = findModule(study, "executive_summary");
  const investment = findModule(study, "investment_recommendation");
  const isTechnicalOperational = isTechnicalOperationalStudy(study);
  if (summary) {
    if (typeof summary === "string") {
      renderNarrative(summary, lang, nodes);
    } else {
      Object.entries(summary).forEach(([k, v]) => {
        if (META_KEYS.has(k.toLowerCase())) return;
        renderModuleField(k, translateKey(k, lang), v, 1, lang, nodes);
      });
    }
  }

  // Investment decision KPI band, if available
  if (investment && typeof investment === "object") {
    const decision = investment.go_conditional_go_no_go_assessment;
    const confidence = investment.investment_confidence_level;
    const stats = [];
    if (decision && decision.determination) {
      stats.push({
        label: t("determination", lang),
        value: decision.determination,
      });
    }
    if (confidence) {
      stats.push({
        label: t("overallScore", lang),
        value:
          typeof confidence === "string"
            ? confidence.slice(0, 24)
            : String(confidence),
      });
    }
    if (stats.length) {
      nodes.push(emptyLine());
      nodes.push(kpiBand(stats, lang));
      nodes.push(emptyLine());
    }
  }

  return nodes;
};
const recommendationValueToText = (value, lang) => {
  if (value === null || value === undefined) return "";

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => recommendationValueToText(item, lang))
      .filter(Boolean)
      .join(" • ");
  }

  if (typeof value === "object") {
    const preferredKeys = [
      "status",
      "assessment",
      "verdict",
      "summary",
      "conclusion",
      "description",
      "recommendation",
      "action",
      "priority",
      "rationale",
      "finding",
      "risk_description",
      "condition",
    ];

    for (const key of preferredKeys) {
      if (value[key] !== undefined) {
        const text = recommendationValueToText(value[key], lang);
        if (text) return text;
      }
    }

    return Object.entries(value)
      .filter(([key]) => !META_KEYS.has(String(key).toLowerCase()))
      .map(([key, itemValue]) => {
        const text = recommendationValueToText(itemValue, lang);
        if (!text) return "";

        const label =
          lang === "ar"
            ? arabicizeLabel(prettifyKey(key), lang, key)
            : prettifyKey(key);

        return `${label}: ${text}`;
      })
      .filter(Boolean)
      .join("؛ ");
  }

  return "";
};

const recommendationListToText = (value, lang) => {
  if (!Array.isArray(value)) return [];

  return uniqueTextItems(
    value.map((item) => recommendationValueToText(item, lang)).filter(Boolean),
  );
};
const buildRecommendationsPage = (study, lang) => {
  const investment = findModule(study, "investment_recommendation");
  const summary = findModule(study, "executive_summary");

  const isTechnicalOperational = isTechnicalOperationalStudy(study);

  const nodes = [];

  if (!investment && !summary) return nodes;

  nodes.push(
    sectionHeading(
      isTechnicalOperational
        ? lang === "ar"
          ? "التوصيات الفنية والتشغيلية"
          : "Technical & Operational Recommendations"
        : translateKey("recommendations", lang),
      0,
      lang,
    ),
  );
  if (summary && typeof summary === "object") {
    const cards = summaryCards(
      isTechnicalOperational
        ? [
            {
              title: lang === "ar" ? "الجدوى الفنية" : "Technical Feasibility",
              value: recommendationValueToText(
                summary.overall_technical_feasibility,
                lang,
              ),
            },
            {
              title:
                lang === "ar" ? "الجاهزية التشغيلية" : "Operational Readiness",
              value: recommendationValueToText(
                summary.overall_operational_readiness,
                lang,
              ),
            },
            {
              title:
                lang === "ar"
                  ? "المخاطر الفنية الحرجة"
                  : "Critical Technical Risks",
              value: Array.isArray(summary.critical_technical_risks)
                ? lang === "ar"
                  ? `${summary.critical_technical_risks.length} مخاطر فنية حرجة`
                  : `${summary.critical_technical_risks.length} Critical Technical Risks`
                : recommendationValueToText(
                    summary.critical_technical_risks,
                    lang,
                  ),
            },
          ]
        : [
            {
              title: lang === "ar" ? "جاذبية السوق" : "Market Attractiveness",
              value: summary.market_attractiveness,
            },
            {
              title: lang === "ar" ? "نظرة المخاطر" : "Risk Overview",
              value: summary.risk_overview || summary.executive_risk_overview,
            },
            {
              title:
                lang === "ar" ? "جاهزية التنفيذ" : "Implementation Readiness",
              value: summary.implementation_readiness,
            },
          ],
      lang,
    );

    if (cards) {
      nodes.push(cards);
      nodes.push(emptyLine());
    }
  }
  if (!investment && summary && typeof summary === "object") {
    const priorities = isTechnicalOperational
      ? summary.recommended_next_steps
      : summary.immediate_executive_priorities;
    if (Array.isArray(priorities) && priorities.length) {
      nodes.push(
        highlightBox(
          isTechnicalOperational
            ? lang === "ar"
              ? "الخطوات التالية الموصى بها"
              : "Recommended Next Steps"
            : lang === "ar"
              ? "الأولويات التنفيذية العاجلة"
              : "Immediate Executive Priorities",
          recommendationListToText(priorities, lang),
          lang,
          "EEF2F8",
          THEME.secondary,
        ),
      );

      nodes.push(emptyLine());
    }

    return nodes;
  }
  const conditions = investment.investment_conditions;
  const strengths = investment.investment_strengths;
  const constraints = investment.investment_constraints;

  if (strengths) {
    nodes.push(
      sectionHeading(translateKey("investment_strengths", lang), 1, lang),
    );
    (Array.isArray(strengths) ? strengths : toPointList(strengths)).forEach(
      (s) => nodes.push(bulletParagraph(s, lang)),
    );
  }
  if (constraints) {
    nodes.push(
      sectionHeading(translateKey("investment_constraints", lang), 1, lang),
    );
    (Array.isArray(constraints)
      ? constraints
      : toPointList(constraints)
    ).forEach((s) => nodes.push(bulletParagraph(s, lang)));
  }
  if (conditions) {
    const lines = Array.isArray(conditions)
      ? conditions.map(String)
      : toPointList(conditions);
    nodes.push(highlightBox(t("keyFindings", lang), lines, lang));
    nodes.push(emptyLine());
  }
  const decision = investment.go_conditional_go_no_go_assessment;

  if (decision) {
    const determination =
      decision.determination ||
      (lang === "ar" ? "يتطلب التقييم" : "Requires Assessment");

    const justification = decision.justification || "";

    nodes.push(
      decisionBox(
        lang === "ar" ? "القرار التنفيذي" : "Executive Decision",
        String(determination),
        String(justification),
        lang,
      ),
    );

    nodes.push(emptyLine());
  }

  return nodes;
};

const buildReferencesPageLegacy = (study, lang) => {
  const refs = collectReferences(study, lang);
  const nodes = [sectionHeading(t("references", lang), 0, lang)];
  nodes.push(
    bodyParagraph(
      lang === "ar"
        ? "استندت هذه الدراسة إلى بيانات السوق والمؤشرات الاقتصادية المتاحة للعموم، إلى جانب الافتراضات الموثقة ضمن محتوى الدراسة. المصادر المرجعية التالية ذات صلة بفئات البيانات الواردة في هذا التقرير:"
        : "This study draws on publicly available market and economic data alongside the assumptions documented throughout the analysis. The following reference sources are relevant to the categories of data referenced in this report:",
      lang,
    ),
  );
  nodes.push(emptyLine());
  if (refs.length) {
    refs.forEach((r, i) => {
      nodes.push(numberedParagraph(r, lang, i, "premium-numbers"));
    });
  }
  return nodes;
};

const buildReferencesPage = (study, lang) => {
  const refs = collectReferences(study, lang);

  const isTechnicalOperational = isTechnicalOperationalStudy(study);

  const nodes = [sectionHeading(t("references", lang), 0, lang)];

  nodes.push(
    bodyParagraph(
      isTechnicalOperational
        ? lang === "ar"
          ? "استندت هذه الدراسة إلى بيانات المشروع والمدخلات الفنية والتشغيلية المقدمة، وإلى مصادر فنية وتنظيمية موثوقة تم استخدامها للتحقق من المتطلبات المتعلقة بالموقع والمرافق والمعدات والتقنيات والجودة والسلامة والبيئة والتشغيل. تعرض القائمة التالية المراجع والروابط التي تم استخدامها أو الرجوع إليها ضمن التحليل الفني والتشغيلي."
          : "This study draws on project data, technical and operational inputs, and reliable technical and regulatory sources used to verify requirements related to site, utilities, equipment, technology, quality, safety, environment, and operations. The following references include the sources and links used or consulted in the technical and operational analysis."
        : lang === "ar"
          ? "استندت هذه الدراسة إلى بيانات المشروع والافتراضات المقدمة، وإلى مصادر عامة موثوقة عند تناول مؤشرات السوق والاقتصاد والقطاع. تعرض القائمة التالية مراجع وروابط يمكن الرجوع إليها للتحقق من البيانات العامة ذات الصلة."
          : "This study draws on project data, stated assumptions, and reliable public sources where market, economic, and sector indicators are discussed. The following references include direct links for verification.",
      lang,
    ),
  );

  nodes.push(emptyLine());

  refs.forEach((ref, i) => nodes.push(referenceParagraph(ref, i, lang)));

  return nodes;
};

const buildAppendixPage = (lang) => [
  sectionHeading(t("appendix", lang), 0, lang),
  bodyParagraph(
    lang === "ar"
      ? "يشمل هذا الملحق أي بيانات أو جداول داعمة إضافية ذات صلة بالدراسة، بما في ذلك تفاصيل الافتراضات الموسّعة والمخرجات التفصيلية لكل وحدة من وحدات الدراسة."
      : "This appendix includes any additional supporting data or tables relevant to the study, including expanded assumption detail and the full module-level outputs referenced throughout the report.",
    lang,
  ),
];

// =========================================================
// HEADER / FOOTER / STYLES
// =========================================================

const buildHeader = (study, lang, watermarkBytes) => {
  console.log("🟡 BUILD HEADER WATERMARK BYTES:", watermarkBytes?.length);
  const isTechnicalOperational = isTechnicalOperationalStudy(study);
  return new Header({
    children: [
      new Paragraph({
        children: [
          new ImageRun({
            type: "png",
            data: watermarkBytes,

            transformation: {
              width: 430,
              height: 590,
            },

            floating: {
              behindDocument: true,
              allowOverlap: true,

              horizontalPosition: {
                relative: HorizontalPositionRelativeFrom.PAGE,
                align: HorizontalPositionAlign.CENTER,
              },

              verticalPosition: {
                relative: VerticalPositionRelativeFrom.PAGE,
                align: VerticalPositionAlign.CENTER,
              },

              wrap: {
                type: TextWrappingType.NONE,
              },
            },
          }),
        ],
      }),

      new Paragraph({
        alignment: AlignmentType.RIGHT,
        bidirectional: lang === "ar",
        border: {
          bottom: {
            style: BorderStyle.SINGLE,
            size: 4,
            color: THEME.accent,
            space: 4,
          },
        },
        children: [
          professionalTextRun(
            {
              text: isTechnicalOperational
                ? lang === "ar"
                  ? "تقرير الدراسة الفنية والتشغيلية"
                  : "Technical & Operational Study Report"
                : t("headerTitle", lang),
              bold: true,
              size: 18,
              color: THEME.primary,
            },
            lang,
          ),
          professionalTextRun(
            {
              text: "   ·   ",
              size: 18,
              color: THEME.accent,
            },
            lang,
          ),
          professionalTextRun(
            {
              text: t("headerConfidential", lang),
              size: 18,
              color: THEME.gold,
              italics: true,
            },
            lang,
          ),
        ],
      }),
    ],
  });
};
const buildFooter = (lang) =>
  new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 40 },
        border: {
          top: {
            style: BorderStyle.SINGLE,
            size: 4,
            color: THEME.accent,
            space: 4,
          },
        },
        children: [
          professionalTextRun(
            {
              text: `${t("page", lang)} `,
              size: 18,
              color: THEME.accent,
            },
            lang,
          ),
          professionalTextRun(
            {
              children: [PageNumber.CURRENT],
              size: 18,
              color: THEME.accent,
            },
            lang,
          ),
          professionalTextRun(
            {
              text: ` ${t("of", lang)} `,
              size: 18,
              color: THEME.accent,
            },
            lang,
          ),
          professionalTextRun(
            {
              children: [PageNumber.TOTAL_PAGES],
              size: 18,
              color: THEME.accent,
            },
            lang,
          ),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        bidirectional: lang === "ar",
        children: [
          professionalTextRun(
            {
              text: t("generatedBy", lang),
              size: 15,
              color: THEME.accent,
            },
            lang,
          ),
        ],
      }),
    ],
  });

const docStyles = (lang = "en") => ({
  default: {
    document: {
      run: {
        font: lang === "ar" ? THEME.fontArabic : THEME.fontBody,
        size: 22,
      },
      paragraph: {
        bidirectional: lang === "ar",
        alignment: align(lang),
      },
    },
  },

  heading1: {
    run: {
      font: lang === "ar" ? THEME.fontArabic : THEME.fontHeading,
      size: 34,
      bold: true,
      color: THEME.primary,
    },
    paragraph: {
      bidirectional: lang === "ar",
      alignment: align(lang),
      spacing: { before: 320, after: 160 },
    },
  },

  heading2: {
    run: {
      font: lang === "ar" ? THEME.fontArabic : THEME.fontHeading,
      size: 27,
      bold: true,
      color: THEME.secondary,
    },
    paragraph: {
      bidirectional: lang === "ar",
      alignment: align(lang),
      spacing: { before: 220, after: 120 },
    },
  },

  heading3: {
    run: {
      font: lang === "ar" ? THEME.fontArabic : THEME.fontHeading,
      size: 23,
      bold: true,
      color: THEME.tertiary,
    },
    paragraph: {
      bidirectional: lang === "ar",
      alignment: align(lang),
      spacing: { before: 160, after: 100 },
    },
  },

  heading4: {
    run: {
      font: lang === "ar" ? THEME.fontArabic : THEME.fontHeading,
      size: 21,
      bold: true,
      color: THEME.accent,
    },
    paragraph: {
      bidirectional: lang === "ar",
      alignment: align(lang),
      spacing: { before: 140, after: 90 },
    },
  },
});

const docNumbering = (lang = "en") => ({
  config: [
    {
      reference: "premium-bullets",
      levels: [
        {
          level: 0,
          format: LevelFormat.BULLET,
          text: "•",
          alignment: lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,
          style: {
            paragraph: {
              indent:
                lang === "ar"
                  ? { right: 420, hanging: 260 }
                  : { left: 420, hanging: 260 },
            },
          },
        },
        {
          level: 1,
          format: LevelFormat.BULLET,
          text: "◦",
          alignment: lang === "ar" ? AlignmentType.RIGHT : AlignmentType.LEFT,
          style: {
            paragraph: {
              indent:
                lang === "ar"
                  ? { right: 760, hanging: 260 }
                  : { left: 760, hanging: 260 },
            },
          },
        },
      ],
    },
    {
      reference: "premium-numbers",
      levels: [
        {
          level: 0,
          format: LevelFormat.DECIMAL,
          text: "%1.",
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: 460, hanging: 320 },
            },
          },
        },
      ],
    },
  ],
});

const coverSection = (lang, children) => ({
  properties: {
    page: {
      size: { width: PAGE_WIDTH_DXA, height: 16838 },
      margin: {
        top: convertInchesToTwip(1),
        bottom: convertInchesToTwip(1),
        left: convertInchesToTwip(1),
        right: convertInchesToTwip(1),
      },
    },
  },

  // intentionally no header and no footer on cover
  children,
});
const pageSection = (
  study,
  lang,
  children,
  watermarkBytes,
  restartPageNumbers = false,
) => ({
  properties: {
    page: {
      size: { width: PAGE_WIDTH_DXA, height: 16838 },
      ...(restartPageNumbers ? { pageNumbers: { start: 1 } } : {}),
      margin: {
        top: convertInchesToTwip(1),
        bottom: convertInchesToTwip(1),
        left: convertInchesToTwip(1),
        right: convertInchesToTwip(1),
      },
    },
  },
  headers: {
    default: buildHeader(study, lang, watermarkBytes),
  },
  footers: { default: buildFooter(lang) },
  children,
});
const landscapePageSection = (study, lang, children, watermarkBytes) => ({
  properties: {
    page: {
      size: {
        width: 16838,
        height: PAGE_WIDTH_DXA,
      },
      margin: {
        top: convertInchesToTwip(0.7),
        bottom: convertInchesToTwip(0.7),
        left: convertInchesToTwip(0.6),
        right: convertInchesToTwip(0.6),
      },
    },
  },
  headers: {
    default: buildHeader(study, lang, watermarkBytes),
  },
  footers: {
    default: buildFooter(lang),
  },
  children,
});
const extractMeta = (study) => {
  const meta = study?.meta || study?.metadata || {};

  return {
    clientName:
      meta.client_name ||
      meta.clientName ||
      meta.recipient_name ||
      meta.recipientName ||
      study?.recipient_name ||
      study?.recipientName ||
      study?.client_name ||
      study?.clientName ||
      study?.customer_name ||
      "",

    projectName:
      meta.project_name ||
      meta.projectName ||
      study?.project_name ||
      study?.projectName ||
      study?.title ||
      "",

    studyDate:
      meta.study_date ||
      meta.studyDate ||
      study?.study_date ||
      new Date().toLocaleDateString(),
  };
};

// Ordered walk of every stage/module in the master JSON, skipping
// nothing and summarizing nothing — used by the full report.
const forEachModule = (study, callback) => {
  const stageKeys = Object.keys(study || {})
    .filter((k) => /^stage_?\d+(?:_output)?$/i.test(k))
    .sort(
      (a, b) =>
        parseInt(a.replace(/\D/g, ""), 10) - parseInt(b.replace(/\D/g, ""), 10),
    );

  stageKeys.forEach((stageKey, idx) => {
    const rawStage = study[stageKey];
    const stage =
      rawStage && typeof rawStage === "object" && rawStage.section_contents
        ? rawStage.section_contents
        : rawStage;
    const stageNumber = stageKey.replace(/\D/g, "") || String(idx + 1);
    callback(stageKey, stageNumber, stage);
  });

  if (
    stageKeys.length === 0 &&
    study?.sections &&
    typeof study.sections === "object"
  ) {
    callback("sections", "1", study.sections);
    return 1;
  }

  return stageKeys.length;
};

const downloadDoc = async (doc, filenamePrefix, study = null) => {
  const isTechnicalOperational =
    study === null ? false : isTechnicalOperationalStudy(study);
  const effectiveFilenamePrefix =
    isTechnicalOperational && filenamePrefix.startsWith("feasibility_study")
      ? filenamePrefix.replace(
          "feasibility_study",
          "technical_operational_study",
        )
      : filenamePrefix;

  console.log("⬇️ WORD DOWNLOAD PREFIX:", {
    requested: filenamePrefix,
    effective: effectiveFilenamePrefix,
    isTechnicalOperational,
    studyType: study?.study_type,
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${effectiveFilenamePrefix}_${Date.now()}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return blob;
};

// =========================================================
// FULL REPORT (internal consulting document — every section,
// every paragraph from the Master JSON, no summarization)
// =========================================================

export const generateFullReport = async (study) => {
  const isTechnicalOperational = isTechnicalOperationalStudy(study);
  console.log("🔴 IS TECHNICAL RESULT:", isTechnicalOperational);

  console.log("🔴 TECHNICAL TABLE MODE:", WORD_RENDER_IS_TECHNICAL);
  console.log("========================================");
  console.log("CHART DATA CHECK");
  console.log({
    market_growth_trend: study?.stage1?.market_analysis?.market_growth_trend,

    market_supply_comparison:
      study?.stage1?.market_analysis?.market_supply_comparison,

    market_sizing_chart: study?.stage1?.market_analysis?.market_sizing_chart,

    porters_five_forces_chart:
      study?.stage2?.porters_five_forces?.porters_five_forces_chart,

    risk_level_distribution_chart:
      study?.stage5?.risk_analysis?.risk_level_distribution_chart,
  });
  console.log("========================================");
  console.log("🔴 STUDY RECEIVED BY WORD:", study);
  console.log("🔴 STUDY TOP LEVEL KEYS:", Object.keys(study || {}));
  console.log("🚨 STAGE 4 KEYS:", Object.keys(study?.stage4 || {}));
  console.log("🚨 EXACT STUDY TYPE:", JSON.stringify(study?.study_type));
  console.log("🟠 STAGE 2 KEYS:", Object.keys(study?.stage2 || {}));

  console.log("🟠 STAGE 2 FULL:", study?.stage2);
  const lang = detectReportLanguage(study);
  const meta = extractMeta(study);
  console.log("🚨 STUDY TYPE VALUE:", study?.study_type);
  console.log("🚨 TECH DETECTOR:", isTechnicalOperationalStudy(study));
  console.log("🚨 STAGE 4 KEYS:", Object.keys(study?.stage4 || {}));
  WORD_RENDER_IS_TECHNICAL = isTechnicalOperational;

  WORD_RENDER_TABLE_COUNT = 0;
  console.log("🔴 EXTRACTED META:", meta);
  const watermarkBytes = await loadWatermarkBytes();
  console.log("🟢 WATERMARK LOADED SUCCESSFULLY");
  const logoBytes = await loadLogoBytes();

  const coverChildren = buildCoverPage(
    study,
    lang,
    meta,

    logoBytes,
  );
  const children = [];

  children.push(...buildCopyrightPage(lang));
  children.push(pageBreak());

  children.push(...buildDisclaimerPage(lang));
  children.push(pageBreak());
  children.push(...buildTocPage(lang));
  children.push(pageBreak());

  children.push(...buildIntroductionPage(study, lang));
  children.push(pageBreak());
  // ---------- Every stage, every module, every field ----------
  const stageCount = forEachModule(study, (stageKey, stageNumber, stage) => {
    const stageNodes = [];
    const technicalStageTitles = {
      1: {
        ar: "الجزء الأول: الأساس الفني والطاقة والموقع",
        en: "Part One: Technical Foundation, Capacity & Site",
      },
      2: {
        ar: "الجزء الثاني: المعدات والعمليات والجودة",
        en: "Part Two: Equipment, Operations & Quality",
      },
      3: {
        ar: "الجزء الثالث: القوى العاملة والسلامة والأتمتة",
        en: "Part Three: Workforce, Safety & Automation",
      },
      4: {
        ar: "الجزء الرابع: المخاطر الفنية ومتطلبات التنفيذ",
        en: "Part Four: Technical Risks & Implementation Requirements",
      },
      5: {
        ar: "الجزء الخامس: الحكم الفني والتشغيلي النهائي",
        en: "Part Five: Final Technical & Operational Assessment",
      },
    };

    const stageTitle =
      isTechnicalOperational && technicalStageTitles[Number(stageNumber)]
        ? technicalStageTitles[Number(stageNumber)][lang]
        : translateKey(`stage${stageNumber}`, lang);
    stageNodes.push(majorSectionDivider(stageTitle, lang));

    stageNodes.push(emptyLine(140));

    if (!stage || typeof stage !== "object") {
      if (stage) stageNodes.push(bodyParagraph(String(stage), lang));

      if (stageNodes.length > 2) {
        appendNodesWithBreak(children, stageNodes);
      }

      return;
    }

    Object.entries(stage).forEach(([moduleKey, moduleValue]) => {
      const moduleKeyNorm = moduleKey.toLowerCase();
      if (
        META_KEYS.has(moduleKeyNorm) ||
        (!isTechnicalOperational &&
          FULL_REPORT_SPECIAL_MODULES.has(moduleKeyNorm))
      ) {
        return;
      }

      // unwrap modules that double-wrap themselves: { roi_analysis: { roi_analysis: {...} } }
      let effectiveValue = moduleValue;
      if (
        effectiveValue &&
        typeof effectiveValue === "object" &&
        !Array.isArray(effectiveValue) &&
        effectiveValue[moduleKey] !== undefined &&
        Object.keys(effectiveValue).length === 1
      ) {
        effectiveValue = effectiveValue[moduleKey];
      }

      const moduleTitle = forceArabicTitle(
        (effectiveValue &&
          typeof effectiveValue === "object" &&
          (effectiveValue.title || effectiveValue.section_title)) ||
          "",
        moduleKey,
        lang,
      );

      const moduleNodes = [sectionHeading(moduleTitle, 1, lang)];

      // =========================================================
      // TECHNICAL FACILITY LAYOUT — MODULE LEVEL RENDERER
      // =========================================================
      if (
        isTechnicalOperational &&
        moduleKeyNorm === "facility_layout_plan" &&
        effectiveValue &&
        typeof effectiveValue === "object" &&
        !Array.isArray(effectiveValue)
      ) {
        console.log("🟦 FACILITY LAYOUT MODULE ROUTER HIT:", effectiveValue);

        renderModuleField(
          moduleKey,
          moduleTitle,
          effectiveValue,
          2,
          lang,
          moduleNodes,
        );

        if (moduleNodes.length > 1) {
          stageNodes.push(...moduleNodes);
        }

        return;
      }

      if (
        effectiveValue &&
        typeof effectiveValue === "object" &&
        !Array.isArray(effectiveValue)
      ) {
        if (moduleKey === "competitor_analysis") {
          console.log(
            "🟠 COMPETITOR ANALYSIS KEYS:",
            Object.keys(effectiveValue),
          );

          console.log("🟠 COMPETITOR ANALYSIS FULL VALUE:", effectiveValue);
        }
        // ---------- Market Sizing: TAM / SAM / SOM ----------
        if (moduleKey === "market_analysis") {
          const sizingTable = marketSizingTable(effectiveValue, lang);

          if (sizingTable) {
            moduleNodes.push(
              sectionHeading(
                lang === "ar" ? "حجم السوق المستهدف" : "Market Sizing",
                2,
                lang,
              ),
            );

            moduleNodes.push(sizingTable);
            moduleNodes.push(emptyLine());
          }
        }
        // ---------- Risk Matrix ----------
        const kpiSnapshot = genericKpiSnapshot(effectiveValue, lang);

        if (kpiSnapshot) {
          moduleNodes.push(
            sectionHeading(
              lang === "ar" ? "مؤشرات رئيسية" : "Key Indicators",
              2,
              lang,
            ),
          );

          moduleNodes.push(kpiSnapshot);
          moduleNodes.push(emptyLine());
        }
        if (moduleKey === "risk_analysis") {
          const riskMatrix = qualitativeRiskMatrix(effectiveValue, lang);

          if (riskMatrix) {
            moduleNodes.push(
              sectionHeading(
                lang === "ar" ? "مصفوفة المخاطر" : "Risk Matrix",
                2,
                lang,
              ),
            );

            moduleNodes.push(riskMatrix);
            moduleNodes.push(emptyLine());
          }
        }
        // ---------- Risk Distribution Donut ----------

        // ---------- Customer Journey ----------
        if (moduleKey === "customer_analysis") {
          const journeyChart = customerJourneyFlowChart(effectiveValue, lang);

          if (journeyChart) {
            moduleNodes.push(
              sectionHeading(
                lang === "ar" ? "رحلة العميل" : "Customer Journey",
                2,
                lang,
              ),
            );

            moduleNodes.push(journeyChart);
            moduleNodes.push(emptyLine());
          }
        }
        // ---------- Customer Personas ----------
        if (moduleKey === "customer_analysis") {
          const personasTable = customerPersonasTable(effectiveValue, lang);

          if (personasTable) {
            moduleNodes.push(
              sectionHeading(
                lang === "ar"
                  ? "شخصيات العملاء الرئيسية"
                  : "Key Customer Personas",
                2,
                lang,
              ),
            );

            moduleNodes.push(personasTable);
            moduleNodes.push(emptyLine());
          }
        }
        // ---------- Buying Process ----------
        let buyingProcessRendered = false;

        if (moduleKey === "customer_analysis") {
          const buyingProcess =
            effectiveValue?.buying_process_and_decision_criteria;

          const buyingFlow = genericProcessFlowChart(buyingProcess, lang);

          if (buyingFlow) {
            moduleNodes.push(
              sectionHeading(
                lang === "ar"
                  ? "عملية الشراء ومعايير القرار"
                  : "Buying Process & Decision Criteria",
                2,
                lang,
              ),
            );

            moduleNodes.push(buyingFlow);
            moduleNodes.push(emptyLine());

            buyingProcessRendered = true;
          }
        }
        Object.entries(effectiveValue).forEach(([fieldKey, fieldValue]) => {
          // TAM / SAM / SOM are already rendered in the market sizing table
          if (
            moduleKey === "market_analysis" &&
            [
              "total_addressable_market_tam",
              "serviceable_addressable_market_sam",
              "serviceable_obtainable_market_som",
            ].includes(fieldKey)
          ) {
            return;
          }
          if (
            moduleKey === "risk_analysis" &&
            fieldKey === "overall_risk_matrix"
          ) {
            return;
          }
          if (
            moduleKey === "customer_analysis" &&
            (fieldKey === "customer_journey" ||
              fieldKey === "customer_personas")
          ) {
            return;
          }
          if (
            moduleKey === "customer_analysis" &&
            fieldKey === "buying_process_and_decision_criteria" &&
            buyingProcessRendered
          ) {
            return;
          }
          let fieldLabel = translateKey(fieldKey, lang);

          if (
            lang === "ar" &&
            /strategic|affecting|business.model/i.test(String(fieldKey))
          ) {
            console.log("🔴 HEADING DEBUG:", {
              fieldKey,
              fieldLabel,
            });
          }
          if (
            moduleKey === "business_overview" &&
            fieldKey === "executive_summary"
          ) {
            fieldLabel = isTechnicalOperational
              ? lang === "ar"
                ? "الخلاصة الفنية والتشغيلية"
                : "Technical & Operational Summary"
              : lang === "ar"
                ? "الخلاصة السوقية الأولية"
                : "Initial Market Overview";
          }
          renderModuleField(
            fieldKey,
            fieldLabel,
            fieldValue,
            2,
            lang,
            moduleNodes,
          );
        });
      } else {
        renderModuleField(
          moduleKey,
          moduleTitle,
          effectiveValue,
          2,
          lang,
          moduleNodes,
        );
      }
      if (moduleNodes.length > 1) stageNodes.push(...moduleNodes);
    });

    if (stageNodes.length > 2) {
      appendNodesWithBreak(children, stageNodes);
    }
  });

  // Fallback: no stageN keys found — still render generically so the
  // document is never just cover/copyright/disclaimer.
  if (stageCount === 0) {
    Object.entries(study || {}).forEach(([key, value]) => {
      if (["meta", "metadata"].includes(key.toLowerCase())) return;
      const sectionNodes = [sectionHeading(translateKey(key, lang), 0, lang)];
      renderModuleField(
        key,
        translateKey(key, lang),
        value,
        1,
        lang,
        sectionNodes,
      );
      if (sectionNodes.length > 1) appendNodesWithBreak(children, sectionNodes);
    });
  }

  // ---------- Recommendations ----------

  const recNodes = buildRecommendationsPage(study, lang);
  children.push(...recNodes);

  children.push(pageBreak());

  children.push(...buildReferencesPage(study, lang));
  // =========================================================
  // SPLIT REPORT AROUND LANDSCAPE RISK REGISTER
  // =========================================================
  const beforeRiskLandscape = [];
  const riskLandscapeChildren = [];
  const afterRiskLandscape = [];

  let riskLandscapeMode = false;
  let riskLandscapeFinished = false;

  children.forEach((node) => {
    if (node === RISK_LANDSCAPE_START) {
      riskLandscapeMode = true;
      return;
    }

    if (node === RISK_LANDSCAPE_END) {
      riskLandscapeMode = false;
      riskLandscapeFinished = true;
      return;
    }

    if (riskLandscapeMode) {
      riskLandscapeChildren.push(node);
    } else if (!riskLandscapeFinished) {
      beforeRiskLandscape.push(node);
    } else {
      afterRiskLandscape.push(node);
    }
  });
  // ---------- Appendix ----------
  const doc = new Document({
    features: { updateFields: true },
    styles: docStyles(lang),
    numbering: docNumbering(lang),
    sections: [
      coverSection(lang, coverChildren),

      ...(riskLandscapeChildren.length
        ? [
            pageSection(study, lang, beforeRiskLandscape, watermarkBytes, true),

            landscapePageSection(
              study,
              lang,
              riskLandscapeChildren,
              watermarkBytes,
            ),

            pageSection(study, lang, afterRiskLandscape, watermarkBytes),
          ]
        : [pageSection(study, lang, children, watermarkBytes, true)]),
    ],
  });

  return downloadDoc(
    doc,
    isTechnicalOperational
      ? "technical_operational_study_full_report"
      : "feasibility_study_full_report",
    study,
  );
};

// =========================================================
// EXECUTIVE REPORT (client-facing, ~5-10 pages)
// =========================================================

export const generateExecutiveReport = async (study) => {
  console.log("🟢 EXECUTIVE REPORT NEW LOGO/WATERMARK VERSION");
  const lang = detectReportLanguage(study);
  const meta = extractMeta(study);
  const isTechnicalOperational = isTechnicalOperationalStudy(study);
  WORD_RENDER_IS_TECHNICAL = isTechnicalOperational;

  WORD_RENDER_TABLE_COUNT = 0;
  const watermarkBytes = await loadWatermarkBytes();
  console.log("🟢 WATERMARK LOADED SUCCESSFULLY");
  const logoBytes = await loadLogoBytes();
  console.log("EXEC LOGO BYTES:", logoBytes?.length);
  console.log("EXEC WATERMARK BYTES:", watermarkBytes?.length);
  const coverChildren = buildCoverPage(study, lang, meta, logoBytes);
  const children = [];

  // Executive Summary
  children.push(...buildExecutiveSummaryPage(study, lang));
  children.push(emptyLine());

  // Main Risks
  const riskModule = findModule(
    study,
    isTechnicalOperational ? "technical_risk_analysis" : "risk_analysis",
  );
  const executiveSummary = findModule(study, "executive_summary");
  console.log("🔴 EXECUTIVE RISK MODULE FOUND:", riskModule);

  if (riskModule && typeof riskModule === "object") {
    children.push(
      sectionHeading(
        isTechnicalOperational
          ? lang === "ar"
            ? "المخاطر الفنية والتشغيلية الرئيسية"
            : "Key Technical & Operational Risks"
          : translateKey("risk_analysis", lang),
        1,
        lang,
      ),
    );
    if (isTechnicalOperational) {
      const criticalTechnicalRisks = executiveSummary?.critical_technical_risks;

      if (criticalTechnicalRisks) {
        children.push(
          new Paragraph({
            bidirectional: lang === "ar",
            alignment: align(lang),
            spacing: { after: 60 },
            children: [
              professionalTextRun(
                {
                  text:
                    lang === "ar"
                      ? "المخاطر الفنية والتشغيلية الحرجة"
                      : "Critical Technical & Operational Risks",
                  bold: true,
                  color: THEME.secondary,
                  size: 22,
                },
                lang,
              ),
            ],
          }),
        );

        recommendationListToText(
          Array.isArray(criticalTechnicalRisks)
            ? criticalTechnicalRisks
            : [criticalTechnicalRisks],
          lang,
        )
          .slice(0, 5)
          .forEach((p) => children.push(bulletParagraph(p, lang)));
      }
    } else {
      const riskFields = [
        "strategic_risks",
        "market_risks",
        "competitive_risks",
        "financial_risks",
        "operational_risks",
      ];

      riskFields.forEach((rf) => {
        if (riskModule[rf]) {
          children.push(
            new Paragraph({
              bidirectional: lang === "ar",
              alignment: align(lang),
              spacing: { after: 60 },
              children: [
                professionalTextRun(
                  {
                    text: translateKey(rf, lang),
                    bold: true,
                    color: THEME.secondary,
                    size: 22,
                  },
                  lang,
                ),
              ],
            }),
          );

          toPointList(riskModule[rf])
            .slice(0, 3)
            .forEach((p) => children.push(bulletParagraph(p, lang)));
        }
      });
    }
    if (Array.isArray(riskModule.key_takeaways)) {
      children.push(emptyLine());
      children.push(
        highlightBox(
          t("keyTakeaways", lang),
          riskModule.key_takeaways.map(String),
          lang,
          "FBEAEA",
          "C0504D",
        ),
      );
    }
    children.push(emptyLine());
  }

  // Investment Highlights
  // Key Highlights
  if (isTechnicalOperational) {
    const technicalHighlights =
      executiveSummary?.critical_pre_operation_requirements;

    if (technicalHighlights) {
      children.push(
        sectionHeading(
          lang === "ar"
            ? "أبرز المتطلبات قبل التشغيل"
            : "Key Pre-Operation Requirements",
          1,
          lang,
        ),
      );

      recommendationListToText(
        Array.isArray(technicalHighlights)
          ? technicalHighlights
          : [technicalHighlights],
        lang,
      )
        .slice(0, 6)
        .forEach((p) => children.push(bulletParagraph(p, lang)));

      children.push(emptyLine());
    }
  } else {
    const investment = findModule(study, "investment_recommendation");

    if (investment && typeof investment === "object") {
      children.push(
        sectionHeading(translateKey("investment_highlights", lang), 1, lang),
      );

      if (investment.investment_strengths) {
        toPointList(investment.investment_strengths).forEach((p) =>
          children.push(bulletParagraph(p, lang)),
        );
      }

      children.push(emptyLine());
    }
  }

  // Feasibility Score
  // Feasibility / Readiness Assessment
  if (isTechnicalOperational) {
    const technicalFeasibility =
      executiveSummary?.overall_technical_feasibility;

    const operationalReadiness =
      executiveSummary?.overall_operational_readiness;

    if (technicalFeasibility || operationalReadiness) {
      children.push(
        sectionHeading(
          lang === "ar"
            ? "التقييم الفني والجاهزية التشغيلية"
            : "Technical Feasibility & Operational Readiness",
          1,
          lang,
        ),
      );

      if (technicalFeasibility) {
        children.push(
          new Paragraph({
            bidirectional: lang === "ar",
            alignment: align(lang),
            spacing: { after: 60 },
            children: [
              professionalTextRun(
                {
                  text:
                    lang === "ar" ? "الجدوى الفنية" : "Technical Feasibility",
                  bold: true,
                  color: THEME.secondary,
                  size: 22,
                },
                lang,
              ),
            ],
          }),
        );

        renderNarrative(
          recommendationValueToText(technicalFeasibility, lang).slice(0, 900),
          lang,
          children,
        );
      }

      if (operationalReadiness) {
        children.push(
          new Paragraph({
            bidirectional: lang === "ar",
            alignment: align(lang),
            spacing: { after: 60 },
            children: [
              professionalTextRun(
                {
                  text:
                    lang === "ar"
                      ? "الجاهزية التشغيلية"
                      : "Operational Readiness",
                  bold: true,
                  color: THEME.secondary,
                  size: 22,
                },
                lang,
              ),
            ],
          }),
        );

        renderNarrative(
          recommendationValueToText(technicalFeasibility, lang).slice(0, 900),
          lang,
          children,
        );
      }

      children.push(emptyLine());
    }
  } else {
    const swot = findModule(study, "swot_analysis");
    const feasText = swot && swot.feasibility_assessment;

    if (feasText) {
      children.push(
        sectionHeading(translateKey("feasibility_score", lang), 1, lang),
      );

      renderNarrative(String(feasText).slice(0, 900), lang, children);

      children.push(emptyLine());
    }
  }

  // Strategic Recommendation / Final Decision
  const recNodes = buildRecommendationsPage(study, lang);
  appendNodesWithBreak(children, recNodes);

  // ---------- References ----------
  appendNodesWithBreak(children, buildReferencesPage(study, lang));

  // ---------- Appendix ----------

  const doc = new Document({
    features: { updateFields: true },
    styles: docStyles(lang),
    numbering: docNumbering(lang),
    sections: [
      coverSection(lang, coverChildren),
      pageSection(study, lang, children, watermarkBytes),
    ],
  });

  return downloadDoc(
    doc,
    isTechnicalOperational
      ? "technical_operational_study_executive_summary"
      : "feasibility_study_executive_summary",
    study,
  );
};
// =========================================================
// PUBLIC ENTRY POINT — generates BOTH documents, preserving
// the original single-call usage pattern.
// =========================================================

export const generateWord = async (study) => {
  console.log("🔥🔥🔥 WORD FILE TEST 999 🔥🔥🔥");
  console.log("🔴 STUDY TYPE:", study?.study_type);
  console.log("🔴 HAS STAGE1:", !!study?.stage1);
  console.log("🔴 STAGE1 KEYS:", Object.keys(study?.stage1 || {}));
  console.log("🔴 IS TECHNICAL RESULT:", isTechnicalOperationalStudy(study));
  try {
    await generateFullReport(study);
    await generateExecutiveReport(study);
    console.log("✅ Both documents generated and downloaded successfully!");
    return true;
  } catch (error) {
    console.error("❌ Error generating document:", error);
    throw error;
  }
};
